import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import { setAiContext, clearAiContext } from '../utils/aiContext';
import openPdfInPrintDialog from '../utils/openPdfInPrintDialog';
import { apiErrorMessage, parseApiError, detailToString } from '../utils/apiError';
import { emailSenderHeaders } from '../utils/emailSender';
import MtnDetailModal from '../components/MtnDetailModal';
import RemnantLineageModal from '../components/RemnantLineageModal';
import EarmarkChip from '../components/EarmarkChip';
import EarmarkSheetsModal from '../components/EarmarkSheetsModal';
import MtnUnitCostCell from '../components/MtnUnitCost';
import MaterialCustodyCard from '../components/MaterialCustodyCard';
import LotStockBadge from '../components/LotStockBadge';
import AllVendorCustodyCard from '../components/AllVendorCustodyCard';
import { mtnRemaining, mtnLotBadge, isTransferableMtn } from '../utils/mtnLot';
import { desktopUi } from '../features/desktopUi';
import { addMaterialTransaction, recordMaterialDefect, transferStock, TRANSACTION_REASONS } from '../features/materials/materialActions';
import {
  loadMaterialList, loadMaterial, loadInboundOrders, loadMaterialTransactions as fetchMaterialTransactions,
  loadMaterialLots, loadTrackingLots, materialListCandidates, isInStock, materialFigures, TRACKING_STATUS_OPTIONS, filterLots,
  transferFilterDefaults, transferDestinations, onlyOpenLotId, loadOwnerCopy,
} from '../features/materials/materialData';
import { materialsEntryDeepLink } from '../utils/scanCodes';
import { amadaSizeText, grainCheck, LENGTH_LABEL, WIDTH_LABEL } from '../utils/sheetGrain';
import { shopToday } from '../utils/shopDate';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ReferenceLine, ReferenceDot
} from 'recharts';


const PROFILE_SHAPES = [
    "Flat Bar", "Round Bar", "Square Bar", "Hex Bar",
    "Round Tube", "Square Tube", "Rectangular Tube",
    "Pipe", "Angle", "Channel", "I-Beam", "Tee"
];

// Material type abbreviations for auto-generated material codes
const MATERIAL_TYPE_ABBREVIATIONS = {
    "Abrasion Resistant AR400": "AR400",
    "Abrasion Resistant AR500": "AR500",
    "Abrasion Resistant AR600": "AR600",
    "Aluminum 3003": "AL3003",
    "Aluminum 3003 H14": "AL3003H14",
    "Aluminum 5052 H32": "AL5052H32",
    "Aluminum 6061": "AL6061",
    "Brass 260": "BR260",
    "Carbon Steel A500 Gr B": "CSA500B",
    "Carbon Steel A500 Gr C": "CSA500C",
    "Carbon Steel A513": "CSA513",
    "Carbon Steel A514": "CSA514",
    "Carbon Steel A516-70": "CSA516-70",
    "Carbon Steel A529": "CSA529",
    "Carbon Steel A53": "CSA53",
    "Carbon Steel A572 Gr 50": "CSGR50",
    "Carbon Steel A992": "CSA992",
    "Copper C110": "CUC110",
    "Mild Steel 1018 CD": "CS1018CD",
    "Mild Steel A1011 CS": "CSA1011CS",
    "Mild Steel A36": "CSA36",
    "Mild Steel A36 SSAB": "CSA36-SSAB",
    "Mild Steel CS-B": "CSB",
    "Mild Steel CS-B/1008": "CSB-1008",
    "Stainless 304": "SS304",
    "Stainless 304 Perforated": "SS304PERF",
    "Stainless 316": "SS316",
    "Stainless 430": "SS430",
    "Titanium Grade 2": "TIGR2",
    "Titanium Grade 5": "TIGR5",
    "Tool Steel A2": "TSA2",
    "Tool Steel D2": "TSD2",
    "Weathering Steel A588": "CORTEN",
};

// Surface finish abbreviations for auto-generated material codes
const SURFACE_FINISH_ABBREVIATIONS = {
    "2B": "2B",
    "#3": "NO3",
    "#4": "NO4",
    "#8 Mirror": "NO8",
    "Anodized": "ANO",
    "BA (Bright Annealed)": "BA",
    "Brushed": "BRSH",
    "Coated": "COAT",
    "Cold Rolled": "CR",
    "Galvanized": "GALV",
    "Galvannealed": "GAVL",
    "Hot Rolled": "HR",
    "HRPO": "HRPO",
    "Mill Finish": "MILL",
    "Polished": "POL",
};

// Profile shape abbreviations for auto-generated material codes
const PROFILE_SHAPE_ABBREVIATIONS = {
    "Flat Bar": "FB",
    "Round Bar": "RB",
    "Square Bar": "SB",
    "Hex Bar": "HB",
    "Round Tube": "HSS",
    "Square Tube": "HSS",
    "Rectangular Tube": "HSS",
    "Pipe": "P",
    "Angle": "L",
    "Channel": "C",
    "I-Beam": "W",
    "Tee": "WT",
};

// Profile shapes grouped by their code generation pattern
const BAR_SHAPES = new Set(["Flat Bar", "Square Bar"]);
const SINGLE_DIM_BAR_SHAPES = new Set(["Round Bar", "Hex Bar"]);
const TUBE_SHAPES = new Set(["Round Tube", "Square Tube", "Rectangular Tube"]);
const ANGLE_SHAPES = new Set(["Angle"]);
const PIPE_SHAPES = new Set(["Pipe"]);
const STRUCTURAL_SHAPES = new Set(["Channel", "I-Beam", "Tee"]);

// Style for required fields that are empty (light pink background)
const REQUIRED_FIELD_EMPTY_STYLE = { backgroundColor: 'var(--jq-required-empty-bg, #fff0f0)' };

// Helper: format updated_at as "Mar 25, 2026" and determine staleness for price dates
function formatPriceDate(isoString) {
  if (!isoString) return { text: '\u2014', isStale: true, isVeryStale: true, daysSince: null };
  const date = new Date(isoString);
  const now = new Date();
  const daysSince = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  const text = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return { text, isStale: daysSince > 30, isVeryStale: daysSince > 60, daysSince };
}

// Render M/D for chart axis. Today's row + first-of-month rows get extra emphasis upstream.
function formatChartDate(iso) {
  if (!iso) return '';
  const parts = iso.split('-');
  return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
}

function RunoutForecastView({
  forecastData,
  forecastLoading,
  forecastError,
  forecastHorizon,
  setForecastHorizon,
  forecastUnsched,
  setForecastUnsched,
  forecastSelectedDay,
  setForecastSelectedDay,
  onRefresh,
}) {
  if (forecastLoading) {
    return <div className="text-center py-5"><div className="spinner-border" role="status" /></div>;
  }
  if (forecastError) {
    return (
      <div className="alert alert-danger">
        Failed to load forecast: {forecastError}
        <button className="btn btn-sm btn-outline-danger ms-2" onClick={onRefresh}>Retry</button>
      </div>
    );
  }
  if (!forecastData) {
    return <div className="text-muted py-3">No forecast data.</div>;
  }

  const {
    daily_projection = [],
    starting_balance = 0,
    minimum_available = 0,
    runout_date,
    below_min_date,
    unscheduled_demand = { total_sheets: 0, task_count: 0, tasks: [] },
    undated_inbound = { total_quantity: 0, po_count: 0, pos: [] },
    material_code,
    description,
    unit_of_measurement,
  } = forecastData;

  const unit = unit_of_measurement || 'sheets';
  const finalBalance = daily_projection.length
    ? daily_projection[daily_projection.length - 1].balance
    : starting_balance;
  const netDelta = finalBalance - starting_balance;

  const chartData = daily_projection.map(d => ({
    date: d.date,
    dateLabel: formatChartDate(d.date),
    balance: Number(d.balance.toFixed(2)),
    consumption: d.consumption,
    inbound: d.inbound,
  }));

  const inboundEvents = daily_projection.filter(d => d.inbound > 0);
  const demandRows = [];
  daily_projection.forEach(d => {
    d.demand_events.forEach(e => demandRows.push({ ...e, date: d.date }));
  });
  const inboundRows = [];
  daily_projection.forEach(d => {
    d.inbound_events.forEach(e => inboundRows.push({ ...e, date: d.date }));
  });

  const selectedDayData = forecastSelectedDay
    ? daily_projection.find(d => d.date === forecastSelectedDay)
    : null;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1">Runout Forecast — {material_code}</h5>
          <div className="text-muted small">{description}</div>
        </div>
        <div className="d-flex gap-3 align-items-center flex-wrap">
          <div className="d-flex gap-2 align-items-center">
            <span className="text-muted small">Horizon:</span>
            <div className="btn-group btn-group-sm" role="group">
              {[30, 60, 90, 180].map(h => (
                <button
                  key={h}
                  type="button"
                  className={`btn ${forecastHorizon === h ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setForecastHorizon(h)}
                >{h}d</button>
              ))}
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <span className="text-muted small">Unsched:</span>
            <div className="btn-group btn-group-sm" role="group" title="How to handle tasks with no scheduled_date">
              {[
                {v: 'exclude', label: 'Exclude', tip: 'Unscheduled tasks NOT in curve (default, realistic)'},
                {v: 'spread',  label: 'Spread',  tip: 'Distribute unscheduled across horizon evenly'},
                {v: 'today',   label: 'Today',   tip: 'Pile unscheduled on today (worst case)'},
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  title={opt.tip}
                  className={`btn ${forecastUnsched === opt.v ? 'btn-info text-white' : 'btn-outline-info'}`}
                  onClick={() => setForecastUnsched && setForecastUnsched(opt.v)}
                >{opt.label}</button>
              ))}
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRefresh}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body py-2 text-center">
              <div className="text-muted small">Current Stock</div>
              <div className="h4 mb-0">{starting_balance} <span className="small text-muted">{unit}</span></div>
              <div className="small text-muted">Min: {minimum_available}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card h-100 ${below_min_date ? 'border-warning' : 'border-success'}`}>
            <div className="card-body py-2 text-center">
              <div className="text-muted small">Dips Below Min</div>
              <div className={`h4 mb-0 ${below_min_date ? 'text-warning' : 'text-success'}`}>
                {below_min_date || 'Never'}
              </div>
              {!below_min_date && <div className="small text-muted">in {forecastHorizon}d</div>}
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card h-100 ${runout_date ? 'border-danger' : 'border-success'}`}>
            <div className="card-body py-2 text-center">
              <div className="text-muted small">Runs Out</div>
              <div className={`h4 mb-0 ${runout_date ? 'text-danger' : 'text-success'}`}>
                {runout_date || 'Not within horizon'}
              </div>
              {!runout_date && <div className="small text-muted">in {forecastHorizon}d</div>}
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body py-2 text-center">
              <div className="text-muted small">Net Δ ({forecastHorizon}d)</div>
              <div className={`h4 mb-0 ${netDelta < 0 ? 'text-danger' : 'text-success'}`}>
                {netDelta > 0 ? '+' : ''}{netDelta.toFixed(0)} <span className="small text-muted">{unit}</span>
              </div>
              <div className="small text-muted">final: {finalBalance.toFixed(0)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header py-2">
          Projected Balance over {forecastHorizon} days
          <span className="text-muted small ms-2">click any day for details</span>
        </div>
        <div className="card-body" style={{ height: 380 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 16, right: 50, left: 10, bottom: 8 }}
              onClick={(e) => {
                if (e && e.activeLabel) {
                  const row = chartData.find(c => c.dateLabel === e.activeLabel);
                  if (row) setForecastSelectedDay(row.date);
                }
              }}
            >
              <defs>
                <linearGradient id="balanceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d6efd" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#0d6efd" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={28}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#666' } }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const row = chartData.find(c => c.dateLabel === label);
                  if (!row) return null;
                  const dayDetail = daily_projection.find(d => d.date === row.date);
                  return (
                    <div style={{ background: '#fff', border: '1px solid #ccc', padding: 8, fontSize: 12 }}>
                      <div><b>{row.date}</b></div>
                      <div>Balance: <b>{row.balance}</b> {unit}</div>
                      {row.consumption > 0 && <div className="text-danger">\u2212 {row.consumption} consumed</div>}
                      {row.inbound > 0 && <div className="text-success">+ {row.inbound} received</div>}
                      {dayDetail?.negative && <div className="text-danger small mt-1">⚠ Out of stock</div>}
                      {dayDetail?.below_min && !dayDetail?.negative && <div className="text-warning small mt-1">⚠ Below min</div>}
                    </div>
                  );
                }}
              />
              <ReferenceLine
                y={minimum_available}
                stroke="#fd7e14"
                strokeDasharray="6 4"
                label={{ value: `Min ${minimum_available}`, position: 'right', fill: '#fd7e14', fontSize: 11 }}
              />
              <ReferenceLine y={0} stroke="#dc3545" strokeWidth={2} />
              {forecastSelectedDay && (() => {
                const row = chartData.find(c => c.date === forecastSelectedDay);
                return row
                  ? <ReferenceLine x={row.dateLabel} stroke="#6c757d" strokeDasharray="3 3" />
                  : null;
              })()}
              <Area
                type="stepAfter"
                dataKey="balance"
                stroke="#0d6efd"
                strokeWidth={2}
                fill="url(#balanceFill)"
                name="Projected balance"
                isAnimationActive={false}
              />
              {inboundEvents.map(d => {
                const row = chartData.find(c => c.date === d.date);
                if (!row) return null;
                return (
                  <ReferenceDot
                    key={d.date}
                    x={row.dateLabel}
                    y={row.balance}
                    r={6}
                    fill="#198754"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-footer py-2 small text-muted d-flex gap-3 flex-wrap">
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#0d6efd', verticalAlign: 'middle' }} /> Projected balance</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#198754', borderRadius: '50%', verticalAlign: 'middle' }} /> PO arrival</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#fd7e14', borderTop: '2px dashed #fd7e14', verticalAlign: 'middle' }} /> Minimum</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#dc3545', verticalAlign: 'middle' }} /> Zero</span>
        </div>
      </div>

      {selectedDayData && (
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center py-2">
            <span>Detail for <b>{selectedDayData.date}</b> — projected balance {selectedDayData.balance.toFixed(1)} {unit}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForecastSelectedDay(null)}>×</button>
          </div>
          <div className="card-body p-0">
            {selectedDayData.demand_events.length === 0 && selectedDayData.inbound_events.length === 0 ? (
              <div className="p-3 text-muted small">No events on this day. Balance carries from prior day.</div>
            ) : (
              <>
                {selectedDayData.demand_events.length > 0 && (
                  <table className="table table-sm mb-0">
                    <thead className="table-light"><tr>
                      <th colSpan={5}>Consumption — {selectedDayData.consumption} {unit}</th>
                    </tr><tr>
                      <th>Job</th><th>Task</th><th>Cut Sheet</th><th>Status</th><th className="text-end">Sheets</th>
                    </tr></thead>
                    <tbody>
                      {selectedDayData.demand_events.map(e => (
                        <tr key={`d-${e.task_id}`}>
                          <td>{e.master_job_num ? `#${e.master_job_num}` : `Job ${e.job_id}`}</td>
                          <td>{e.task_id}</td>
                          <td>{e.cut_sheet_name}</td>
                          <td><span className="badge bg-secondary">{e.status}</span></td>
                          <td className="text-end fw-bold">{e.sheets}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {selectedDayData.inbound_events.length > 0 && (
                  <table className="table table-sm mb-0">
                    <thead className="table-light"><tr>
                      <th colSpan={4}>Inbound — {selectedDayData.inbound} {unit}</th>
                    </tr><tr>
                      <th>PO</th><th>Supplier</th><th>ETA</th><th className="text-end">Qty Due</th>
                    </tr></thead>
                    <tbody>
                      {selectedDayData.inbound_events.map(e => (
                        <tr key={`i-${e.purchase_order_id}`}>
                          <td>{e.po_number}</td>
                          <td>{e.supplier}</td>
                          <td>{e.estimated_receive_date || '—'}</td>
                          <td className="text-end fw-bold text-success">+{e.quantity_due}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="row g-2">
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header py-2 d-flex justify-content-between">
              <span>Upcoming Demand (Laser tasks)</span>
              <span className="badge bg-secondary">{demandRows.length}</span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {demandRows.length === 0 ? (
                <div className="p-3 text-muted small">No scheduled or pending Laser tasks consuming this material in the horizon.</div>
              ) : (
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light sticky-top"><tr>
                    <th>Date</th><th>Job</th><th>Cut Sheet</th><th>Status</th><th className="text-end">Sheets</th>
                  </tr></thead>
                  <tbody>
                    {demandRows.slice(0, 100).map((r, idx) => (
                      <tr key={`dr-${r.task_id}-${idx}`} style={{ cursor: 'pointer' }} onClick={() => setForecastSelectedDay(r.date)}>
                        <td>{r.date}</td>
                        <td>{r.master_job_num ? `#${r.master_job_num}` : `Job ${r.job_id}`}</td>
                        <td>{r.cut_sheet_name}</td>
                        <td><span className="badge bg-secondary">{r.status}</span></td>
                        <td className="text-end">{r.sheets}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {demandRows.length > 100 && (
                <div className="text-muted small p-2">Showing first 100 of {demandRows.length}</div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header py-2 d-flex justify-content-between">
              <span>Inbound POs</span>
              <span className="badge bg-success">{inboundRows.length}</span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {inboundRows.length === 0 ? (
                <div className="p-3 text-muted small">No inbound POs scheduled in the horizon.</div>
              ) : (
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light sticky-top"><tr>
                    <th>ETA</th><th>PO</th><th>Supplier</th><th className="text-end">Qty Due</th>
                  </tr></thead>
                  <tbody>
                    {inboundRows.map((r, idx) => (
                      <tr key={`ir-${r.purchase_order_id}-${idx}`} style={{ cursor: 'pointer' }} onClick={() => setForecastSelectedDay(r.date)}>
                        <td>{r.date}</td>
                        <td>{r.po_number}</td>
                        <td>{r.supplier}</td>
                        <td className="text-end fw-bold text-success">+{r.quantity_due}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {(unscheduled_demand.task_count > 0 || undated_inbound.po_count > 0) && (
        <div className="row g-2 mt-1">
          {unscheduled_demand.task_count > 0 && (
            <div className="col-md-6">
              <div className="card border-warning">
                <div className="card-header py-2 bg-warning bg-opacity-10">
                  ⚠ Unscheduled demand: <b>{unscheduled_demand.total_sheets}</b> {unit} across {unscheduled_demand.task_count} task(s)
                </div>
                <div className="card-body p-0" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  <table className="table table-sm mb-0">
                    <thead className="table-light sticky-top"><tr>
                      <th>Job</th><th>Cut Sheet</th><th>Status</th><th className="text-end">Sheets</th>
                    </tr></thead>
                    <tbody>
                      {unscheduled_demand.tasks.map(t => (
                        <tr key={`u-${t.task_id}`}>
                          <td>{t.master_job_num ? `#${t.master_job_num}` : `Job ${t.job_id}`}</td>
                          <td>{t.cut_sheet_name}</td>
                          <td><span className="badge bg-secondary">{t.status}</span></td>
                          <td className="text-end">{t.sheets}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer py-1 small text-muted">
                  {forecastUnsched === 'exclude' && <>NOT in the projected curve. Use the <b>Unsched</b> toggle to fold these in, or set scheduled_date on the tasks.</>}
                  {forecastUnsched === 'today' && <>Piled onto today in the curve (worst case). Set scheduled_date or switch to Exclude/Spread for a realistic view.</>}
                  {forecastUnsched === 'spread' && <>Distributed evenly across the horizon in the curve. Set scheduled_date for precise placement.</>}
                </div>
              </div>
            </div>
          )}
          {undated_inbound.po_count > 0 && (
            <div className="col-md-6">
              <div className="card border-info">
                <div className="card-header py-2 bg-info bg-opacity-10">
                  ℹ POs with no ETA: <b>{undated_inbound.total_quantity}</b> {unit} across {undated_inbound.po_count} PO(s)
                </div>
                <div className="card-body p-0" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  <table className="table table-sm mb-0">
                    <thead className="table-light sticky-top"><tr>
                      <th>PO</th><th>Supplier</th><th className="text-end">Qty Due</th>
                    </tr></thead>
                    <tbody>
                      {undated_inbound.pos.map(p => (
                        <tr key={`up-${p.purchase_order_id}`}>
                          <td>{p.po_number}</td>
                          <td>{p.supplier}</td>
                          <td className="text-end fw-bold text-success">+{p.quantity_due}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer py-1 small text-muted">
                  NOT included in the projected balance line. Set an estimated_receive_date on the PO to place these on the timeline.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MaterialEntryPage() {
  // ======================= STATE DEFINITIONS =======================

  // Left panel
  const [materials, setMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [stockTypeFilter, setStockTypeFilter] = useState('Sheet Stock');

  // Right panel: material detail
  const [dbMaterialId, setDbMaterialId] = useState(null);
  const [materialCode, setMaterialCode] = useState("");
  const [description, setDescription] = useState("");
  const [materialType, setMaterialType] = useState("");
  const [stockQuantity, setStockQuantity] = useState("");
  const [originalStockQuantity, setOriginalStockQuantity] = useState("");
  const [unitOfMeasurement, setUnitOfMeasurement] = useState("");
  const [reservedQuantity, setReservedQuantity] = useState("");
  const [inboundQuantity, setInboundQuantity] = useState(0);
  const [minimumAvailable, setMinimumAvailable] = useState("");
  const [ownerCustomerId, setOwnerCustomerId] = useState("");

  // NEW: State to manage which category is being edited/created
  const [formMaterialCategory, setFormMaterialCategory] = useState('Sheet Stock');

  // Sheet Stock Specific State
  const [thicknessOptions, setThicknessOptions] = useState([]);
  const [thicknessId, setThicknessId] = useState("");
  const [surfaceFinish, setSurfaceFinish] = useState("");
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [weight, setWeight] = useState("");

  // NEW: Shape Stock Specific State
  const [profileShape, setProfileShape] = useState("");
  const [profileWidth, setProfileWidth] = useState("");
  const [profileHeight, setProfileHeight] = useState("");
  const [wallThickness, setWallThickness] = useState("");
  const [stockLength, setStockLength] = useState("");
  const [schedule, setSchedule] = useState("");           // For Pipe only
  const [poundsPerFoot, setPoundsPerFoot] = useState(""); // For Channel, I-Beam, Tee only
  const [nps, setNps] = useState("");                     // Nominal Pipe Size for Pipe only
  const [notes, setNotes] = useState("");
  const [stockWeight, setStockWeight] = useState("");     // Full stick weight (lbs) for Shape Stock

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [txDelta, setTxDelta] = useState("");
  const [txReason, setTxReason] = useState("");
  const [txJobId, setTxJobId] = useState("");
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // === Analytics State ===
  const [priceHistory, setPriceHistory] = useState([]);
  const [consumptionHistory, setConsumptionHistory] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Default analytics date range: Last 6 months
  const [analyticsStart, setAnalyticsStart] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  });
  const [analyticsEnd, setAnalyticsEnd] = useState(() => shopToday());
  const [analyticsGroupBy, setAnalyticsGroupBy] = useState("month");

  // Error/loading states
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Stock Take
  const [stockTakeMode, setStockTakeMode] = useState(false);
  const [stockTakeFilterType, setStockTakeFilterType] = useState("");
  const [stockTakeFilterOwner, setStockTakeFilterOwner] = useState("");
  const [stockTakeFilterThickness, setStockTakeFilterThickness] = useState("");
  const [stockTakeFilterFinish, setStockTakeFilterFinish] = useState("");
  const [counts, setCounts] = useState({});

  // Low Stock / Shortages
  const [materialShortages, setMaterialShortages] = useState([]);
  const [selectedShortageIds, setSelectedShortageIds] = useState([]);
  const [hideRemnantShortages, setHideRemnantShortages] = useState(true);

  // Multi-supplier
  const [materialVendors, setMaterialVendors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [newVendorId, setNewVendorId] = useState("");
  const [newVendorCost, setNewVendorCost] = useState("");
  const [newVendorLeadTime, setNewVendorLeadTime] = useState("");
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [editCost, setEditCost] = useState("");
  const [editLeadTime, setEditLeadTime] = useState("");

  // Owner filter on the left panel
  const [ownerFilter, setOwnerFilter] = useState("");
  const [hideRemnants, setHideRemnants] = useState(true);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [customers, setCustomers] = useState([]);
  
  // For navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Active Tab State
  const [activeTab, setActiveTab] = useState('details');

  // Material Tracking state (global Tracking tab + per-material section in Details tab)
  const [trackingMtns, setTrackingMtns] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingSearch, setTrackingSearch] = useState('');
  const [trackingStatus, setTrackingStatus] = useState('all');
  const [trackingSupplier, setTrackingSupplier] = useState('');
  const [trackingMaterialType, setTrackingMaterialType] = useState('');
  const [trackingThicknessId, setTrackingThicknessId] = useState('');
  const [trackingFinish, setTrackingFinish] = useState('');
  const [trackingOwner, setTrackingOwner] = useState('');
  const [trackingSort, setTrackingSort] = useState({ key: 'mtn_id', dir: 'desc' });
  const [countSheetLoading, setCountSheetLoading] = useState(false);
  // Remnant origins (2026-10-14): remnants whose pieces have no lot, and recording their tags.
  const [showRemnantLineage, setShowRemnantLineage] = useState(false);

  // RFQ modal state (Material Details tab → Request Quote button)
  const [showRfqModal, setShowRfqModal] = useState(false);
  const [rfqQty, setRfqQty] = useState('');
  const [rfqSelectedVendorIds, setRfqSelectedVendorIds] = useState([]);
  const [rfqShowAllVendors, setRfqShowAllVendors] = useState(false);
  const [rfqRequestedDeliveryDate, setRfqRequestedDeliveryDate] = useState('');
  const [rfqDueDate, setRfqDueDate] = useState('');
  const [rfqNotes, setRfqNotes] = useState('');
  const [rfqSubmitting, setRfqSubmitting] = useState(false);
  const [rfqSendEmail, setRfqSendEmail] = useState(true);
  const [rfqEmailBody, setRfqEmailBody] = useState('');
  const [materialMtns, setMaterialMtns] = useState([]);
  const [materialMtnLoading, setMaterialMtnLoading] = useState(false);
  const [materialMtnFilter, setMaterialMtnFilter] = useState('all');
  const [selectedMtnId, setSelectedMtnId] = useState(null);
  const [showMtnModal, setShowMtnModal] = useState(false);

  // Runout Forecast state
  const [runoutForecastData, setRunoutForecastData] = useState(null);
  const [runoutForecastLoading, setRunoutForecastLoading] = useState(false);
  const [runoutForecastError, setRunoutForecastError] = useState(null);
  const [runoutForecastHorizon, setRunoutForecastHorizon] = useState(180);
  const [runoutForecastSelectedDay, setRunoutForecastSelectedDay] = useState(null);
  const [runoutForecastUnsched, setRunoutForecastUnsched] = useState('exclude'); // 'exclude' | 'today' | 'spread'

  // CWT Pricing State
  const [cwtRules, setCwtRules] = useState([]);
  const [cwtLoading, setCwtLoading] = useState(false);
  const [editingCwtRule, setEditingCwtRule] = useState(null); 
  const [newCwtRule, setNewCwtRule] = useState({
      material_type: "",
      surface_finish: "",
      thickness_id: "",
      cwt_price: "",
      vendor_id: "",
      sheet_cost: "",
      sheet_width: "",
      sheet_length: ""
  });
  const [cwtFilter, setCwtFilter] = useState({
      material_type: "",
      surface_finish: "",
      thickness_id: "",
      vendor_id: ""
  });

  const [selectedCwtRuleIds, setSelectedCwtRuleIds] = useState([]);
  const [bulkCwtPrice, setBulkCwtPrice] = useState("");
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Inventory Transfer State
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  // Earmark sheets of this material for a sales order (pallets on the floor or on order).
  const [showEarmarkModal, setShowEarmarkModal] = useState(false);
  const [transferDestId, setTransferDestId] = useState("");
  const [transferQty, setTransferQty] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  // Source MTN selection — required when the source material has any MTN rows
  // with remaining qty so the new MTN inherits the right heat/PO and the
  // source MTN's remaining qty decrements correctly. Multiple physical stacks
  // can share a material_id, so this can't be auto-picked.
  const [transferSourceMtnId, setTransferSourceMtnId] = useState("");
  // "Create it for <owner>" (2026-10-29): the API's answer for the Owner filter (GET /materials/{id}/owner_copy,
  // kept with the material + owner it was asked for) and the "material:owner" the box was ticked for. Both count
  // only while they match the modal's material and Owner filter; opening the modal or picking an owner clears the
  // box, so it never carries to another material or back to an owner (review 2026-10-29). ownerCopyTick re-reads.
  const [ownerCopyRead, setOwnerCopyRead] = useState(null);
  const [transferCreateFor, setTransferCreateFor] = useState("");
  const [ownerCopyTick, setOwnerCopyTick] = useState(0);

  // NEW: State for the list of inbound orders
  const [inboundOrders, setInboundOrders] = useState([]);
  const [inboundOrdersLoading, setInboundOrdersLoading] = useState(false);

  // ======= FORECAST SECTION STATE ========
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastRows, setForecastRows] = useState([]);
  const [forecastStart, setForecastStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1));
    return d.toISOString().slice(0, 10);
  });
  const [forecastEnd, setForecastEnd] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 28);
    return d.toISOString().slice(0, 10);
  });
  const [forecastError, setForecastError] = useState(null);

    // ======= NEW: DENSITY MANAGER STATE ========
  const [densities, setDensities] = useState([]);
  const [densitiesLoading, setDensitiesLoading] = useState(false);
  const [editingDensity, setEditingDensity] = useState(null); // { id, type, value }
  const [newDensity, setNewDensity] = useState({ type: "", value: "" });

  // ======= NEW: THICKNESS MANAGER STATE ========
  const [thicknesses, setThicknesses] = useState([]);
  const [thicknessesLoading, setThicknessesLoading] = useState(false);
  const [editingThickness, setEditingThickness] = useState(null); // { id, display_thickness, thickness_in, material_type }
  const [newThickness, setNewThickness] = useState({ display_thickness: "", thickness_in: "", material_type: "" });

  // ======= MODIFICATION: State for new left panel filters ========
  const [materialTypeFilter, setMaterialTypeFilter] = useState("");
  const [thicknessFilter, setThicknessFilter] = useState("");
  const [finishFilter, setFinishFilter] = useState("");
  const [shapeProfileFilter, setShapeProfileFilter] = useState("");
  const [shapeWidthFilter, setShapeWidthFilter] = useState("");
  const [shapeHeightFilter, setShapeHeightFilter] = useState("");
  const [shapeLengthFilter, setShapeLengthFilter] = useState("");

  // ======= NEW: CUSTOMER MATERIAL INTAKE STATE ========
  const [intakeCustomerId, setIntakeCustomerId] = useState("");
  const [intakeMaterials, setIntakeMaterials] = useState([]);
  const [intakeCounts, setIntakeCounts] = useState({});
  const [intakeHeatNumbers, setIntakeHeatNumbers] = useState({});
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeHistory, setIntakeHistory] = useState([]);
  const [intakeHistoryLoading, setIntakeHistoryLoading] = useState(false);
  const [intakeFilterType, setIntakeFilterType] = useState("");
  const [intakeFilterThickness, setIntakeFilterThickness] = useState("");
  const [intakeFilterFinish, setIntakeFilterFinish] = useState("");
  const [intakeShipmentNumber, setIntakeShipmentNumber] = useState("");
  const [intakeManifestFile, setIntakeManifestFile] = useState(null);

  // Sub-tab inside Customer Material — 'intake' (receive) or 'return' (send back)
  const [intakeSubTab, setIntakeSubTab] = useState('intake');

  // ======= CUSTOMER MATERIAL RETURN STATE ========
  // Shares intakeCustomerId with the Intake sub-tab so the customer picker
  // applies to both. Per-row selection + per-row qty override (blank = return
  // all available); default behaviour is "return everything unreserved".
  const [cmrSelected, setCmrSelected] = useState({});      // { [material_id]: true }
  const [cmrQuantities, setCmrQuantities] = useState({});  // { [material_id]: "string" }
  const [cmrReturnDate, setCmrReturnDate] = useState(() => shopToday());
  const [cmrNotes, setCmrNotes] = useState("");
  const [cmrPin, setCmrPin] = useState("");
  const [cmrSubmitting, setCmrSubmitting] = useState(false);
  const [cmrFilterType, setCmrFilterType] = useState("");
  const [cmrFilterThickness, setCmrFilterThickness] = useState("");
  const [cmrFilterFinish, setCmrFilterFinish] = useState("");
  // History of past returns for the selected customer (loaded on customer pick + after submit)
  const [cmrHistory, setCmrHistory] = useState([]);
  const [cmrHistoryLoading, setCmrHistoryLoading] = useState(false);

  // A. Add new state variables
  const [materialTypes, setMaterialTypes] = useState([]);
  const [surfaceFinishes, setSurfaceFinishes] = useState([]);

  // ======= REMNANT STATE ========
  const [dropMaterialType, setDropMaterialType] = useState("");
  const [dropThicknessId, setDropThicknessId] = useState("");
  const [dropSurfaceFinish, setDropSurfaceFinish] = useState("");
  const [dropOwnerId, setDropOwnerId] = useState("");
  const [dropLength, setDropLength] = useState("");
  const [dropWidth, setDropWidth] = useState("");
  const [dropLoading, setDropLoading] = useState(false);

  // Remnant inventory + creation form
  const [remnants, setRemnants] = useState([]);
  const [remnantsLoading, setRemnantsLoading] = useState(false);
  const [remnantSourceMaterialId, setRemnantSourceMaterialId] = useState("");
  const [remnantWidth, setRemnantWidth] = useState("");
  const [remnantLength, setRemnantLength] = useState("");
  const [remnantQty, setRemnantQty] = useState("1");
  const [remnantMtnId, setRemnantMtnId] = useState("");
  const [remnantNotes, setRemnantNotes] = useState("");
  const [remnantCreating, setRemnantCreating] = useState(false);
  // Remnant inventory list view filters (Owner/Type/Thickness/Finish + search)
  const [remnantOwnerFilter, setRemnantOwnerFilter] = useState("");
  const [remnantTypeFilter, setRemnantTypeFilter] = useState("");
  const [remnantThicknessFilter, setRemnantThicknessFilter] = useState("");
  const [remnantFinishFilter, setRemnantFinishFilter] = useState("");
  const [remnantSearch, setRemnantSearch] = useState("");

  // Bulk return-to-vendor (remnants → supplier paperwork + audit log)
  const [selectedRemnantIds, setSelectedRemnantIds] = useState([]);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnDate, setReturnDate] = useState(() => shopToday());
  const [returnNotes, setReturnNotes] = useState("");
  const [returnPin, setReturnPin] = useState("");
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  // Record-new-remnant filters (sticky — persist across creations to speed up batch entry)
  const [newRemnantOwnerFilter, setNewRemnantOwnerFilter] = useState("");
  const [newRemnantTypeFilter, setNewRemnantTypeFilter] = useState("");
  const [newRemnantThicknessFilter, setNewRemnantThicknessFilter] = useState("");
  const [newRemnantFinishFilter, setNewRemnantFinishFilter] = useState("");

  // Edit-a-single-remnant + delete-a-single-remnant
  const [editingRemnant, setEditingRemnant] = useState(null);
  const [editRemnantWidth, setEditRemnantWidth] = useState("");
  const [editRemnantLength, setEditRemnantLength] = useState("");
  const [editRemnantQty, setEditRemnantQty] = useState("1");
  const [editRemnantNotes, setEditRemnantNotes] = useState("");
  const [editRemnantSubmitting, setEditRemnantSubmitting] = useState(false);
  const [deletingRemnant, setDeletingRemnant] = useState(null);
  const [deleteRemnantSubmitting, setDeleteRemnantSubmitting] = useState(false);

  // --- NEW: Warning state for Missing Laser Tech Specs ---
  const [missingSpecWarning, setMissingSpecWarning] = useState(null);

  // TRANSFER MODAL FILTERS & LOGIC
  const [transferFilters, setTransferFilters] = useState({
    owner: "",
    type: "",
    thickness: "",
    finish: "",
    search: ""
  });

  // Clear AI context on unmount
  useEffect(() => () => clearAiContext(), []);

  // "Smart" Filter Initialization:
  // When modal opens, pre-fill filters to match the SOURCE material's physical properties.
  // This assumes you usually transfer to the exact same material (just different owner/location).
  useEffect(() => {
    if (isTransferModalOpen) {
      // materialData: the same type / thickness / finish, any owner
      setTransferFilters(transferFilterDefaults({ material_type: materialType, thickness_id: thicknessId, surface_finish: surfaceFinish }));
      setTransferCreateFor("");
      setOwnerCopyRead(null);
    }
  }, [isTransferModalOpen, materialType, thicknessId, surfaceFinish]);

  // Auto-select the source MTN when there's exactly one eligible — no choice to
  // make, so don't force the operator to click. Uses functional setState so this
  // effect doesn't need transferSourceMtnId in its deps (which would re-trigger
  // after the auto-pick and fight a manual change).
  useEffect(() => {
    if (!isTransferModalOpen) return;
    const only = onlyOpenLotId(materialMtns); // features/materials/materialData (the phone's too)
    if (!only) return;
    setTransferSourceMtnId(prev => prev || only);
  }, [isTransferModalOpen, materialMtns]);

  // Computed list of valid destinations based on filters
  const filteredTransferDestinations = useMemo(() => {
    return transferDestinations(materials, dbMaterialId, transferFilters); // features/materials/materialData
  }, [materials, dbMaterialId, transferFilters]);


  // Base URL for your API
  const API_BASE = config.API_BASE_URL;

  // An Owner picked in the transfer filters: ask the API whether that owner already has this steel and what a copy
  // would be called. The page can't say: its own code preview floors 7.33 to 7 and leaves out REM- and the owner.
  useEffect(() => {
    const owner = transferFilters.owner;
    if (!isTransferModalOpen || !dbMaterialId || !owner) return undefined;
    let live = true;
    loadOwnerCopy(dbMaterialId, owner).then((data) => {   // features/materials/materialData (null on a failed read)
      if (live) setOwnerCopyRead({ material: dbMaterialId, owner: String(owner), data });
    });
    return () => { live = false; };
  }, [isTransferModalOpen, dbMaterialId, transferFilters.owner, ownerCopyTick]);
  const ownerCopy = (isTransferModalOpen && ownerCopyRead && ownerCopyRead.material === dbMaterialId
    && ownerCopyRead.owner === String(transferFilters.owner || '')) ? ownerCopyRead.data : null;
  const transferCreateDest = !!ownerCopy?.can_create && transferCreateFor === `${dbMaterialId}:${transferFilters.owner}`;

  // ======================= EFFECTS / LOADERS =======================

  async function loadAllOptions() {
    try {
      const [typesRes, finishesRes, thickRes, vendorsRes, customersRes, shortagesRes, cwtRes, densitiesRes] = await Promise.all([
        fetch(`${API_BASE}/materials/types`),
        fetch(`${API_BASE}/materials/finishes`), // Fetch all possible finishes once
        fetch(`${API_BASE}/material_thicknesses`),
        fetch(`${API_BASE}/vendors`),
        fetch(`${API_BASE}/customers`),
        fetch(`${API_BASE}/materials/below_min`),
        fetch(`${API_BASE}/pricing/cwt`),
        fetch(`${API_BASE}/material_densities`)
      ]);
  
      const thickData = await thickRes.json();
  
      setMaterialTypes(await typesRes.json());
      setSurfaceFinishes(await finishesRes.json()); // Store the master list of all finishes
      setThicknessOptions(thickData);
      setThicknesses(thickData);
      setVendors(await vendorsRes.json());
      setCustomers((await customersRes.json()).sort((a, b) => a.name.localeCompare(b.name)));
      setMaterialShortages(await shortagesRes.json());
      setCwtRules(await cwtRes.json());
      setDensities(await densitiesRes.json());
  
    } catch (err) {
      setError("Failed to load initial page data: " + err.message);
    }
  }

  // B. Fetch Dynamic Data
  useEffect(() => {
    // Load all necessary dropdown data on mount
    loadAllOptions();
    loadMaterials(); // Keep this separate if it reloads often
    // eslint-disable-next-line
  }, []); // Empty dependency array, runs once.
  
  // === Analytics Fetch Logic ===
  useEffect(() => {
    if (dbMaterialId) {
      fetchAnalyticsData();
    }
  }, [dbMaterialId, analyticsStart, analyticsEnd, analyticsGroupBy]);

  const fetchAnalyticsData = async () => {
    if (!dbMaterialId) return;
    setAnalyticsLoading(true);
    try {
      // 1. Fetch Price History
      const priceRes = await fetch(`${API_BASE}/materials/${dbMaterialId}/analytics/price_history?start_date=${analyticsStart}&end_date=${analyticsEnd}`);
      if (priceRes.ok) {
        setPriceHistory(await priceRes.json());
      }

      // 2. Fetch Consumption History
      const consRes = await fetch(`${API_BASE}/materials/${dbMaterialId}/analytics/consumption_history?start_date=${analyticsStart}&end_date=${analyticsEnd}&group_by=${analyticsGroupBy}`);
      if (consRes.ok) {
        setConsumptionHistory(await consRes.json());
      }
    } catch (err) {
      console.error("Failed to load analytics:", err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // ======= FORECAST: FETCH & LOAD WHEN MATERIAL OR DATES CHANGE ========
  async function fetchMaterialForecast(matId, start, end) {
    if (!matId) {
      setForecastRows([]);
      return;
    }
    setForecastLoading(true);
    setForecastError(null);
    try {
      const res = await fetch(
        `${API_BASE}/reports/weekly_sheet_order_plan?start_date=${start}&end_date=${end}`
      );
      if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to load the order forecast"));
      const rows = await res.json();

      const filtered = rows.filter(
        (r) => r.material_id === matId && r.week_start !== "FINAL_SUM"
      );
      setForecastRows(filtered);
    } catch (err) {
      setForecastError("Could not load forecast. " + (err.message || err.toString()));
      setForecastRows([]);
    } finally {
      setForecastLoading(false);
    }
  }

  useEffect(() => {
    if (!dbMaterialId) {
      setForecastRows([]);
      return;
    }
    fetchMaterialForecast(dbMaterialId, forecastStart, forecastEnd);
    // eslint-disable-next-line
  }, [dbMaterialId, forecastStart, forecastEnd]);

  useEffect(() => {
  const calculateWeight = async () => {
    // Ensure all required values are present and valid
    const lengthNum = parseFloat(length);
    const widthNum = parseFloat(width);
    const selectedThicknessId = parseInt(thicknessId, 10);

    if (!lengthNum || !widthNum || !selectedThicknessId || !materialType) {
      setWeight(""); // Clear weight if inputs are missing
      return;
    }

    // Find the selected thickness object to get its value in inches
    const selectedThickness = thicknessOptions.find(
      (opt) => opt.thickness_id === selectedThicknessId
    );

    if (!selectedThickness || !selectedThickness.thickness_in) {
      return;
    }

    const thicknessIn = parseFloat(selectedThickness.thickness_in);

    try {
      // Fetch the density for the selected material type
      const res = await fetch(`${API_BASE}/densities/${encodeURIComponent(materialType)}`);
      if (!res.ok) {
        setWeight(""); // Clear weight if density is not found
        return;
      }
      const data = await res.json();
      const density = parseFloat(data.density);

      // Perform the calculation
      const calculatedWeight = lengthNum * widthNum * thicknessIn * density;
      setWeight(calculatedWeight.toFixed(1)); // Update the weight state, rounded to 1 decimal place
    } catch (err) {
      console.error("Failed to calculate weight:", err);
      setWeight(""); // Clear on error
    }
  };

  calculateWeight();
}, [length, width, thicknessId, materialType, thicknessOptions, API_BASE]); // Dependencies array

  useEffect(() => {
    if (dbMaterialId) {
      const fetchInboundOrders = async () => {
        setInboundOrdersLoading(true);
        // features/materials/materialData (the phone's too); unreadable = an empty list here, no big error
        setInboundOrders((await loadInboundOrders(dbMaterialId)) || []);
        setInboundOrdersLoading(false);
      };
      fetchInboundOrders();
    } else {
      // Clear the list if no material is selected
      setInboundOrders([]);
    }
  }, [dbMaterialId, API_BASE]); // Dependency array ensures this runs when dbMaterialId changes

  useEffect(() => {
    const calculateCwtPrice = async () => {
      const { sheet_cost, sheet_width, sheet_length, material_type, thickness_id } = newCwtRule;

      // Check if all necessary fields have valid values
      if (
        parseFloat(sheet_cost) > 0 &&
        parseFloat(sheet_width) > 0 &&
        parseFloat(sheet_length) > 0 &&
        material_type &&
        thickness_id
      ) {
        try {
          const payload = {
            sheet_cost: parseFloat(sheet_cost),
            sheet_width: parseFloat(sheet_width),
            sheet_length: parseFloat(sheet_length),
            material_type: material_type,
            thickness_id: parseInt(thickness_id, 10),
          };

          const res = await fetch(`${API_BASE}/pricing/cwt/calculate-from-sheet`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!res.ok) {
            // If calculation fails, clear the price
            setNewCwtRule(prev => ({ ...prev, cwt_price: "" }));
            return;
          }

          const data = await res.json();
          // Update the CWT price field with the calculated value
          setNewCwtRule(prev => ({ ...prev, cwt_price: data.calculated_cwt_price.toFixed(2) }));

        } catch (err) {
          console.error("Failed to calculate CWT price:", err);
          setNewCwtRule(prev => ({ ...prev, cwt_price: "" }));
        }
      }
    };

    calculateCwtPrice();
  }, [newCwtRule.sheet_cost, newCwtRule.sheet_width, newCwtRule.sheet_length, newCwtRule.material_type, newCwtRule.thickness_id, API_BASE]);

  // --- Check for Laser Tech Spec existence when material changes ---
  useEffect(() => {
    // Only validate if it is Sheet Stock and we have the necessary data
    if (formMaterialCategory !== 'Sheet Stock' || !materialType || !thicknessId) {
        setMissingSpecWarning(null);
        return;
    }

    // Find display name for thickness for a nicer error message
    const thickObj = thicknessOptions.find(t => t.thickness_id === parseInt(thicknessId));
    const thickName = thickObj ? thickObj.display_thickness : thicknessId;

    fetch(`${API_BASE}/laser_specs/validate?material_type=${encodeURIComponent(materialType)}&thickness_id=${thicknessId}`)
        .then(res => res.json())
        .then(data => {
            if (!data.exists) {
                setMissingSpecWarning(`WARNING: Material '${materialType} - ${thickName}' has no Laser Tech Spec. Gas tracking will fail.`);
            } else {
                setMissingSpecWarning(null);
            }
        })
        .catch(e => console.error(e));
  }, [formMaterialCategory, materialType, thicknessId, thicknessOptions]);

  useEffect(() => {
      const calculateCwtPriceForEdit = async () => {
        // Exit if we are not in editing mode
        if (!editingCwtRule) return;

        const { sheet_cost, sheet_width, sheet_length, material_type, thickness_id } = editingCwtRule;

        if (
          parseFloat(sheet_cost) > 0 &&
          parseFloat(sheet_width) > 0 &&
          parseFloat(sheet_length) > 0 &&
          material_type &&
          thickness_id
        ) {
          try {
            const payload = {
              sheet_cost: parseFloat(sheet_cost),
              sheet_width: parseFloat(sheet_width),
              sheet_length: parseFloat(sheet_length),
              material_type: material_type,
              thickness_id: parseInt(thickness_id, 10),
            };

            const res = await fetch(`${API_BASE}/pricing/cwt/calculate-from-sheet`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            
            if (!res.ok) {
              setEditingCwtRule(prev => ({ ...prev, price: "" }));
              return;
            }

            const data = await res.json();
            setEditingCwtRule(prev => ({ ...prev, price: data.calculated_cwt_price.toFixed(2) }));

          } catch (err) {
            console.error("Failed to calculate CWT price for edit:", err);
            setEditingCwtRule(prev => ({ ...prev, price: "" }));
          }
        }
      };

      calculateCwtPriceForEdit();
  }, [editingCwtRule?.sheet_cost, editingCwtRule?.sheet_width, editingCwtRule?.sheet_length, editingCwtRule, API_BASE]);  

  
  async function loadMaterials() {
    setLoading(true);
    setError(null);
    try {
      const data = await loadMaterialList(); // features/materials/materialData
      setMaterials(data);
      return data; // Return data for chaining
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMaterialVendors(matId) {
    try {
      const r = await fetch(`${API_BASE}/materials/${matId}/vendors`);
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r, `Failed to load vendors for material #${matId}`));
      }
      const data = await r.json();
      setMaterialVendors(data);
    } catch (err) {
      console.error("Failed to load material vendors:", err);
      setMaterialVendors([]);
    }
  }

  async function loadMaterialTransactions(mId, sDate = startDate, eDate = endDate) {
    setTransactionsLoading(true);
    setTransactions([]);
    try {
      setTransactions(await fetchMaterialTransactions(mId, { start: sDate, end: eDate })); // materialData
    } catch (err) {
      console.error("Failed to load transactions:", err);
    } finally {
      setTransactionsLoading(false);
    }
  }

  async function handleConfirmTransfer() {
    // features/materials/materialActions (the phone's transfer too): the checks, the PIN, the request, the
    // success alert and the receipt printed. The page keeps its banner, the modal and the re-reads.
    const createFor = transferCreateDest ? ownerCopy.owner_customer_id : null;
    const r = await transferStock({
      sourceMaterialId: dbMaterialId, destinationMaterialId: createFor ? '' : transferDestId, createDestinationForOwnerId: createFor,
      quantity: transferQty, reason: transferReason,
      sourceMtnId: transferSourceMtnId, onHand: stockQuantity, lots: materialMtns, ui: desktopUi, printPdf: openPdfInPrintDialog,
      setBusy: (b) => { setTransferLoading(b); if (b) setError(null); },
    });
    if (r.needsLot) {
      await loadMaterialMtns(dbMaterialId);
      setError(r.message);
      return;
    }
    if (!r.done) {
      if (r.message) setError(r.message);
      // The owner may have this steel by now (someone else's transfer, a 409): ask again, the box follows.
      if (createFor) setOwnerCopyTick((t) => t + 1);
      return;
    }
    // Close modal and refresh data
    setIsTransferModalOpen(false);
    setTransferDestId("");
    setTransferQty("");
    setTransferReason("");
    setTransferSourceMtnId("");
    setTransferCreateFor("");
    setOwnerCopyRead(null);

    await loadMaterials();
    // Reselect the current material to update its details in the form
    handleSelectMaterial({ material_id: dbMaterialId });
  }

  // ======================= FILTERING =======================

  // Everything but "In stock only"; that one is applied last so the list can say how many it hid.
  const listCandidates = materialListCandidates(materials, { // features/materials/materialData (the phone's list too)
    category: stockTypeFilter, hideRemnants, search: searchTerm, owner: ownerFilter, materialType: materialTypeFilter,
    thickness: thicknessFilter, finish: finishFilter, profileShape: shapeProfileFilter, profileWidth: shapeWidthFilter,
    profileHeight: shapeHeightFilter, stockLength: shapeLengthFilter,
  });

  // In stock = QoH above zero (materialData.isInStock). The open material stays listed even at zero (a new one, or one just counted out).
  const outOfStockCount = listCandidates.filter((m) => !isInStock(m)).length;
  const filteredMaterials = inStockOnly
    ? listCandidates.filter((m) => isInStock(m) || m.material_id === selectedMaterial?.material_id)
    : listCandidates;
  const outOfStockHidden = listCandidates.length - filteredMaterials.length;

  const filteredCwtRules = useMemo(() => {
    return cwtRules.filter(rule => {
        const { material_type, surface_finish, thickness_id, vendor_id } = cwtFilter;
        if (material_type && rule.material_type !== material_type) return false;
        if (surface_finish && rule.surface_finish !== surface_finish) return false;
        if (thickness_id && rule.thickness_id !== parseInt(thickness_id)) return false;

        if (vendor_id) {
            if (vendor_id === 'null') {
                if (rule.vendor_id !== null) return false;
            } else {
                if (rule.vendor_id !== parseInt(vendor_id)) return false;
            }
        }
        return true;
    });
  }, [cwtRules, cwtFilter]);


  function formatNumericValue(val) {
    const num = Number(val) || 0;
    if (num < 0) {
      return <span style={{ color: "red", fontWeight: "bold" }}>{num}</span>;
    }
    return num;
  }

  // ======================= SELECTION / FORM FILL =======================

  // --- MODIFIED: Reset thicknessId when creating a new material ---
  function handleCreateNew() {
    setSelectedMaterial(null);
    setDbMaterialId(null);
    setMaterialCode("");
    setDescription("");
    setMaterialType("");
    setStockQuantity("");
    setOriginalStockQuantity("");
    setUnitOfMeasurement("");
    setReservedQuantity("");
    setInboundQuantity(0);
    setTransactions([]);
    setTxDelta("");
    setTxReason("");
    setTxJobId("");
    setMinimumAvailable("");
    setMaterialVendors([]);
    setEditingVendorId(null);
    setOwnerCustomerId("");
    setActiveTab('details');
    setForecastRows([]);
    setForecastError(null);
    setFormMaterialCategory('Sheet Stock');
    setThicknessId("");
    setSurfaceFinish("");
    setWidth("");
    setLength("");
    setWeight("");
    setProfileShape("");
    setProfileWidth("");
    setProfileHeight("");
    setWallThickness("");
    setStockLength("");
    setStockWeight("");
    setSchedule("");
    setPoundsPerFoot("");
    setNps("");
    setNotes("");
    setMissingSpecWarning(null);
    setPriceHistory([]);
    setConsumptionHistory([]);
  }

  async function handleSelectMaterial(m) {
    setSelectedMaterial(m);
    setError(null);
    setAiContext('material', { materialId: m.material_id, materialDescription: m.description, materialCode: m.material_code });
    try {
      const detail = await loadMaterial(m.material_id); // features/materials/materialData
      fillForm(detail);
      loadMaterialVendors(m.material_id);
      loadMaterialMtns(m.material_id);
    } catch (err) {
      setError(err.message);
    }
  }

  // --- Material Tracking helpers ---
  async function loadTrackingMtns() {
    setTrackingLoading(true);
    // features/materials/materialData (the phone's lot lookup too); unreadable = an empty list here
    setTrackingMtns((await loadTrackingLots({ status: trackingStatus, supplier: trackingSupplier, owner: trackingOwner })) || []);
    setTrackingLoading(false);
  }

  // Printable inventory count sheet — active/unused (on-hand) MTN lots grouped by
  // material, scoped by the same filters as the Material Tracking screen.
  async function handlePrintCountSheet() {
    setCountSheetLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', 'on_hand');
      if (trackingOwner) params.set('owner_customer_id', trackingOwner);
      if (trackingSupplier) params.set('supplier', trackingSupplier);
      if (trackingMaterialType) params.set('material_type', trackingMaterialType);
      if (trackingThicknessId) params.set('thickness_id', trackingThicknessId);
      if (trackingFinish) params.set('surface_finish', trackingFinish);
      const res = await fetch(`${API_BASE}/reports/material_count_sheet.pdf?${params.toString()}`);
      if (!res.ok) throw new Error(`Failed to generate count sheet: ${res.status}`);
      const blob = await res.blob();
      openPdfInPrintDialog(blob);
    } catch (err) {
      console.error('handlePrintCountSheet error:', err);
      alert('Could not generate the count sheet. ' + err.message);
    } finally {
      setCountSheetLoading(false);
    }
  }

  async function loadMaterialMtns(materialId) {
    if (!materialId) {
      setMaterialMtns([]);
      return;
    }
    setMaterialMtnLoading(true);
    try {
      setMaterialMtns(await loadMaterialLots(materialId)); // features/materials/materialData
    } catch (err) {
      console.error('loadMaterialMtns error:', err);
      setMaterialMtns([]);
    } finally {
      setMaterialMtnLoading(false);
    }
  }

  async function loadRunoutForecast(materialId, horizon, unsched) {
    const useHorizon = horizon || runoutForecastHorizon;
    const useUnsched = unsched || runoutForecastUnsched;
    if (!materialId) {
      setRunoutForecastData(null);
      return;
    }
    setRunoutForecastLoading(true);
    setRunoutForecastError(null);
    setRunoutForecastSelectedDay(null);
    try {
      const res = await fetch(`${API_BASE}/materials/${materialId}/runout_forecast?horizon_days=${useHorizon}&unscheduled_treatment=${useUnsched}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRunoutForecastData(data);
    } catch (err) {
      console.error('loadRunoutForecast error:', err);
      setRunoutForecastError(String(err.message || err));
      setRunoutForecastData(null);
    } finally {
      setRunoutForecastLoading(false);
    }
  }

  // Auto-load runout forecast when the selected material, horizon, or
  // unscheduled-treatment changes while we're on the Material Details tab
  // (the chart lives at the bottom of that tab now).
  useEffect(() => {
    if (activeTab === 'details' && dbMaterialId) {
      loadRunoutForecast(dbMaterialId, runoutForecastHorizon, runoutForecastUnsched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbMaterialId, runoutForecastHorizon, runoutForecastUnsched]);

  // A link to a lot (a pallet beacon's QR: /s/mtn-409 -> ?tab=tracking&mtn=409) opens the
  // Material Tracking tab with that TRK's popup (utils/scanCodes materialsEntryDeepLink).
  useEffect(() => {
    const link = materialsEntryDeepLink(location.search);
    if (!link) return;
    setActiveTab(link.tab);
    if (link.mtnId) {
      setSelectedMtnId(link.mtnId);
      setShowMtnModal(true);
    }
  }, [location.search]);

  const closeMtnModal = () => {
    setShowMtnModal(false);
    // Closing a linked popup drops ?mtn= so a reload doesn't reopen it (and the same link works again).
    const link = materialsEntryDeepLink(location.search);
    if (link && link.mtnId) navigate({ pathname: location.pathname, search: '?tab=tracking' }, { replace: true });
  };

  // Reload tracking list when the Tracking tab becomes active or when status/supplier filters change
  useEffect(() => {
    if (activeTab === 'tracking') {
      loadTrackingMtns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, trackingStatus, trackingSupplier, trackingOwner]);

  // Derived: unique supplier list for the supplier dropdown filter
  const trackingSupplierOptions = useMemo(() => {
    const s = new Set();
    trackingMtns.forEach(m => { if (m.supplier) s.add(m.supplier); });
    return Array.from(s).sort();
  }, [trackingMtns]);

  // Derived: unique material type / thickness / finish lists, computed from the loaded
  // tracking rows so each dropdown only offers values that actually appear in results.
  const trackingMaterialTypeOptions = useMemo(() => {
    const s = new Set();
    trackingMtns.forEach(m => { if (m.material_type) s.add(m.material_type); });
    return Array.from(s).sort();
  }, [trackingMtns]);

  const trackingThicknessOptions = useMemo(() => {
    const seen = new Map(); // key: thickness_id, value: { id, label, in }
    trackingMtns.forEach(m => {
      if (m.thickness_id != null && m.display_thickness) {
        if (!seen.has(m.thickness_id)) {
          seen.set(m.thickness_id, {
            id: m.thickness_id,
            label: m.display_thickness,
            in: m.thickness_in != null ? Number(m.thickness_in) : null
          });
        }
      }
    });
    // Sort ascending by actual thickness_in (so 11ga < 14ga < 1/4" reads numerically)
    return Array.from(seen.values()).sort((a, b) => {
      if (a.in != null && b.in != null) return a.in - b.in;
      return String(a.label).localeCompare(String(b.label));
    });
  }, [trackingMtns]);

  const trackingFinishOptions = useMemo(() => {
    const s = new Set();
    trackingMtns.forEach(m => { if (m.surface_finish) s.add(m.surface_finish); });
    return Array.from(s).sort();
  }, [trackingMtns]);

  // RFQ modal vendor list — known-vendors-first, optionally expanded to all suppliers.
  const rfqVendorList = useMemo(() => {
    const knownIds = new Set(materialVendors.map(mv => mv.vendor_id));
    const known = materialVendors.map(mv => ({
      vendor_id: mv.vendor_id,
      name: mv.vendor_name,
      is_known: true,
      is_preferred: !!mv.is_preferred
    }));
    if (!rfqShowAllVendors) return known;
    const others = vendors
      .filter(v => !knownIds.has(v.vendor_id))
      .map(v => ({ vendor_id: v.vendor_id, name: v.name, is_known: false, is_preferred: false }));
    others.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [...known, ...others];
  }, [materialVendors, vendors, rfqShowAllVendors]);

  // Default RFQ email body — mirrors the server's hardcoded template
  // (POEmailRequest in jobq-api). Operator can edit before send;
  // whatever they type is sent verbatim. The RFQ PDF is attached
  // automatically by the email endpoint.
  function buildDefaultRfqEmailBody({ materialCode, description, qty, unit, requestedDeliveryDate, dueDate }) {
    const fmt = (iso) => {
      if (!iso) return null;
      try {
        const [y, m, d] = iso.split('-');
        return `${m}/${d}/${y}`;
      } catch { return iso; }
    };
    const deliveryLine = requestedDeliveryDate
      ? `Desired Receive Date: ${fmt(requestedDeliveryDate)}\n\n`
      : '';
    const dueLine = dueDate
      ? `Please reply with your quote by ${fmt(dueDate)}.\n\n`
      : 'Please reply with your quote at your earliest convenience.\n\n';
    const itemLine = materialCode
      ? `Material: ${materialCode}${description ? ` — ${description}` : ''}\nQuantity: ${qty || ''} ${unit || 'units'}\n\n`
      : '';
    return (
      `Hello,\n\n` +
      `Please find the attached Request for Quote (RFQ).\n` +
      `We are looking for pricing and availability for the items listed.\n\n` +
      itemLine +
      deliveryLine +
      `Shipping Method: Delivery\n\n` +
      dueLine +
      `Thanks,\nKanCraft Manufacturing`
    );
  }

  // Open the Request Quote modal. Pre-fills qty from the reorder gap and
  // pre-selects every vendor already linked to this material.
  function openRfqModal() {
    setError(null);
    const minA = parseFloat(minimumAvailable);
    const stockQ = parseFloat(stockQuantity);
    const reserved = parseFloat(reservedQuantity);
    let suggested = 1;
    if (!isNaN(minA) && !isNaN(stockQ)) {
      const reservedAdj = isNaN(reserved) ? 0 : reserved;
      const gap = Math.ceil(minA + reservedAdj - stockQ);
      suggested = Math.max(gap, 1);
    }
    setRfqQty(String(suggested));
    setRfqSelectedVendorIds(materialVendors.map(mv => mv.vendor_id));
    setRfqShowAllVendors(false);
    setRfqRequestedDeliveryDate('');
    setRfqDueDate('');
    setRfqNotes('');
    setRfqSendEmail(true);
    setRfqEmailBody(buildDefaultRfqEmailBody({
      materialCode,
      description,
      qty: suggested,
      unit: unitOfMeasurement,
      requestedDeliveryDate: '',
      dueDate: ''
    }));
    setShowRfqModal(true);
  }

  function toggleRfqVendor(vendorId) {
    setRfqSelectedVendorIds(prev => {
      if (prev.includes(vendorId)) return prev.filter(id => id !== vendorId);
      return [...prev, vendorId];
    });
  }

  async function handleSubmitRfq() {
    if (!dbMaterialId) {
      alert('Save the material before requesting a quote.');
      return;
    }
    const qtyNum = parseInt(rfqQty, 10);
    if (!qtyNum || qtyNum <= 0) {
      alert('Quantity must be greater than 0.');
      return;
    }
    if (rfqSelectedVendorIds.length === 0) {
      alert('Select at least one vendor.');
      return;
    }
    const payload = {
      vendor_ids: rfqSelectedVendorIds,
      lines: [{
        item_type: 'Material',
        material_id: parseInt(dbMaterialId, 10),
        consumable_id: null,
        quantity_ordered: qtyNum,
        cost_per_unit: null,
        requested_delivery_date: rfqRequestedDeliveryDate || rfqDueDate || null
      }],
      due_date: rfqDueDate || null,
      notes: rfqNotes || `RFQ for ${materialCode}`
    };
    try {
      setRfqSubmitting(true);
      const res = await fetch(`${API_BASE}/purchase_orders/rfq_group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to create the RFQ"));
      const data = await res.json();

      // Optional follow-up: fan out the operator's email body to each new RFQ PO.
      // Same endpoint POEmailModal calls; same `email_body` override field.
      // Partial failures don't roll back the RFQ — operator can resend from the PO page.
      let emailNote = '\n\nVisit the Purchase Orders page to send emails when ready.';
      const poIds = Array.isArray(data.po_ids) ? data.po_ids : [];
      if (rfqSendEmail && poIds.length > 0) {
        const sendResults = await Promise.allSettled(
          poIds.map(poId =>
            fetch(`${API_BASE}/purchase_orders/${poId}/email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...emailSenderHeaders() },
              body: JSON.stringify({ email_body: rfqEmailBody })
            }).then(async r => {
              if (!r.ok) throw new Error(await apiErrorMessage(r, `Failed to email PO #${poId}`));
              return r.json();
            })
          )
        );
        const sent = sendResults.filter(r => r.status === 'fulfilled').length;
        const failed = sendResults.length - sent;
        emailNote = failed === 0
          ? `\n\n📧 Sent ${sent} email${sent === 1 ? '' : 's'} to selected vendors.`
          : `\n\n📧 Sent ${sent} of ${poIds.length} email${poIds.length === 1 ? '' : 's'}. ${failed} failed — resend from the Purchase Orders page.`;
      } else if (!rfqSendEmail) {
        emailNote = '\n\nEmail send was skipped. Send manually from the Purchase Orders page when ready.';
      }

      const msg = `${data.message || 'RFQ created'}\n\nRFQ Group ID: ${data.rfq_group_id || '?'}${emailNote}`;
      alert(msg);
      setShowRfqModal(false);
    } catch (err) {
      alert('Error creating RFQ: ' + err.message);
    } finally {
      setRfqSubmitting(false);
    }
  }

  // Sortable column header click handler — toggles dir when clicking the active key.
  const handleTrackingSortClick = (key) => {
    setTrackingSort(prev => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      // Default to ascending for text/numeric columns; received-date stays descending by default elsewhere
      return { key, dir: 'asc' };
    });
  };

  const trackingSortIndicator = (key) => {
    if (trackingSort.key !== key) return null;
    return <span className="ms-1 text-muted">{trackingSort.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  // Derived: filtered + sorted tracking list. Search is client-side; material type / thickness /
  // finish are client-side too (since the rows are already loaded and the user expects instant
  // dropdowns). Status + supplier remain server-side via loadTrackingMtns().
  const filteredTrackingMtns = useMemo(() => {
    // features/materials/materialData (the phone's lot lookup too)
    const filtered = filterLots(trackingMtns, { search: trackingSearch, materialType: trackingMaterialType, thicknessId: trackingThicknessId, finish: trackingFinish });

    const { key, dir } = trackingSort;
    const mult = dir === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      let av, bv;
      switch (key) {
        case 'material_type':
          av = (a.material_type || '').toLowerCase();
          bv = (b.material_type || '').toLowerCase();
          break;
        case 'thickness':
          // Sort numerically by thickness_in when available, fall back to display string
          av = a.thickness_in != null ? Number(a.thickness_in) : Number.POSITIVE_INFINITY;
          bv = b.thickness_in != null ? Number(b.thickness_in) : Number.POSITIVE_INFINITY;
          if (av === bv) {
            av = (a.display_thickness || '').toLowerCase();
            bv = (b.display_thickness || '').toLowerCase();
          }
          break;
        case 'surface_finish':
          av = (a.surface_finish || '').toLowerCase();
          bv = (b.surface_finish || '').toLowerCase();
          break;
        case 'mtn_id':
        default:
          av = a.mtn_id || 0;
          bv = b.mtn_id || 0;
          break;
      }
      if (av < bv) return -1 * mult;
      if (av > bv) return 1 * mult;
      return 0;
    };
    return [...filtered].sort(cmp);
  }, [trackingMtns, trackingSearch, trackingMaterialType, trackingThicknessId, trackingFinish, trackingSort]);

  // Derived: filtered per-material MTN list for the Material Details tab section
  const filteredMaterialMtns = useMemo(() => {
    if (materialMtnFilter === 'all') return materialMtns;
    return materialMtns.filter(m => {
      const isReconciled = !!m.reconciled_at;
      const over = m.lot_status ? m.lot_status === 'over' : mtnRemaining(m) < 0;
      const hasUsage = (m.used_qty || 0) > 0;
      const hasRemaining = mtnRemaining(m) > 0;
      if (materialMtnFilter === 'active') {
        // Same bucket the server's status=active filter and the badge use, so
        // this local filter can't drift from them.
        if (m.lot_status) return m.lot_status === 'active';
        return !isReconciled && hasUsage && hasRemaining && !over;
      }
      if (materialMtnFilter === 'flagged') {
        if (isReconciled) return false;
        if (over) return true;
        // Abandoned-style: has usage, has remaining, and a newer MTN for same material has usage
        if (hasUsage && hasRemaining) {
          return materialMtns.some(other =>
            other.mtn_id > m.mtn_id && !other.reconciled_at && (other.used_qty || 0) > 0
          );
        }
        return false;
      }
      return true;
    });
  }, [materialMtns, materialMtnFilter]);

  // --- MODIFIED: Set thicknessId from the API response ---
  function fillForm(mat) {
    setDbMaterialId(mat.material_id);
    setMaterialCode(mat.material_code || "");
    setDescription(mat.description || "");
    setMaterialType(mat.material_type || "");
    setStockQuantity(String(mat.stock_quantity || ""));
    setOriginalStockQuantity(String(mat.stock_quantity || ""));
    setUnitOfMeasurement(mat.unit_of_measurement || "");
    setReservedQuantity(String(mat.reserved_quantity || ""));
    setMinimumAvailable(String(mat.minimum_available || ""));
    setOwnerCustomerId(String(mat.owner_customer_id || ""));
    setNotes(mat.notes || "");
    
    // Set the form's category based on the material's data
    const category = mat.material_category || 'Sheet Stock';
    setFormMaterialCategory(category);
    
    if (category === 'Sheet Stock') {
        setThicknessId(mat.thickness_id || "");
        setSurfaceFinish(mat.surface_finish || "");
        setWidth(mat.width != null ? String(mat.width) : "");
        setLength(mat.length != null ? String(mat.length) : "");
        setWeight(mat.weight != null ? String(mat.weight) : "");
        // Clear shape fields
        setProfileShape(""); setProfileWidth(""); setProfileHeight(""); setWallThickness(""); setStockLength("");
        setSchedule(""); setPoundsPerFoot(""); setNps(""); setStockWeight("");
    } else { // Shape Stock
        setProfileShape(mat.profile_shape || "");
        setProfileWidth(mat.profile_width != null ? String(mat.profile_width) : "");
        setProfileHeight(mat.profile_height != null ? String(mat.profile_height) : "");
        setWallThickness(mat.wall_thickness != null ? String(mat.wall_thickness) : "");
        setStockLength(mat.stock_length != null ? String(mat.stock_length) : "");
        setSchedule(mat.schedule != null ? String(mat.schedule) : "");
        setPoundsPerFoot(mat.pounds_per_foot != null ? String(mat.pounds_per_foot) : "");
        setNps(mat.nps || "");
        setStockWeight(mat.weight != null ? String(mat.weight) : "");
        // Clear sheet fields
        setThicknessId(""); setSurfaceFinish(""); setWidth(""); setLength(""); setWeight("");
    }

    if (typeof mat.inbound_quantity === "number") {
      setInboundQuantity(mat.inbound_quantity);
    } else {
      const fromList = materials.find((x) => x.material_id === mat.material_id);
      setInboundQuantity(fromList?.inbound_quantity || 0);
    }

    loadMaterialTransactions(mat.material_id, startDate, endDate);
    setTxDelta("");
    setTxReason("");
    setTxJobId("");
    setEditingVendorId(null);
  }

  // ======================= MAIN CRUD & TRANSACTION LOGIC =======================

  // --- MODIFIED: Include thickness_id in the save payload ---
  async function handleSaveMaterial() {
    setError(null);

    // For Sheet Stock, material_code is auto-generated by the backend, so don't require it
    // For Shape Stock, material_code must be provided by the user
    if (formMaterialCategory === 'Sheet Stock') {
      if (!ownerCustomerId) {
        alert("Owner is required.");
        return;
      }
      // Validate required fields for Sheet Stock auto-generation
      if (!materialType || !thicknessId || !surfaceFinish || !width || !length || !unitOfMeasurement) {
        alert("For Sheet Stock, Material Type, Thickness, Surface Finish, Width, Length, and Unit of Measurement are all required.");
        return;
      }
    } else {
      // Shape Stock requires manual material_code
      if (!materialCode || !ownerCustomerId) {
        alert("Material Code and Owner are required.");
        return;
      }
      if (!stockWeight || parseFloat(stockWeight) <= 0) {
        alert("Stock Weight (lbs) is required for Shape Stock materials.");
        return;
      }
    }

    // --- LASER SPEC VALIDATION ---
    // Only run this check for Sheet Stock, as Shape Stock usually doesn't use the same cutting specs
    if (formMaterialCategory === 'Sheet Stock' && materialType && thicknessId) {
        try {
            const checkRes = await fetch(`${API_BASE}/laser_specs/validate?material_type=${encodeURIComponent(materialType)}&thickness_id=${thicknessId}`);
            const checkData = await checkRes.json();

            if (!checkData.exists) {
                // BLOCKING CONFIRMATION
                const proceed = window.confirm(
                    `⚠️ MISSING LASER TECH SPEC ⚠️\n\n` +
                    `There is no Laser Tech Spec defined for:\n` +
                    `Type: ${materialType}\n` +
                    `Thickness ID: ${thicknessId}\n\n` +
                    `Without this, Gas Usage and Nozzle tracking will fail for this material.\n\n` +
                    `Do you want to save anyway?`
                );
                if (!proceed) return; // Stop the save
            }
        } catch (err) {
            console.error("Failed to validate laser spec", err);
            // Optional: decide if you want to block on network error
        }
    }

    // Base payload for common fields
    // material_code and description are auto-generated by the backend for both Sheet Stock and Shape Stock
    const payload = {
      material_type: materialType || null,
      unit_of_measurement: unitOfMeasurement || null,
      minimum_available: parseInt(minimumAvailable, 10) || 0,
      owner_customer_id: parseInt(ownerCustomerId, 10) || null,
      material_category: formMaterialCategory,
      notes: notes || null,
    };

    // Conditionally add fields based on the form's category
    if (formMaterialCategory === 'Sheet Stock') {
        payload.thickness_id = thicknessId ? parseInt(thicknessId, 10) : null;
        payload.surface_finish = surfaceFinish || null;
        payload.width = width ? parseFloat(width) : null;
        payload.length = length ? parseFloat(length) : null;
    } else { // Shape Stock
        payload.profile_shape = profileShape || null;
        payload.profile_width = profileWidth ? parseFloat(profileWidth) : null;
        payload.profile_height = profileHeight ? parseFloat(profileHeight) : null;
        payload.wall_thickness = wallThickness ? parseFloat(wallThickness) : null;
        payload.stock_length = stockLength ? parseFloat(stockLength) : null;
        // New fields for Shape Stock
        payload.schedule = schedule ? parseInt(schedule, 10) : null;
        payload.pounds_per_foot = poundsPerFoot ? parseFloat(poundsPerFoot) : null;
        payload.nps = nps || null;
        payload.weight = stockWeight ? parseFloat(stockWeight) : null;
    }

    if (!dbMaterialId) {
      // A new material starts at 0 on hand (jobq-api refuses an opening stock since 2026-10-20: it
      // wrote no ledger row and no tracking number). Stock arrives on a PO receipt, a customer
      // intake, or an adjustment with a reason once the material exists.
      payload.stock_quantity = 0;
      try {
        const res = await fetch(`${API_BASE}/materials`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, "Failed to create material"));
        }
        const created = await res.json();
        alert(`New material created! ID = ${created.material_id}`);
        await loadMaterials();
        loadAllOptions(); // Refresh shortages
        const newMat = { material_id: created.material_id };
        handleSelectMaterial(newMat);
      } catch (err) {
        setError(err.message);
      }
    } else {
      const oldQty = parseInt(originalStockQuantity, 10) || 0;
      const newQty = parseInt(stockQuantity, 10) || 0;
      const qtyDiff = newQty - oldQty;

      if (qtyDiff !== 0) {
        const userPin = await pinPrompt("Enter your 4-digit PIN to adjust QoH:");
        if (!userPin) {
          alert("QoH change cancelled. PIN required.");
          return;
        }
        const txPayload = {
          delta: qtyDiff,
          reason: "Manual QoH Correction",
          entered_pin: userPin,
        };
        try {
          const txRes = await fetch(`${API_BASE}/materials/${dbMaterialId}/transactions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(txPayload),
          });
          if (!txRes.ok) {
            throw new Error(await apiErrorMessage(txRes, "Failed to adjust quantity on hand"));
          }
          const txData = await txRes.json();
          alert(`QoH adjusted via transaction. Old=${txData.old_quantity}, New=${txData.new_quantity}.`);
          setStockQuantity(String(txData.new_quantity));
          setOriginalStockQuantity(String(txData.new_quantity));
          loadMaterialTransactions(dbMaterialId, startDate, endDate);
          loadAllOptions(); // Refresh shortages
        } catch (err) {
          alert(err.message);
          return;
        }
      }

      try {
        // Never send stock here (2026-10-20): PATCH /materials ignores it, and sending the stock the
        // form LOADED undid any cut or receipt made while it was open. QoH changes go through the
        // transaction above.
        const patchPayload = { ...payload };
        const url = `${API_BASE}/materials/${dbMaterialId}`;
        const res = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchPayload),
        });
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, "Failed to save material"));
        }
        alert(`Material #${dbMaterialId} updated!`);
        await loadMaterials();
        loadAllOptions(); // Refresh shortages
        handleSelectMaterial({ material_id: dbMaterialId });
      } catch (err) {
        setError(err.message);
      }
    }
  }

  async function handleDeleteMaterial() {
    if (!dbMaterialId) {
      alert("No material selected.");
      return;
    }
    if (!window.confirm("Really delete this material?")) return;
    try {
      const res = await fetch(`${API_BASE}/materials/${dbMaterialId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to delete material"));
      }
      alert("Material deleted.");
      loadMaterials();
      loadAllOptions(); // Refresh shortages
      handleCreateNew();
    } catch (err) {
      setError(err.message);
    }
  }

  // ======================= NEW: DUPLICATE MATERIAL LOGIC =======================
  async function handleDuplicateMaterial() {
    if (!dbMaterialId) {
        alert("Please select a material to duplicate.");
        return;
    }
    if (!window.confirm("Are you sure you want to duplicate this material? A new material entry will be created with a '-COPY' suffix.")) {
        return;
    }

    setLoading(true);
    setError(null);

    try {
        const res = await fetch(`${API_BASE}/materials/${dbMaterialId}/duplicate`, {
            method: "POST",
        });

        if (!res.ok) {
            const errorData = await res.json();
            throw new Error(detailToString(errorData.detail) || "Failed to duplicate material.");
        }

        const newMaterial = await res.json();
        alert(`Material duplicated successfully! New Material ID: ${newMaterial.material_id}`);
        
        // Refresh the material list and then select the new one
        const updatedMaterials = await loadMaterials();
        if (updatedMaterials) {
            const newlyCreated = updatedMaterials.find(m => m.material_id === newMaterial.material_id);
            if (newlyCreated) {
                handleSelectMaterial(newlyCreated);
            }
        }

    } catch (err) {
        setError(err.message);
        alert("Error duplicating material: " + err.message);
    } finally {
        setLoading(false);
    }
  }

  // ======================= NEW: DROP MATERIAL LABEL HANDLER =======================
  async function handleGenerateDropLabel() {
    if (!dropMaterialType || !dropThicknessId || !dropSurfaceFinish || !dropOwnerId || !dropLength || !dropWidth) {
      alert("Please fill out all fields for the drop material.");
      return;
    }

    setDropLoading(true);
    setError(null);

    const payload = {
      material_type: dropMaterialType,
      thickness_id: parseInt(dropThicknessId, 10),
      surface_finish: dropSurfaceFinish,
      owner_customer_id: parseInt(dropOwnerId, 10),
      length: parseFloat(dropLength),
      width: parseFloat(dropWidth),
    };

    try {
      const res = await fetch(`${API_BASE}/materials/drop/label`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(detailToString(errorData.detail) || "Failed to generate drop label.");
      }

      const pdfBlob = await res.blob();
      openPdfInPrintDialog(pdfBlob);

    } catch (err) {
      setError("Failed to generate label: " + err.message);
      alert("Failed to generate label: " + err.message);
    } finally {
      setDropLoading(false);
    }
  }


  // ======================= REMNANT INVENTORY HANDLERS =======================
  async function loadRemnants() {
    setRemnantsLoading(true);
    try {
      // Load all remnants — filtering happens client-side via `filteredRemnants` memo.
      const params = new URLSearchParams({ include_empty: 'false' });
      const res = await fetch(`${API_BASE}/materials/remnants?${params}`);
      if (!res.ok) throw new Error(`Failed to load remnants: ${res.status}`);
      setRemnants(await res.json());
    } catch (err) {
      setError("Failed to load remnants: " + err.message);
    } finally {
      setRemnantsLoading(false);
    }
  }

  async function handleCreateRemnant() {
    if (!remnantSourceMaterialId || !remnantWidth || !remnantLength) {
      alert("Please select a source material and enter width and length.");
      return;
    }
    // Grain (2026-10-25): a remnant keeps its sheet's grain, so it must fit the sheet the same
    // way round. The API asks too (409 grain_check); this asks before the round trip.
    let orientationConfirmed = false;
    if (newRemnantGrain) {
      if (!window.confirm(`${newRemnantGrain.message}\n\nOK = save the sizes as entered. Cancel = go back and fix them.`)) return;
      orientationConfirmed = true;
    }
    setRemnantCreating(true);
    setError(null);
    try {
      const payload = {
        source_material_id: parseInt(remnantSourceMaterialId, 10),
        width: parseFloat(remnantWidth),
        length: parseFloat(remnantLength),
        stock_quantity: parseInt(remnantQty, 10) || 1,
        notes: remnantNotes || null,
        mtn_id: remnantMtnId ? parseInt(remnantMtnId, 10) : null,
        orientation_confirmed: orientationConfirmed,
      };
      let res = await fetch(`${API_BASE}/materials/remnant`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.status === 409) {
        const errData = await res.json().catch(() => ({}));
        const d = errData && errData.detail;
        if (d && d.error === 'grain_check') {
          if (!window.confirm(`${d.message}\n\nOK = save the sizes as entered. Cancel = go back and fix them.`)) return;
          res = await fetch(`${API_BASE}/materials/remnant`, {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, orientation_confirmed: true })
          });
        } else {
          throw new Error(detailToString(d) || "Failed to create remnant.");
        }
      }
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(detailToString(errData.detail) || "Failed to create remnant.");
      }
      // Response is a PDF label — open print dialog
      const pdfBlob = await res.blob();
      openPdfInPrintDialog(pdfBlob);

      // Reset dimensions/qty/notes but KEEP sticky filters (owner/type/thickness/finish)
      // and the source material — this speeds up entering multiple remnants of the same type.
      setRemnantWidth("");
      setRemnantLength("");
      setRemnantQty("1");
      setRemnantMtnId("");
      setRemnantNotes("");
      loadRemnants();
    } catch (err) {
      setError("Failed to create remnant: " + err.message);
      alert("Failed to create remnant: " + err.message);
    } finally {
      setRemnantCreating(false);
    }
  }

  async function handlePrintRemnantLabel(materialId) {
    try {
      const res = await fetch(`${API_BASE}/materials/labels/batch`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ material_id: materialId }] })
      });
      if (!res.ok) throw new Error("Failed to generate label.");
      const pdfBlob = await res.blob();
      openPdfInPrintDialog(pdfBlob);
    } catch (err) {
      alert("Failed to print label: " + err.message);
    }
  }

  function openEditRemnant(r) {
    setEditingRemnant(r);
    setEditRemnantWidth(r.width != null ? String(r.width) : "");
    setEditRemnantLength(r.length != null ? String(r.length) : "");
    setEditRemnantQty(String(r.stock_quantity ?? 1));
    setEditRemnantNotes(r.notes || "");
  }

  async function handleSaveEditRemnant() {
    if (!editingRemnant) return;
    const w = parseFloat(editRemnantWidth);
    const l = parseFloat(editRemnantLength);
    const q = parseInt(editRemnantQty, 10);
    if (!w || w <= 0 || !l || l <= 0) {
      alert("Width and length must be positive numbers.");
      return;
    }
    if (Number.isNaN(q) || q < 0) {
      alert("Quantity must be 0 or greater.");
      return;
    }
    if (editRemnantGrain
        && !window.confirm(`${editRemnantGrain.message}\n\nOK = save the sizes as entered. Cancel = go back and fix them.`)) {
      return;
    }
    const qtyDelta = q - (parseInt(editingRemnant.stock_quantity, 10) || 0);
    let qtyPin = null;
    if (qtyDelta !== 0) {
      // A count change writes a ledger row (PATCH /materials no longer sets stock, 2026-10-20).
      qtyPin = await pinPrompt("Enter your PIN to change this remnant's count:");
      if (!qtyPin) {
        alert("Count change cancelled. PIN required.");
        return;
      }
    }
    setEditRemnantSubmitting(true);
    try {
      if (qtyDelta !== 0) {
        // Send the COUNT, not a difference: jobq-api works it out against the stock under its row
        // lock, so a list loaded earlier or a second press of Save can't apply it twice (2026-10-20).
        const txRes = await fetch(`${API_BASE}/materials/${editingRemnant.material_id}/transactions`, {
          method: "POST",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ counted_quantity: q, reason: "Remnant count correction", entered_pin: qtyPin }),
        });
        if (!txRes.ok) {
          throw new Error(await apiErrorMessage(txRes, "Failed to change the remnant's count"));
        }
        const txData = await txRes.json().catch(() => ({}));
        if (txData && txData.new_quantity != null) {
          setEditingRemnant({ ...editingRemnant, stock_quantity: txData.new_quantity });
        }
      }
      const payload = {
        width: w,
        length: l,
        notes: editRemnantNotes || null,
      };
      const res = await fetch(`${API_BASE}/materials/${editingRemnant.material_id}`, {
        method: "PATCH",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || `HTTP ${res.status}`);
      }
      setEditingRemnant(null);
      loadRemnants();
    } catch (err) {
      alert("Failed to update remnant: " + err.message);
    } finally {
      setEditRemnantSubmitting(false);
    }
  }

  async function handleConfirmDeleteRemnant() {
    if (!deletingRemnant) return;
    setDeleteRemnantSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/materials/${deletingRemnant.material_id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || `HTTP ${res.status}`);
      }
      setDeletingRemnant(null);
      loadRemnants();
    } catch (err) {
      alert("Failed to delete remnant: " + err.message);
    } finally {
      setDeleteRemnantSubmitting(false);
    }
  }

  // ======================= REMNANT BULK RETURN-TO-VENDOR =======================
  function toggleRemnantSelected(materialId) {
    setSelectedRemnantIds(prev =>
      prev.includes(materialId)
        ? prev.filter(x => x !== materialId)
        : [...prev, materialId]
    );
  }

  function toggleAllVisibleRemnants() {
    const visibleIds = filteredRemnants.map(r => r.material_id);
    if (visibleIds.length === 0) return;
    const allSelected = visibleIds.every(id => selectedRemnantIds.includes(id));
    if (allSelected) {
      setSelectedRemnantIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedRemnantIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  }

  async function handleSubmitReturnToVendor() {
    const selectedRows = remnants.filter(r => selectedRemnantIds.includes(r.material_id));
    if (selectedRows.length === 0) {
      alert("Select at least one remnant to return.");
      return;
    }
    const vendors = Array.from(new Set(selectedRows.map(r => r.owner_customer_id)));
    if (vendors.length !== 1) {
      alert(`All selected remnants must be owned by the same vendor (${vendors.length} distinct owners selected).`);
      return;
    }
    if (!returnPin) {
      alert("Please enter your PIN to authorize the return.");
      return;
    }
    setReturnSubmitting(true);
    try {
      const body = {
        vendor_customer_id: vendors[0],
        return_date: returnDate,
        notes: returnNotes || null,
        pin: returnPin,
        material_ids: selectedRemnantIds,
      };
      const res = await fetch(`${API_BASE}/vendor_returns`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // Open the receipt PDF in a print dialog
      try {
        const pdfRes = await fetch(`${API_BASE}${data.pdf_url}`);
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          openPdfInPrintDialog(blob);
        }
      } catch (_e) {
        // PDF open failure is non-fatal — the return is already recorded.
      }
      const idStr = String(data.return_id).padStart(4, '0');
      const wt = data.total_weight_lb != null ? data.total_weight_lb.toFixed(1) : "?";
      alert(`Return VR-${idStr} recorded: ${data.items_count} item(s), ${wt} lb total.`);
      setSelectedRemnantIds([]);
      setReturnNotes("");
      setReturnPin("");
      setShowReturnModal(false);
      loadRemnants();
    } catch (err) {
      setError("Failed to record return: " + err.message);
      alert("Failed to record return: " + err.message);
    } finally {
      setReturnSubmitting(false);
    }
  }

  // Auto-derive source material info for the remnant creation form
  const remnantSourceMaterial = materials.find(m => m.material_id === parseInt(remnantSourceMaterialId, 10));
  // Grain: JobQ length = Amada X = with the grain (utils/sheetGrain.js).
  const newRemnantGrain = remnantSourceMaterial
    ? grainCheck(remnantWidth, remnantLength, remnantSourceMaterial.width, remnantSourceMaterial.length)
    : null;
  const editRemnantGrain = editingRemnant
    ? grainCheck(editRemnantWidth, editRemnantLength, editingRemnant.source_width, editingRemnant.source_length)
    : null;

  // --- Remnant inventory client-side filter (Owner/Type/Thickness/Finish/Search) ---
  const filteredRemnants = useMemo(() => {
    const term = (remnantSearch || "").toLowerCase();
    return remnants.filter(r => {
      if (remnantOwnerFilter && parseInt(remnantOwnerFilter, 10) !== r.owner_customer_id) return false;
      if (remnantTypeFilter && r.material_type !== remnantTypeFilter) return false;
      if (remnantThicknessFilter && r.thickness_id !== parseInt(remnantThicknessFilter, 10)) return false;
      if (remnantFinishFilter && r.surface_finish !== remnantFinishFilter) return false;
      if (term) {
        const blob = `${r.material_code || ''} ${r.description || ''} ${r.notes || ''}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [remnants, remnantOwnerFilter, remnantTypeFilter, remnantThicknessFilter, remnantFinishFilter, remnantSearch]);

  // --- Available source materials for "Record New Remnant" (filtered by sticky Owner/Type/Thickness/Finish) ---
  const newRemnantSourceCandidates = useMemo(() => {
    return materials.filter(m => {
      if (m.material_category !== 'Sheet Stock') return false;
      if (m.material_code?.startsWith('REM-')) return false;
      if (newRemnantOwnerFilter && parseInt(newRemnantOwnerFilter, 10) !== m.owner_customer_id) return false;
      if (newRemnantTypeFilter && m.material_type !== newRemnantTypeFilter) return false;
      if (newRemnantThicknessFilter && m.thickness_id !== parseInt(newRemnantThicknessFilter, 10)) return false;
      if (newRemnantFinishFilter && m.surface_finish !== newRemnantFinishFilter) return false;
      return true;
    });
  }, [materials, newRemnantOwnerFilter, newRemnantTypeFilter, newRemnantThicknessFilter, newRemnantFinishFilter]);

  // Thickness options scoped to the selected material type (mirrors thicknessFilterOptions for the main list)
  const remnantThicknessFilterOptions = useMemo(() => {
    if (!remnantTypeFilter) return thicknessOptions;
    return thicknessOptions.filter(opt => opt.material_type === remnantTypeFilter);
  }, [remnantTypeFilter, thicknessOptions]);

  const newRemnantThicknessFilterOptions = useMemo(() => {
    if (!newRemnantTypeFilter) return thicknessOptions;
    return thicknessOptions.filter(opt => opt.material_type === newRemnantTypeFilter);
  }, [newRemnantTypeFilter, thicknessOptions]);

  async function handleAddTransaction() {
    // features/materials/materialActions (the phone's too): the checks, the PIN, POST, the alert.
    const r = await addMaterialTransaction({ materialId: dbMaterialId, delta: txDelta, reason: txReason, jobId: txJobId, ui: desktopUi });
    if (!r.done) return;
    setStockQuantity(String(r.newQuantity));
    setOriginalStockQuantity(String(r.newQuantity));
    loadMaterialTransactions(dbMaterialId, startDate, endDate);
    loadAllOptions(); // Refresh shortages
    setTxDelta("");
    setTxReason("");
    setTxJobId("");
  }

  async function handleDefectMaterial() {
    // features/materials/materialActions (the phone's too): how many, why, the PIN, POST /defects, the
    // sheet label printed here.
    const r = await recordMaterialDefect({ materialId: dbMaterialId, ui: desktopUi, printPdf: openPdfInPrintDialog });
    if (!r.done) return;
    loadMaterials();
    loadAllOptions(); // Refresh shortages
  }

  async function handlePrintLabel() {
    if (!dbMaterialId) {
      alert("No material selected.");
      return;
    }
    try {
      const labelRes = await fetch(`${API_BASE}/materials/${dbMaterialId}/label`);
      if (!labelRes.ok) {
        // The label endpoint now requires ?mtn_id= when the material has any
        // MTN rows. Surface the structured 400 by pointing the operator at
        // the per-MTN Print buttons in the Material Tracking Numbers table.
        // Read the body ONCE. The previous version called .json() and then
        // .text() in the catch — but the body is already consumed by then, so
        // the fallback threw "body stream already read" and buried the real
        // error instead of reporting it.
        const rawBody = await labelRes.text().catch(() => "");
        let detail = null;
        try {
          detail = JSON.parse(rawBody).detail;
        } catch {
          detail = null;
        }
        if (detail && typeof detail === 'object' && detail.error === 'mtn_id_required') {
          const count = (detail.available_mtns || []).length;
          alert(
            `This material has ${count} tracked MTN${count === 1 ? '' : 's'}. ` +
            `Use the Print button in the Material Tracking Numbers section below ` +
            `to print a label bound to a specific heat / PO.`
          );
          return;
        }
        throw new Error(parseApiError(rawBody, "Failed to fetch the label PDF", labelRes.status));
      }
      const pdfBlob = await labelRes.blob();
      openPdfInPrintDialog(pdfBlob);
    } catch (err) {
      alert("Error printing label: " + err.message);
    }
  }

  async function handlePrintMtnLabel(materialId, mtnId) {
    if (!materialId || !mtnId) {
      alert("Missing material or MTN id.");
      return;
    }
    try {
      const labelRes = await fetch(`${API_BASE}/materials/${materialId}/label?mtn_id=${mtnId}`);
      if (!labelRes.ok) {
        throw new Error(await apiErrorMessage(labelRes, "Failed to fetch the label PDF"));
      }
      const pdfBlob = await labelRes.blob();
      openPdfInPrintDialog(pdfBlob);
    } catch (err) {
      alert("Error printing MTN label: " + err.message);
    }
  }

  // ======================= CWT MANAGEMENT LOGIC =======================

  const handleToggleCwtSelection = (ruleId) => {
    setSelectedCwtRuleIds(prev =>
      prev.includes(ruleId)
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const handleToggleAllCwtSelection = () => {
    if (selectedCwtRuleIds.length === filteredCwtRules.length) {
      setSelectedCwtRuleIds([]);
    } else {
      setSelectedCwtRuleIds(filteredCwtRules.map(rule => rule.cwt_price_id));
    }
  };

  const handleBulkCwtUpdate = async () => {
      const price = parseFloat(bulkCwtPrice);
      if (isNaN(price) || price <= 0) {
          alert("Please enter a valid, positive price for the bulk update.");
          return;
      }
      if (selectedCwtRuleIds.length === 0) {
          alert("No CWT rules selected for update.");
          return;
      }

      if (!window.confirm(`Are you sure you want to update ${selectedCwtRuleIds.length} CWT rule(s) to $${price.toFixed(2)}? This will trigger a cost recalculation for all associated materials.`)) {
          return;
      }

      setIsBulkUpdating(true);
      try {
          const updatePromises = selectedCwtRuleIds.map(id =>
              fetch(`${API_BASE}/pricing/cwt/${id}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ cwt_price: price })
              }).then(res => {
                  if (!res.ok) throw new Error(`Failed to update rule #${id}`);
                  return res.json();
              })
          );
          
          await Promise.all(updatePromises);

          const recalcPromises = selectedCwtRuleIds.map(id => 
              fetch(`${API_BASE}/pricing/cwt/recalculate`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ cwt_price_id: id })
              })
          );

          await Promise.all(recalcPromises);

          alert(`Successfully updated ${selectedCwtRuleIds.length} CWT rule(s) and triggered recalculations.`);
          
          setSelectedCwtRuleIds([]);
          setBulkCwtPrice("");
          await loadAllOptions(); // Reloads CWT rules
          await loadMaterials();
          if (selectedMaterial) {
              handleSelectMaterial(selectedMaterial);
          }

      } catch (err) {
          alert('An error occurred during the bulk update: ' + err.message);
      } finally {
          setIsBulkUpdating(false);
      }
  };

  const handleUpdateCwtRule = async () => {
      if (!editingCwtRule) return;
      try {
          const res = await fetch(`${API_BASE}/pricing/cwt/${editingCwtRule.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ cwt_price: parseFloat(editingCwtRule.price) })
          });
          if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to update the CWT rule"));
          
          await fetch(`${API_BASE}/pricing/cwt/recalculate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ cwt_price_id: editingCwtRule.id })
          });

          alert('CWT price updated and material costs recalculated!');
          setEditingCwtRule(null);
          await loadAllOptions(); // Reloads CWT rules
          await loadMaterials();
          if (selectedMaterial) {
              handleSelectMaterial(selectedMaterial);
          }
      } catch (err) {
          alert('Error updating CWT rule: ' + err.message);
      }
  };

  const handleAddCwtRule = async () => {
      if (!newCwtRule.material_type || !newCwtRule.surface_finish || !newCwtRule.thickness_id) {
          alert("Please select material type, finish, and thickness.");
          return;
      }

      const payload = {
          material_type: newCwtRule.material_type,
          surface_finish: newCwtRule.surface_finish,
          thickness_id: parseInt(newCwtRule.thickness_id),
          cwt_price: parseFloat(newCwtRule.cwt_price) || 0,
          vendor_id: newCwtRule.vendor_id ? parseInt(newCwtRule.vendor_id) : null
      };

      try {
          const res = await fetch(`${API_BASE}/pricing/cwt`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });
          if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to add the CWT rule"));
          alert('New CWT rule added!');
          setNewCwtRule({ 
              material_type: "", 
              surface_finish: "", 
              thickness_id: "", 
              cwt_price: "", 
              vendor_id: "",
              // Also reset these fields
              sheet_cost: "",
              sheet_width: "",
              sheet_length: ""
          });
          loadAllOptions(); // Reloads CWT rules

      } catch (err) {
          alert('Error adding CWT rule: ' + err.message);
      }
  };

  const handleDeleteCwtRule = async (ruleId) => {
      if (!window.confirm("Are you sure you want to delete this CWT rule? This cannot be undone.")) return;
      try {
          const res = await fetch(`${API_BASE}/pricing/cwt/${ruleId}`, {
              method: 'DELETE'
          });
          if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to delete the CWT rule"));
          alert('CWT rule deleted.');
          loadAllOptions(); // Reloads CWT rules
      } catch (err) {
          alert('Error deleting CWT rule: ' + err.message);
      }
  };

  const handleAddThickness = async () => {
      if (!newThickness.material_type || !newThickness.display_thickness || !newThickness.thickness_in) {
        alert("Material Type, Display Thickness, and Thickness (in) are required.");
        return;
      }
      try {
        const payload = {
          material_type: newThickness.material_type,
          display_thickness: newThickness.display_thickness,
          thickness_in: parseFloat(newThickness.thickness_in)
        };
        const res = await fetch(`${API_BASE}/material_thicknesses`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to add the thickness"));
        alert("Thickness added successfully.");
        setNewThickness({ display_thickness: "", thickness_in: "", material_type: "" });
        loadAllOptions(); // Refresh the list
      } catch (err) {
        alert("Error adding thickness: " + err.message);
      }
  };

  const handleUpdateThickness = async () => {
      if (!editingThickness) return;
      try {
        const payload = {
          display_thickness: editingThickness.display_thickness,
          thickness_in: parseFloat(editingThickness.thickness_in),
          material_type: editingThickness.material_type
        };
        const res = await fetch(`${API_BASE}/material_thicknesses/${editingThickness.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to update the thickness"));
        alert("Thickness updated successfully.");
        setEditingThickness(null);
        loadAllOptions(); // Refresh the list
      } catch (err) {
        alert("Error updating thickness: " + err.message);
      }
  };

  const handleDeleteThickness = async (thicknessId) => {
      if (!window.confirm("Are you sure you want to delete this thickness entry? This may affect existing materials.")) return;
      try {
        const res = await fetch(`${API_BASE}/material_thicknesses/${thicknessId}`, {
          method: "DELETE"
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to delete the thickness"));
        alert("Thickness deleted successfully.");
        loadAllOptions(); // Refresh the list
      } catch (err) {
        alert("Error deleting thickness: " + err.message);
      }
  };  

  // ======================= STOCK TAKE LOGIC =======================
  const stockTakeFilteredMaterials = materials.filter((m) => {
    if (stockTakeFilterType && m.material_type !== stockTakeFilterType) return false;
    if (stockTakeFilterOwner && m.owner_customer_id !== parseInt(stockTakeFilterOwner, 10)) return false;
    if (stockTakeFilterThickness && m.thickness_id !== parseInt(stockTakeFilterThickness, 10)) return false;
    if (stockTakeFilterFinish && m.surface_finish !== stockTakeFilterFinish) return false;
    return true;
  });

  async function handleCommitStockTake() {
    const changes = [];
    for (let m of stockTakeFilteredMaterials) {
      const oldQty = m.stock_quantity || 0;
      const inputVal = counts[m.material_id];
      if (!inputVal) continue;
      const newQty = parseInt(inputVal, 10);
      if (isNaN(newQty)) continue;
      if (newQty !== oldQty) {
        changes.push({ material_id: m.material_id, oldQty, newQty });
      }
    }

    if (!changes.length) {
      alert("No changes to commit.");
      return;
    }

    if (!window.confirm(`You are about to commit stock changes for ${changes.length} material(s). Proceed?`)) {
      return;
    }

    const userPin = await pinPrompt("Enter your 4-digit PIN:");
    if (!userPin) return;

    try {
      for (let c of changes) {
        const delta = c.newQty - c.oldQty;
        const res = await fetch(`${API_BASE}/materials/${c.material_id}/transactions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            delta,
            reason: "Stock Take",
            entered_pin: userPin,
          }),
        });
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, `Failed updating material #${c.material_id}`));
        }
      }
      alert("Stock Take committed successfully!");
      loadMaterials();
      loadAllOptions(); // Refresh shortages
      setCounts({});
    } catch (err) {
      alert("Error committing Stock Take: " + err.message);
    }
  }

  // ======================= SHORTAGES -> PURCHASE ORDER LOGIC =======================
  function handleToggleShortage(id, checked) {
    if (checked) {
      setSelectedShortageIds((prev) => [...prev, id]);
    } else {
      setSelectedShortageIds((prev) => prev.filter((x) => x !== id));
    }
  }

  async function handleGeneratePOFromShortages() {
    const selectedShortages = materialShortages.filter((m) =>
      selectedShortageIds.includes(m.material_id)
    );
    if (!selectedShortages.length) {
      alert("No items selected!");
      return;
    }

    const vendorGroups = {};

    for (let mat of selectedShortages) {
      const shortfall = mat.minimum_available - mat.total_avail;
      const quantityNeeded = Math.max(shortfall, 0);
      if (quantityNeeded === 0) continue;

      try {
        const r = await fetch(`${API_BASE}/materials/${mat.material_id}/vendors`);
        if (!r.ok) {
          throw new Error(await apiErrorMessage(r, `Failed to fetch vendors for material #${mat.material_id}`));
        }
        const vendorArr = await r.json();
        if (!vendorArr.length) {
          console.warn(`Material #${mat.material_id} has no vendors, skipping.`);
          continue;
        }
        const preferredVendorId = vendorArr[0].vendor_id;
        if (!vendorGroups[preferredVendorId]) {
          vendorGroups[preferredVendorId] = [];
        }
        vendorGroups[preferredVendorId].push({
          item_type: "Material",
          material_id: mat.material_id,
          quantity_ordered: quantityNeeded,
        });
      } catch (err) {
        alert(err.message);
        return;
      }
    }

    const createdPOs = [];
    const todayStr = shopToday();

    for (const [vendorId, lines] of Object.entries(vendorGroups)) {
      if (!lines.length) continue;
      let vendorName = "";
      try {
        const vr = await fetch(`${API_BASE}/vendors/${vendorId}`);
        if (vr.ok) {
          const vd = await vr.json();
          vendorName = vd.name;
        }
      } catch (err) {
        console.error("Could not fetch vendor name:", err);
      }

      const payload = {
        supplier: vendorName,
        order_date: todayStr,
        estimated_receive_date: null,
        notes: "Auto-created from shortages",
        lines,
      };

      try {
        const res = await fetch(`${API_BASE}/purchase_orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, `Failed to create PO for vendor #${vendorId}`));
        }
        const data = await res.json();
        createdPOs.push(data);
      } catch (err) {
        alert(err.message);
        return;
      }
    }

    if (!Object.keys(vendorGroups).length) {
      alert("No valid vendor lines to create POs for.");
      return;
    }

    alert(`Successfully created ${createdPOs.length} PO(s)!\n` +
        createdPOs.map((p) => `PO# ${p.po_number}`).join(", "));
    loadAllOptions(); // Refresh shortages
  }

  // ======================= MULTI-SUPPLIER TABLE LOGIC =======================
  async function handleAddMaterialVendor() {
    if (!dbMaterialId) {
      alert("No material selected.");
      return;
    }
    if (!newVendorId) {
      alert("Select a vendor.");
      return;
    }
    const payload = {
      vendor_id: parseInt(newVendorId, 10),
      cost_per_unit: parseFloat(newVendorCost) || 0.0,
      lead_time_days: newVendorLeadTime ? parseInt(newVendorLeadTime, 10) : null,
    };
    try {
      const r = await fetch(`${API_BASE}/materials/${dbMaterialId}/vendors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r, "Failed to add the supplier"));
      }
      alert("Supplier added!");
      setNewVendorId("");
      setNewVendorCost("");
      setNewVendorLeadTime("");
      loadMaterialVendors(dbMaterialId);
    } catch (err) {
      alert("Failed to add supplier: " + err.message);
    }
  }

  async function handleRemoveMaterialVendor(mvId) {
    if (!window.confirm("Remove this supplier from the material?")) return;
    try {
      const r = await fetch(`${API_BASE}/materials/${dbMaterialId}/vendors/${mvId}`, {
        method: "DELETE",
      });
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r, "Failed to remove the supplier"));
      }
      alert("Supplier association removed.");
      loadMaterialVendors(dbMaterialId);
    } catch (err) {
      alert("Failed to remove association: " + err.message);
    }
  }

  function handleEditVendorRow(mv) {
    setEditingVendorId(mv.material_vendor_id);
    setEditCost(String(mv.cost_per_unit));
    setEditLeadTime(mv.lead_time_days ? String(mv.lead_time_days) : "");
  }

  function handleCancelEdit() {
    setEditingVendorId(null);
    setEditCost("");
    setEditLeadTime("");
  }

  async function handleUpdateMaterialVendor(mvId) {
    if (!dbMaterialId) return;
    const payload = {
      cost_per_unit: parseFloat(editCost) || 0,
      lead_time_days: editLeadTime ? parseInt(editLeadTime, 10) : null,
    };
    try {
      const r = await fetch(`${API_BASE}/materials/${dbMaterialId}/vendors/${mvId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r, "Failed to update the supplier"));
      }
      alert("Supplier updated!");
      setEditingVendorId(null);
      setEditCost("");
      setEditLeadTime("");
      loadMaterialVendors(dbMaterialId);
    } catch (err) {
      alert("Failed to update supplier: " + err.message);
    }
  }

  async function handleSetPreferred(mvId) {
    if (!dbMaterialId) return;

    if (!window.confirm("Are you sure you want to change the preferred vendor?")) return;

    try {
      const res = await fetch(`${API_BASE}/materials/${dbMaterialId}/vendors/${mvId}/set-preferred`, {
        method: "POST",
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(detailToString(errorData.detail) || "Failed to set preferred vendor.");
      }

      alert("Preferred vendor has been updated.");
      // Reload the vendors for this material to reflect the change
      await loadMaterialVendors(dbMaterialId);

    } catch (err) {
      alert("Error: " + err.message);
    }
  }

  async function handleRecalculateVendorCostFromCwt(mv) {
    if (!dbMaterialId) return;
    try {
      const res = await fetch(
        `${API_BASE}/materials/${dbMaterialId}/vendors/${mv.material_vendor_id}/calculate-cost-from-cwt`,
        { method: "POST" }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
      }
      const newCost = Number(body.new_cost_per_unit ?? 0).toFixed(2);
      const cwtPrice = Number(body.cwt_price ?? 0).toFixed(2);
      const scope = body.cwt_rule_scope === "vendor_specific"
        ? `${body.vendor_name}-specific`
        : "default";
      alert(
        `${body.vendor_name} cost recalculated to $${newCost}\n` +
        `(${scope} CWT rule: $${cwtPrice}/cwt × ${Number(body.weight).toFixed(2)} lb)`
      );
      await loadMaterialVendors(dbMaterialId);
    } catch (err) {
      alert("Failed to recalculate cost: " + err.message);
    }
  }

  // ======= NEW: CUSTOMER MATERIAL INTAKE HANDLERS ========
  
  /**
   * Handles selection of a customer from the intake dropdown.
   * Filters the main material list to find materials owned by that customer.
   */
  function handleSelectIntakeCustomer(e) {
    const customerId = e.target.value;
    setIntakeCustomerId(customerId);
    if (customerId) {
        const ownedMaterials = materials.filter(m => m.owner_customer_id === parseInt(customerId, 10));
        setIntakeMaterials(ownedMaterials);
        loadIntakeHistory(customerId);
    } else {
        setIntakeMaterials([]);
        setIntakeHistory([]);
    }
    setIntakeCounts({}); // Reset counts whenever the customer changes
    setIntakeHeatNumbers({});
    setIntakeFilterType("");
    setIntakeFilterThickness("");
    setIntakeFilterFinish("");
    setIntakeShipmentNumber("");
    setIntakeManifestFile(null);
    // Reset return-tab state too — same customer scope
    setCmrSelected({});
    setCmrQuantities({});
    setCmrNotes("");
    setCmrPin("");
    setCmrFilterType("");
    setCmrFilterThickness("");
    setCmrFilterFinish("");
    if (customerId) {
      loadReturnHistory(customerId);
    } else {
      setCmrHistory([]);
    }
  }

  async function loadReturnHistory(customerId) {
    if (!customerId) {
      setCmrHistory([]);
      return;
    }
    setCmrHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vendor_returns?vendor_customer_id=${customerId}&limit=200`);
      if (!res.ok) throw new Error(`Failed to load returns: HTTP ${res.status}`);
      const data = await res.json();
      setCmrHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadReturnHistory:", err);
      setCmrHistory([]);
    } finally {
      setCmrHistoryLoading(false);
    }
  }

  async function handleReprintReturnManifest(returnId) {
    try {
      const res = await fetch(`${API_BASE}/vendor_returns/${returnId}/pdf`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      openPdfInPrintDialog(blob);
    } catch (err) {
      alert(`Failed to reprint manifest: ${err.message}`);
    }
  }

  async function handleSubmitCustomerMaterialReturn() {
    const selectedIds = Object.keys(cmrSelected).filter(id => cmrSelected[id]).map(Number);
    if (!intakeCustomerId) {
      alert("Select a customer first.");
      return;
    }
    if (selectedIds.length === 0) {
      alert("Select at least one material to return.");
      return;
    }
    if (!cmrPin) {
      alert("Please enter your PIN to authorize the return.");
      return;
    }
    setCmrSubmitting(true);
    try {
      const items = selectedIds.map(mid => {
        const item = { material_id: mid };
        const raw = cmrQuantities[mid];
        if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
          const parsed = parseInt(raw, 10);
          if (!isNaN(parsed) && parsed > 0) item.quantity = parsed;
        }
        return item;
      });
      const body = {
        customer_id: parseInt(intakeCustomerId, 10),
        return_date: cmrReturnDate,
        notes: cmrNotes || null,
        pin: cmrPin,
        items,
      };
      const res = await fetch(`${API_BASE}/customer_material_returns`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // Open the receipt PDF in a print dialog (non-fatal on failure)
      try {
        const pdfRes = await fetch(`${API_BASE}${data.pdf_url}`);
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          openPdfInPrintDialog(blob);
        }
      } catch (_e) {
        // PDF open failure is non-fatal — the return is already recorded.
      }
      const idStr = String(data.return_id).padStart(4, '0');
      const wt = data.total_weight_lb != null ? data.total_weight_lb.toFixed(1) : "?";
      const skippedNote = data.skipped && data.skipped.length > 0
        ? `\nSkipped: ${data.skipped.map(s => `${s.material_code} (${s.reason})`).join('; ')}`
        : '';
      alert(`Return VR-${idStr} recorded: ${data.items_count} item(s), ${wt} lb total.${skippedNote}`);
      // Reset form
      setCmrSelected({});
      setCmrQuantities({});
      setCmrNotes("");
      setCmrPin("");
      // Refresh stock so the table reflects new availability
      const fresh = await loadMaterials();
      if (fresh && intakeCustomerId) {
        const owned = fresh.filter(m => m.owner_customer_id === parseInt(intakeCustomerId, 10));
        setIntakeMaterials(owned);
      }
      // Refresh history so the new return appears at the top
      loadReturnHistory(intakeCustomerId);
    } catch (err) {
      setError("Failed to record return: " + err.message);
      alert("Failed to record return: " + err.message);
    } finally {
      setCmrSubmitting(false);
    }
  }

  async function handleDownloadIntakeManifest(intakeId, filename) {
    try {
      const res = await fetch(`${API_BASE}/materials/customer_intake/${intakeId}/manifest`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || "Failed to fetch manifest");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `Intake_${intakeId}_Manifest.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Error downloading manifest: " + err.message);
    }
  }

  async function handleUploadIntakeManifestForRow(intakeId, file) {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      alert("Manifest must be a PDF file.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/materials/customer_intake/${intakeId}/manifest`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || "Upload failed");
      }
      if (intakeCustomerId) await loadIntakeHistory(intakeCustomerId);
    } catch (err) {
      alert("Error uploading manifest: " + err.message);
    }
  }

  async function loadIntakeHistory(customerId) {
    setIntakeHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/customer_material_intakes?customer_id=${customerId}`);
      if (res.ok) {
        setIntakeHistory(await res.json());
      }
    } catch (err) {
      console.warn("Failed to load intake history:", err);
    } finally {
      setIntakeHistoryLoading(false);
    }
  }

  async function handleDownloadIntakeReport(intakeId) {
    try {
      const res = await fetch(`${API_BASE}/materials/customer_intake/${intakeId}/report`);
      if (!res.ok) throw new Error("Failed to generate report");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Intake_${intakeId}_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Error downloading report: " + err.message);
    }
  }

  async function handleDownloadIntakeLabels(materialIds, trackingNumbers) {
    try {
      // If tracking numbers provided, use items format for richer labels
      let body;
      if (trackingNumbers && trackingNumbers.length > 0) {
        const items = trackingNumbers.map(t => ({ material_id: t.material_id, mtn_id: t.mtn_id }));
        // Add any materials without tracking numbers
        const trackedMaterialIds = new Set(trackingNumbers.map(t => t.material_id));
        materialIds.forEach(mid => {
          if (!trackedMaterialIds.has(mid)) items.push({ material_id: mid });
        });
        body = JSON.stringify({ items });
      } else {
        body = JSON.stringify({ material_ids: materialIds });
      }
      const res = await fetch(`${API_BASE}/materials/labels/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body
      });
      if (!res.ok) throw new Error("Failed to generate labels");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Material_Labels.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Error downloading labels: " + err.message);
    }
  }

  /**
   * Updates the quantity for a specific material in the intake form.
   */
  function handleIntakeCountChange(materialId, value) {
    const newCounts = { ...intakeCounts };
    const qty = parseInt(value, 10);
    if (!isNaN(qty) && qty > 0) {
        newCounts[materialId] = qty;
    } else {
        delete newCounts[materialId]; // Remove if empty, zero, or invalid
    }
    setIntakeCounts(newCounts);
  }

  /**
   * Updates the (optional) heat # for a specific material on the intake form.
   * Heat # is free-form text per material lot; an empty value clears it.
   */
  function handleIntakeHeatChange(materialId, value) {
    const newHeats = { ...intakeHeatNumbers };
    const trimmed = (value || "").trim();
    if (trimmed) {
        newHeats[materialId] = value; // store raw (untrimmed) so user input isn't fighting the cursor
    } else {
        delete newHeats[materialId];
    }
    setIntakeHeatNumbers(newHeats);
  }

  /**
   * Commits the customer material intake to the backend, then generates labels and a summary report.
   */
  async function handleCommitIntake() {
    const intakeItems = Object.entries(intakeCounts).map(([material_id, quantity]) => {
        const heat = (intakeHeatNumbers[material_id] || "").trim();
        return {
            material_id: parseInt(material_id, 10),
            quantity,
            ...(heat ? { heat_number: heat } : {}),
        };
    });

    if (intakeItems.length === 0) {
        alert("Please enter a quantity for at least one material.");
        return;
    }

    const pin = await pinPrompt("Enter your 4-digit PIN to confirm the intake:");
    if (!pin) return;

    setIntakeLoading(true);
    setError(null);

    const shipmentNumberTrimmed = (intakeShipmentNumber || "").trim();
    const manifestFile = intakeManifestFile;
    if (manifestFile && manifestFile.type !== "application/pdf" && !manifestFile.name.toLowerCase().endsWith(".pdf")) {
        alert("Manifest must be a PDF file.");
        return;
    }

    const payload = {
        customer_id: parseInt(intakeCustomerId, 10),
        pin: pin,
        intakes: intakeItems,
        shipment_number: shipmentNumberTrimmed || null,
    };

    try {
        const res = await fetch(`${API_BASE}/materials/customer_intake`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            const errorData = await res.json();
            throw new Error(detailToString(errorData.detail) || "Failed to commit intake.");
        }

        const result = await res.json();
        const intakeId = result.intake_id;
        const trkDisplay = (result.tracking_numbers || []).map(t => t.display).join(', ');

        // Upload the scanned manifest PDF (if provided) before showing the summary alert
        let manifestUploaded = false;
        if (manifestFile) {
            try {
                const fd = new FormData();
                fd.append("file", manifestFile);
                const upRes = await fetch(`${API_BASE}/materials/customer_intake/${intakeId}/manifest`, {
                    method: "POST",
                    body: fd,
                });
                if (!upRes.ok) {
                    const upErr = await upRes.json().catch(() => ({}));
                    throw new Error(detailToString(upErr.detail) || "Manifest upload failed.");
                }
                manifestUploaded = true;
            } catch (upErr) {
                alert(`Intake saved (ID ${intakeId}) but manifest upload failed: ${upErr.message}\nYou can retry the upload from the Intake History row.`);
            }
        }

        alert(
            `Intake successful! Intake ID: ${intakeId}` +
            (shipmentNumberTrimmed ? `\nShipment #: ${shipmentNumberTrimmed}` : '') +
            (trkDisplay ? `\nTracking Numbers: ${trkDisplay}` : '') +
            (manifestUploaded ? `\nManifest PDF attached.` : '') +
            `\n\nDownloading labels and summary report...`
        );

        // Download labels as a single PDF (batch endpoint)
        if (result.updated_material_ids && result.updated_material_ids.length > 0) {
            await handleDownloadIntakeLabels(result.updated_material_ids, result.tracking_numbers);
        }

        // Download the summary/manifest report
        await handleDownloadIntakeReport(intakeId);

        // Refresh data and reset the form
        await loadMaterials();
        // Reload history for the current customer before clearing
        if (intakeCustomerId) {
            await loadIntakeHistory(intakeCustomerId);
        }
        setIntakeCustomerId("");
        setIntakeMaterials([]);
        setIntakeCounts({});
        setIntakeHeatNumbers({});
        setIntakeShipmentNumber("");
        setIntakeManifestFile(null);

    } catch (err) {
        setError("Intake Failed: " + err.message);
        alert("Error: " + err.message);
    } finally {
        setIntakeLoading(false);
    }
  }

  // ======================= NAVIGATION =======================
  function goToPurchaseOrders() {
    navigate("/purchase-orders");
  }

  // ======================= COMPUTED VALUES =======================
  const { available, totalAvailable } = materialFigures({ stock_quantity: stockQuantity, reserved_quantity: reservedQuantity, inbound_quantity: inboundQuantity });
    
  // --- MODIFIED: Dynamically filter thickness options based on selected material type ---
  const filteredThicknessOptions = useMemo(() => {
    if (!materialType) return [];
    return thicknessOptions.filter(opt => opt.material_type === materialType);
  }, [materialType, thicknessOptions]);
  
  const filteredFinishOptions = useMemo(() => {
    if (!materialType) return [];

    // Return all surface finishes when a material type is selected
    // TODO: In the future, this could filter finishes based on material type compatibility
    return surfaceFinishes;

  }, [materialType, surfaceFinishes]);

  // ======= AUTO-GENERATE MATERIAL CODE AND DESCRIPTION FOR SHEET STOCK ========
  useEffect(() => {
    // Only auto-generate for Sheet Stock
    if (formMaterialCategory !== 'Sheet Stock') return;

    // Only auto-generate if all required fields are present
    if (!materialType || !thicknessId || !surfaceFinish || !width || !length || !unitOfMeasurement) {
      // Show placeholder when fields are incomplete
      setMaterialCode('(auto-generated when all fields are filled)');
      setDescription('(auto-generated when all fields are filled)');
      return;
    }

    // Find the display_thickness for the selected thickness_id
    const selectedThickness = thicknessOptions.find(opt => opt.thickness_id === parseInt(thicknessId, 10));
    if (!selectedThickness) return;

    const displayThickness = selectedThickness.display_thickness;

    // Get material abbreviation
    const materialAbbrev = MATERIAL_TYPE_ABBREVIATIONS[materialType] || materialType.substring(0, 2).toUpperCase();

    // Get surface finish abbreviation (fallback: strip "Finish", remove special chars, uppercase)
    const finishAbbrev = SURFACE_FINISH_ABBREVIATIONS[surfaceFinish] ||
        surfaceFinish.toUpperCase().replace(/FINISH/gi, '').replace(/\s+/g, '').replace(/#/g, 'NO').trim();

    // Format width and length as integers
    const widthStr = String(Math.floor(parseFloat(width)));
    const lengthStr = String(Math.floor(parseFloat(length)));

    // Build material code: all uppercase, no spaces
    const displayThicknessUpper = displayThickness.toUpperCase();
    const generatedCode = `${displayThicknessUpper}-${materialAbbrev}-${finishAbbrev}-${widthStr}x${lengthStr}`;

    // Build description: {DisplayThickness} {FullMaterialName} {SurfaceFinish} {Width}" x {Length}" {UnitOfMeasurement}
    const generatedDescription = `${displayThickness} ${materialType} ${finishAbbrev} ${widthStr}" x ${lengthStr}" ${unitOfMeasurement}`;

    setMaterialCode(generatedCode);
    setDescription(generatedDescription);
  }, [formMaterialCategory, materialType, thicknessId, surfaceFinish, width, length, unitOfMeasurement, thicknessOptions]);

  // ======= AUTO-GENERATE MATERIAL CODE AND DESCRIPTION FOR SHAPE STOCK ========
  useEffect(() => {
    // Only auto-generate for Shape Stock
    if (formMaterialCategory !== 'Shape Stock') return;

    // Helper to format dimensions - keep decimals, remove trailing zeros
    const fmtDim = (val) => {
      if (!val) return null;
      const num = parseFloat(val);
      if (isNaN(num)) return null;
      if (num === Math.floor(num)) return String(Math.floor(num));
      return String(num).replace(/\.?0+$/, '');
    };

    const shapeAbbrev = PROFILE_SHAPE_ABBREVIATIONS[profileShape] || (profileShape ? profileShape.substring(0, 2).toUpperCase() : null);
    const materialAbbrev = MATERIAL_TYPE_ABBREVIATIONS[materialType] || (materialType ? materialType.substring(0, 2).toUpperCase() : null);

    const widthStr = fmtDim(profileWidth);
    const heightStr = fmtDim(profileHeight);
    const wallStr = fmtDim(wallThickness);
    const lengthStr = fmtDim(stockLength);
    const ppfStr = fmtDim(poundsPerFoot);

    let generatedCode = null;
    let generatedDescription = null;

    // Bar stock with two dimensions (Flat Bar, Square Bar)
    if (BAR_SHAPES.has(profileShape)) {
      if (!widthStr || !heightStr || !materialAbbrev || !lengthStr) {
        setMaterialCode('(auto-generated when all fields are filled)');
        setDescription('(auto-generated when all fields are filled)');
        return;
      }
      generatedCode = `${shapeAbbrev}-${widthStr}X${heightStr}-${materialAbbrev}-${lengthStr}`;
      generatedDescription = `${profileShape} - ${widthStr}"x${heightStr}" - ${materialType} - ${lengthStr}"`;
    }
    // Bar stock with single dimension (Round Bar, Hex Bar)
    else if (SINGLE_DIM_BAR_SHAPES.has(profileShape)) {
      if (!widthStr || !materialAbbrev || !lengthStr) {
        setMaterialCode('(auto-generated when all fields are filled)');
        setDescription('(auto-generated when all fields are filled)');
        return;
      }
      generatedCode = `${shapeAbbrev}-${widthStr}-${materialAbbrev}-${lengthStr}`;
      generatedDescription = `${profileShape} - ${widthStr}" - ${materialType} - ${lengthStr}"`;
    }
    // Tubes (Round, Square, Rectangular)
    else if (TUBE_SHAPES.has(profileShape)) {
      if (!widthStr || !heightStr || !wallStr || !materialAbbrev || !lengthStr) {
        setMaterialCode('(auto-generated when all fields are filled)');
        setDescription('(auto-generated when all fields are filled)');
        return;
      }
      generatedCode = `${shapeAbbrev}-${widthStr}X${heightStr}X${wallStr}-${materialAbbrev}-${lengthStr}`;
      generatedDescription = `${profileShape} - ${widthStr}"x${heightStr}"x${wallStr}" - ${materialType} - ${lengthStr}"`;
    }
    // Angle
    else if (ANGLE_SHAPES.has(profileShape)) {
      if (!widthStr || !heightStr || !wallStr || !materialAbbrev || !lengthStr) {
        setMaterialCode('(auto-generated when all fields are filled)');
        setDescription('(auto-generated when all fields are filled)');
        return;
      }
      generatedCode = `${shapeAbbrev}-${widthStr}X${heightStr}X${wallStr}-${materialAbbrev}-${lengthStr}`;
      generatedDescription = `${profileShape} - ${widthStr}"x${heightStr}"x${wallStr}" - ${materialType} - ${lengthStr}"`;
    }
    // Pipe (no material type) - uses NPS instead of profile width
    else if (PIPE_SHAPES.has(profileShape)) {
      if (!nps || !schedule || !lengthStr) {
        setMaterialCode('(auto-generated when all fields are filled)');
        setDescription('(auto-generated when all fields are filled)');
        return;
      }
      // Format NPS for code: replace "/" with "-" for valid code (e.g., "1/2" -> "1-2")
      const npsCode = nps.replace(/\//g, '-');
      generatedCode = `${shapeAbbrev}-${npsCode}-SCH${schedule}-${lengthStr}`;
      generatedDescription = `${profileShape} - ${nps}" - SCH${schedule} - ${lengthStr}"`;
    }
    // Structural shapes (Channel, I-Beam, Tee)
    else if (STRUCTURAL_SHAPES.has(profileShape)) {
      if (!heightStr || !ppfStr || !materialAbbrev || !lengthStr) {
        setMaterialCode('(auto-generated when all fields are filled)');
        setDescription('(auto-generated when all fields are filled)');
        return;
      }
      generatedCode = `${shapeAbbrev}-${heightStr}X${ppfStr}-${materialAbbrev}-${lengthStr}`;
      generatedDescription = `${profileShape} - ${heightStr}"x${ppfStr}# - ${materialType} - ${lengthStr}"`;
    }
    // No profile shape selected yet
    else {
      setMaterialCode('(auto-generated when all fields are filled)');
      setDescription('(auto-generated when all fields are filled)');
      return;
    }

    if (generatedCode && generatedDescription) {
      setMaterialCode(generatedCode);
      setDescription(generatedDescription);
    }
  }, [formMaterialCategory, materialType, profileShape, profileWidth, profileHeight, wallThickness, stockLength, schedule, poundsPerFoot, nps]);

  // ======= MODIFICATION: Computed lists for filter dropdowns ========
  const thicknessFilterOptions = useMemo(() => {
    if (!materialTypeFilter) return thicknessOptions;
    return thicknessOptions.filter(opt => opt.material_type === materialTypeFilter);
  }, [materialTypeFilter, thicknessOptions]);

  const finishFilterOptions = useMemo(() => {
    if (!materialTypeFilter) return [];
    return surfaceFinishes.map(f => f.finish_name);
  }, [materialTypeFilter, surfaceFinishes]);

  // ======= NEW: DENSITY CRUD HANDLERS ========
  
  const handleAddDensity = async () => {
    if (!newDensity.type || !newDensity.value) {
      alert("Material Type and Density are required.");
      return;
    }
    try {
      const payload = {
        material_type: newDensity.type,
        density_lbs_per_cubic_inch: parseFloat(newDensity.value)
      };
      const res = await fetch(`${API_BASE}/material_densities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to add the density"));
      alert("Density added successfully.");
      setNewDensity({ type: "", value: "" });
      loadAllOptions(); // Reloads densities
    } catch (err) {
      alert("Error adding density: " + err.message);
    }
  };

  const handleUpdateDensity = async () => {
    if (!editingDensity) return;
    try {
       const payload = {
        density_lbs_per_cubic_inch: parseFloat(editingDensity.value)
      };
      const res = await fetch(`${API_BASE}/material_densities/${editingDensity.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to update the density"));
      alert("Density updated successfully.");
      setEditingDensity(null);
      loadAllOptions(); // Reloads densities
    } catch (err) {
      alert("Error updating density: " + err.message);
    }
  };

  const handleDeleteDensity = async (densityId) => {
    if (!window.confirm("Are you sure you want to delete this density entry?")) return;
    try {
      const res = await fetch(`${API_BASE}/material_densities/${densityId}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to delete the density"));
      alert("Density deleted successfully.");
      loadAllOptions(); // Reloads densities
    } catch (err) {
      alert("Error deleting density: " + err.message);
    }
  };

  function TypeFinishManager({ types, finishes, reloadOptions }) {
    const [newTypeName, setNewTypeName] = useState("");
    const [newFinishName, setNewFinishName] = useState("");
  
    const handleAddType = async () => {
      if (!newTypeName.trim()) return;
      await fetch(`${API_BASE}/material_types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_name: newTypeName.trim() })
      });
      setNewTypeName("");
      reloadOptions();
    };
  
    const handleDeleteType = async (typeId) => {
      if (window.confirm("Are you sure you want to delete this material type?")) {
        await fetch(`${API_BASE}/material_types/${typeId}`, { method: 'DELETE' });
        reloadOptions();
      }
    };
  
    const handleAddFinish = async () => {
      if (!newFinishName.trim()) return;
      await fetch(`${API_BASE}/surface_finishes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ finish_name: newFinishName.trim() })
      });
      setNewFinishName("");
      reloadOptions();
    };
  
    const handleDeleteFinish = async (finishId) => {
      if (window.confirm("Are you sure you want to delete this surface finish?")) {
        await fetch(`${API_BASE}/surface_finishes/${finishId}`, { method: 'DELETE' });
        reloadOptions();
      }
    };
  
    return (
      <div>
        <h5>Material Type & Finish Manager</h5>
        <div className="row">
          <div className="col-md-6">
            <h6>Material Types</h6>
            <table className="table table-sm">
              <tbody>
                {types.map(t => (
                  <tr key={t.material_type_id}>
                    <td>{t.type_name}</td>
                    <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteType(t.material_type_id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="input-group">
              <input type="text" className="form-control" value={newTypeName} onChange={e => setNewTypeName(e.target.value)} placeholder="New Material Type Name" />
              <button className="btn btn-primary" onClick={handleAddType}>Add</button>
            </div>
          </div>
          <div className="col-md-6">
            <h6>Surface Finishes</h6>
            <table className="table table-sm">
              <tbody>
                {finishes.map(f => (
                  <tr key={f.surface_finish_id}>
                    <td>{f.finish_name}</td>
                    <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteFinish(f.surface_finish_id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="input-group">
              <input type="text" className="form-control" value={newFinishName} onChange={e => setNewFinishName(e.target.value)} placeholder="New Surface Finish Name" />
              <button className="btn btn-primary" onClick={handleAddFinish}>Add</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ======================= RENDER =======================
  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* LEFT PANEL */}
      <div className="border-end p-3" style={{ width: "350px", flexShrink: 0, overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Materials</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={handleCreateNew}>New</button>
        </div>
        
        {/* NEW: Top-level stock type filter */}
        <div className="btn-group w-100 mb-3">
            <button 
                className={`btn btn-sm ${stockTypeFilter === 'Sheet Stock' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setStockTypeFilter('Sheet Stock')}
            >
                Sheet Stock
            </button>
            <button 
                className={`btn btn-sm ${stockTypeFilter === 'Shape Stock' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setStockTypeFilter('Shape Stock')}
            >
                Shape Stock
            </button>
        </div>

        {/* MODIFIED: Conditional filter section */}
        <div className="p-2 mb-2 border rounded bg-light">
            <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                    <h6 className="mb-0">Filters</h6>
                    {(() => {
                        const remnantCount = materials.filter(m => m.material_category === stockTypeFilter && m.material_code?.startsWith('REM-')).length;
                        if (remnantCount === 0) return null;
                        return (
                            <div className="text-muted" style={{ fontSize: "0.75em" }}>
                                {hideRemnants ? `${remnantCount} remnant${remnantCount === 1 ? '' : 's'} hidden` : `${remnantCount} remnant${remnantCount === 1 ? '' : 's'} shown`}
                            </div>
                        );
                    })()}
                    {(inStockOnly ? outOfStockHidden : outOfStockCount) > 0 && (
                        <div className="text-muted" style={{ fontSize: "0.75em" }} data-testid="out-of-stock-note">
                            {inStockOnly ? `${outOfStockHidden} out of stock hidden` : `${outOfStockCount} out of stock shown`}
                        </div>
                    )}
                </div>
                <div className="d-flex flex-column align-items-start">
                    <label className="form-check-label small mb-0" style={{ cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={inStockOnly}
                            onChange={(e) => setInStockOnly(e.target.checked)}
                        />
                        In stock only
                    </label>
                    <label className="form-check-label small mb-0" style={{ cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={hideRemnants}
                            onChange={(e) => setHideRemnants(e.target.checked)}
                        />
                        Hide remnants
                    </label>
                </div>
            </div>
            <div className="mb-2">
                <label className="form-label form-label-sm">Owner</label>
                <select className="form-select form-select-sm" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
                    <option value="">(All Owners)</option>
                    {customers.map((c) => (<option key={c.customer_id} value={c.customer_id}>{c.name}{c.short_code ? ` (${c.short_code})` : ''}</option>))}
                </select>
            </div>
            <div className="mb-2">
                <label className="form-label form-label-sm">Material Type</label>
                <select 
                    className="form-select form-select-sm" 
                    value={materialTypeFilter} 
                    onChange={(e) => {
                        setMaterialTypeFilter(e.target.value);
                        setThicknessFilter("");
                        setFinishFilter("");
                    }}
                >
                    <option value="">(All Types)</option>
                    {materialTypes.map(type => <option key={type.material_type_id} value={type.type_name}>{type.type_name}</option>)}
                </select>
            </div>
            
            {/* Conditional Filters for Sheet Stock */}
            {stockTypeFilter === 'Sheet Stock' && (
                <>
                    <div className="mb-2">
                        <label className="form-label form-label-sm">Thickness</label>
                        <select 
                            className="form-select form-select-sm" 
                            value={thicknessFilter} 
                            onChange={(e) => setThicknessFilter(e.target.value)}
                            disabled={!materialTypeFilter}
                        >
                            <option value="">(All Thicknesses)</option>
                            {thicknessFilterOptions.map(opt => (
                                <option key={opt.thickness_id} value={opt.thickness_id}>
                                    {opt.display_thickness}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-2">
                        <label className="form-label form-label-sm">Surface Finish</label>
                        <select 
                            className="form-select form-select-sm" 
                            value={finishFilter} 
                            onChange={(e) => setFinishFilter(e.target.value)}
                            disabled={!materialTypeFilter}
                        >
                            <option value="">(All Finishes)</option>
                            {finishFilterOptions.map(fin => (<option key={fin} value={fin}>{fin}</option>))}
                        </select>
                    </div>
                </>
            )}

            {/* NEW: Conditional Filters for Shape Stock */}
            {stockTypeFilter === 'Shape Stock' && (
                <>
                    <div className="mb-2">
                        <label className="form-label form-label-sm">Profile Shape</label>
                        <select className="form-select form-select-sm" value={shapeProfileFilter} onChange={e => setShapeProfileFilter(e.target.value)}>
                            <option value="">(All Shapes)</option>
                            {PROFILE_SHAPES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="mb-2">
                        <label className="form-label form-label-sm">Profile Width (in)</label>
                        <input type="number" step="0.01" className="form-control form-control-sm" value={shapeWidthFilter} onChange={e => setShapeWidthFilter(e.target.value)} />
                    </div>
                    <div className="mb-2">
                        <label className="form-label form-label-sm">Profile Height (in)</label>
                        <input type="number" step="0.01" className="form-control form-control-sm" value={shapeHeightFilter} onChange={e => setShapeHeightFilter(e.target.value)} />
                    </div>
                     <div className="mb-2">
                        <label className="form-label form-label-sm">Stock Length (in)</label>
                        <input type="number" step="0.01" className="form-control form-control-sm" value={shapeLengthFilter} onChange={e => setShapeLengthFilter(e.target.value)} />
                    </div>
                </>
            )}
        </div>
        
        <input type="text" className="form-control mb-2" placeholder="Search Code/Description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        {loading && <div>Loading...</div>}
        {!loading && filteredMaterials.length === 0 && outOfStockHidden > 0 && (
            <div className="text-muted small p-2">
                Nothing in stock matches. Uncheck "In stock only" to see the {outOfStockHidden} out-of-stock match{outOfStockHidden === 1 ? '' : 'es'}.
            </div>
        )}
        {!loading && filteredMaterials.map((m) => {
            const matAvail = (m.stock_quantity || 0) - (m.reserved_quantity || 0);
            const matTotalAvail = (m.stock_quantity || 0) + (m.inbound_quantity || 0) - (m.reserved_quantity || 0);
            const ownerObj = customers.find((c) => c.customer_id === m.owner_customer_id);
            const ownerLabel = ownerObj ? ownerObj.name : `CustID ${m.owner_customer_id || "?"}`;

            return (
              <div
                  key={m.material_id}
                  className="p-2 border-bottom"
                  style={{
                      cursor: "pointer",
                      backgroundColor: selectedMaterial && selectedMaterial.material_id === m.material_id ? "var(--jq-job-selected, #D1E7FD)" : "var(--jq-bg-card, white)",
                  }}
                  onClick={() => handleSelectMaterial(m)}
              >
                <div className="fw-bold">{m.material_code || "???"}</div>
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>{m.description}</div>
                {/* MODIFIED: Combines owner with the new conditional property display */}
                <div style={{ fontSize: "0.75rem", color: "var(--jq-text-muted, #555)" }}>
                    Owner: {ownerLabel} <br/>
                    {m.material_category === 'Shape Stock' ? (
                        <>
                           {m.profile_shape || 'N/A Shape'} | W: {m.profile_width}" H: {m.profile_height}" | L: {m.stock_length}"
                        </>
                    ) : (
                        <>
                           {m.material_type} | {m.display_thickness} | {m.surface_finish}
                        </>
                    )}
                </div>
                {/* This line was missing from your new code */}
                <div style={{ fontSize: "0.8rem" }}>
                  QoH: {formatNumericValue(m.stock_quantity)} | Avail: {formatNumericValue(matAvail)} | Tot. Avail: {formatNumericValue(matTotalAvail)}
                </div>
              </div>
            );
        })}
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-grow-1 p-4 overflow-auto">
        {error && <div className="alert alert-danger">{error}</div>}
        
        <ul className="nav nav-tabs">
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Material Details</button>
            </li>
            {/* Remnants Tab */}
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'remnants' ? 'active' : ''}`} onClick={() => { setActiveTab('remnants'); loadRemnants(); }}>Remnants</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'intake' ? 'active' : ''}`} onClick={() => setActiveTab('intake')}>Customer Material</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'cwt' ? 'active' : ''}`} onClick={() => setActiveTab('cwt')}>CWT Price Manager</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'thickness' ? 'active' : ''}`} onClick={() => setActiveTab('thickness')}>Thickness Manager</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'density' ? 'active' : ''}`} onClick={() => setActiveTab('density')}>Density Manager</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'typeManager' ? 'active' : ''}`} onClick={() => setActiveTab('typeManager')}>Type & Finish Manager</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'tracking' ? 'active' : ''}`} onClick={() => setActiveTab('tracking')}>Material Tracking</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'freeissue' ? 'active' : ''}`} onClick={() => setActiveTab('freeissue')}>Free Issue</button>
            </li>
        </ul>

        <div className="tab-content p-3 border border-top-0">
            {/* MATERIAL DETAILS TAB */}
            <div className={`tab-pane fade ${activeTab === 'details' ? 'show active' : ''}`}>
                <div className="card mb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <span>
                      Low Stock Material Alerts
                      {(() => {
                        const remnantCount = materialShortages.filter(m => m.material_code?.startsWith('REM-')).length;
                        if (remnantCount === 0) return null;
                        return (
                          <span className="text-muted ms-2" style={{ fontSize: "0.85em", fontWeight: "normal" }}>
                            ({hideRemnantShortages ? `${remnantCount} remnant${remnantCount === 1 ? '' : 's'} hidden` : `${remnantCount} remnant${remnantCount === 1 ? '' : 's'} shown`})
                          </span>
                        );
                      })()}
                    </span>
                    <label className="form-check-label small mb-0" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        className="form-check-input me-1"
                        checked={hideRemnantShortages}
                        onChange={(e) => setHideRemnantShortages(e.target.checked)}
                      />
                      Hide remnants
                    </label>
                  </div>
                  <div className="card-body">
                    {(() => {
                      const visibleShortages = hideRemnantShortages
                        ? materialShortages.filter(m => !m.material_code?.startsWith('REM-'))
                        : materialShortages;
                      if (visibleShortages.length === 0) {
                        return materialShortages.length === 0
                          ? <p>No materials below minimum available right now.</p>
                          : <p className="text-muted mb-0">All low-stock alerts are remnants — uncheck "Hide remnants" to view them.</p>;
                      }
                      return (
                        <>
                          <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                            {visibleShortages.map((m) => (
                              <li key={m.material_id} style={{ marginBottom: "0.5rem" }}>
                                <label>
                                  <input type="checkbox" checked={selectedShortageIds.includes(m.material_id)} onChange={(e) => handleToggleShortage(m.material_id, e.target.checked)} style={{ marginRight: "0.5rem" }} />
                                  <strong>{m.material_code}</strong> - {m.description} (QoH={formatNumericValue(m.qoh)}, Inbound={formatNumericValue(m.inbound)}, Reserved={formatNumericValue(m.reserved)}, Min={formatNumericValue(m.minimum_available)}, TotalAvail={formatNumericValue(m.total_avail)})
                                </label>
                              </li>
                            ))}
                          </ul>
                          <button className="btn btn-sm btn-primary mt-2" onClick={handleGeneratePOFromShortages}>Create PO from Selected</button>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4>{dbMaterialId ? `Editing Material (#${dbMaterialId})` : "Create New Material"}</h4>
                  <div>
                    <button className="btn btn-sm btn-secondary me-2" onClick={goToPurchaseOrders}>Go to Purchase Orders</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setStockTakeMode(true)}>Stock Take</button>
                  </div>
                </div>

                {stockTakeMode ? (
                  <>
                    <h5>Stock Take</h5>
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="form-label">Owner</label>
                        <select className="form-select" value={stockTakeFilterOwner} onChange={(e) => setStockTakeFilterOwner(e.target.value)}>
                          <option value="">All Owners</option>
                          {customers
                            .filter(c => materials.some(m => m.owner_customer_id === c.customer_id))
                            .map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Material Type</label>
                        <select className="form-select" value={stockTakeFilterType} onChange={(e) => setStockTakeFilterType(e.target.value)}>
                          <option value="">All Types</option>
                          {materialTypes.map(type => <option key={type.material_type_id} value={type.type_name}>{type.type_name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Thickness</label>
                        <select className="form-select" value={stockTakeFilterThickness} onChange={(e) => setStockTakeFilterThickness(e.target.value)}>
                          <option value="">All Thicknesses</option>
                          {thicknesses.map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Surface Finish</label>
                        <select className="form-select" value={stockTakeFilterFinish} onChange={(e) => setStockTakeFilterFinish(e.target.value)}>
                          <option value="">All Finishes</option>
                          {surfaceFinishes.map(f => <option key={f.surface_finish_id} value={f.finish_name}>{f.finish_name}</option>)}
                        </select>
                      </div>
                    </div>
                    <table className="table table-sm">
                      <thead><tr><th>Material Code / Description</th><th>Owner</th><th>Reported QoH</th><th>Count</th></tr></thead>
                      <tbody>
                        {stockTakeFilteredMaterials.map((m) => {
                          const reportedQoh = m.stock_quantity || 0;
                          const userCount = counts[m.material_id] || "";
                          const ownerObj = customers.find(c => c.customer_id === m.owner_customer_id);
                          const ownerLabel = ownerObj ? ownerObj.name : (m.owner_customer_id ? `CustID ${m.owner_customer_id}` : "—");
                          return (
                            <tr key={m.material_id}>
                              <td><div className="fw-bold">{m.material_code}</div><div>{m.description}</div></td>
                              <td>{ownerLabel}</td>
                              <td>{reportedQoh}</td>
                              <td style={{ maxWidth: "120px" }}><input type="number" className="form-control form-control-sm" value={userCount} onChange={(e) => setCounts({ ...counts, [m.material_id]: e.target.value })} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <button className="btn btn-primary me-2" onClick={handleCommitStockTake} disabled={loading}>{loading ? "Committing..." : "Commit Stock Take"}</button>
                    <button className="btn btn-secondary" onClick={() => setStockTakeMode(false)}>Cancel</button>
                  </>
                ) : (
                  <>
                    {!dbMaterialId && (
                        <div className="mb-3">
                            <label className="form-label fw-bold">Material Category</label>
                            <select 
                                className="form-select"
                                value={formMaterialCategory}
                                onChange={e => setFormMaterialCategory(e.target.value)}
                            >
                                <option value="Sheet Stock">Sheet Stock</option>
                                <option value="Shape Stock">Shape Stock</option>
                            </select>
                        </div>
                    )}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Material Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={materialCode}
                          onChange={(e) => setMaterialCode(e.target.value)}
                          readOnly={true}
                          title="Auto-generated from material attributes"
                          style={{ backgroundColor: 'var(--jq-bg-subtle, #e9ecef)' }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Description</label>
                        <input
                          type="text"
                          className="form-control"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          readOnly={true}
                          title="Auto-generated from material attributes"
                          style={{ backgroundColor: 'var(--jq-bg-subtle, #e9ecef)' }}
                        />
                      </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label className="form-label">Material Type</label>
                            <select
                                className="form-select"
                                value={materialType}
                                onChange={(e) => { setMaterialType(e.target.value); setSurfaceFinish(''); setThicknessId(''); }}
                                style={!dbMaterialId && !materialType ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                            >
                                <option value="">-- Select Type --</option>
                                {materialTypes.map(type => (
                                    <option key={type.material_type_id} value={type.type_name}>
                                        {type.type_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4"><label className="form-label">Stock Quantity (QoH)</label><input type="number" className="form-control" value={dbMaterialId ? stockQuantity : "0"} disabled={!dbMaterialId} title={dbMaterialId ? undefined : "A new material starts at 0. Receive it on a PO, or adjust it with a reason once it exists."} onChange={(e) => setStockQuantity(e.target.value)} />{!dbMaterialId && <div className="form-text">Starts at 0: receive it on a PO, or adjust with a reason after saving.</div>}</div>
                        <div className="col-md-4">
                            <label className="form-label">Unit of Measurement</label>
                            {formMaterialCategory === 'Sheet Stock' ? (
                                <select
                                    className="form-select"
                                    value={unitOfMeasurement}
                                    onChange={(e) => setUnitOfMeasurement(e.target.value)}
                                    style={!dbMaterialId && !unitOfMeasurement ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                >
                                    <option value="">-- Select Unit --</option>
                                    <option value="Sheet">Sheet</option>
                                    <option value="Plate">Plate</option>
                                    <option value="Blank">Blank</option>
                                    <option value="Piece">Piece</option>
                                    <option value="Stock Drop">Stock Drop</option>
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={unitOfMeasurement}
                                    onChange={(e) => setUnitOfMeasurement(e.target.value)}
                                />
                            )}
                        </div>
                    </div>

                    {/* CONDITIONAL UI FOR SHEET STOCK */}
                    {formMaterialCategory === 'Sheet Stock' && (
                        <>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">Thickness</label>
                                    <select
                                        className="form-select"
                                        value={thicknessId}
                                        onChange={(e) => setThicknessId(e.target.value)}
                                        disabled={!materialType}
                                        style={!dbMaterialId && !thicknessId ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    >
                                        <option value="">-- Select Thickness --</option>
                                        {filteredThicknessOptions.map(opt => (<option key={opt.thickness_id} value={opt.thickness_id}>{opt.display_thickness} ({opt.thickness_in}")</option>))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Surface Finish</label>
                                    <select
                                        className="form-select"
                                        value={surfaceFinish}
                                        onChange={(e) => setSurfaceFinish(e.target.value)}
                                        style={!dbMaterialId && !surfaceFinish ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    >
                                        <option value="">-- Select Finish --</option>
                                        {filteredFinishOptions.map((fin) => (
                                            <option key={fin.surface_finish_id} value={fin.finish_name}>
                                                {fin.finish_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">{WIDTH_LABEL}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        value={width}
                                        onChange={(e) => setWidth(e.target.value)}
                                        style={!dbMaterialId && !width ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">{LENGTH_LABEL}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        value={length}
                                        onChange={(e) => setLength(e.target.value)}
                                        style={!dbMaterialId && !length ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    />
                                </div>
                                <div className="col-md-4"><label className="form-label">Weight (lbs)</label><input type="number" step="0.0001" className="form-control" value={weight} readOnly title="Weight is automatically calculated" /></div>
                            </div>
                            {missingSpecWarning && (
                                <div className="alert alert-warning fw-bold">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    {missingSpecWarning}
                                </div>
                            )}
                        </>
                    )}

                    {/* NEW: CONDITIONAL UI FOR SHAPE STOCK */}
                    {formMaterialCategory === 'Shape Stock' && (
                         <>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">Profile Shape</label>
                                    <select
                                        className="form-select"
                                        value={profileShape}
                                        onChange={e => {
                                            setProfileShape(e.target.value);
                                            // Clear conditional fields when shape changes
                                            setSchedule("");
                                            setPoundsPerFoot("");
                                            setNps("");
                                        }}
                                        style={!profileShape ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    >
                                        <option value="">-- Select Shape --</option>
                                        {PROFILE_SHAPES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Stock Length (inches)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        placeholder="e.g., 240 for 20ft"
                                        value={stockLength}
                                        onChange={(e) => setStockLength(e.target.value)}
                                        style={!stockLength ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Stock Weight (lbs) <span className="text-danger">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        placeholder="Total weight of full stick"
                                        value={stockWeight}
                                        onChange={(e) => setStockWeight(e.target.value)}
                                        style={!stockWeight ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                    />
                                </div>
                            </div>
                            <div className="row mb-3">
                                {/* Profile Width - shown for all except Structural shapes */}
                                {!STRUCTURAL_SHAPES.has(profileShape) && (
                                    <div className="col-md-4">
                                        <label className="form-label">Profile Width (inches)</label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            className="form-control"
                                            value={profileWidth}
                                            onChange={(e) => setProfileWidth(e.target.value)}
                                            style={!profileWidth && profileShape && !STRUCTURAL_SHAPES.has(profileShape) ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                        />
                                    </div>
                                )}
                                {/* Profile Height - shown for Bar (except Round/Hex), Tube, Angle, Structural */}
                                {(BAR_SHAPES.has(profileShape) || TUBE_SHAPES.has(profileShape) || ANGLE_SHAPES.has(profileShape) || STRUCTURAL_SHAPES.has(profileShape)) && (
                                    <div className="col-md-4">
                                        <label className="form-label">Profile Height (inches)</label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            className="form-control"
                                            value={profileHeight}
                                            onChange={(e) => setProfileHeight(e.target.value)}
                                            style={!profileHeight ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                        />
                                    </div>
                                )}
                                {/* Wall Thickness - shown for Tube and Angle */}
                                {(TUBE_SHAPES.has(profileShape) || ANGLE_SHAPES.has(profileShape)) && (
                                    <div className="col-md-4">
                                        <label className="form-label">Wall Thickness (inches)</label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            className="form-control"
                                            value={wallThickness}
                                            onChange={(e) => setWallThickness(e.target.value)}
                                            style={!wallThickness ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                        />
                                    </div>
                                )}
                                {/* NPS (Nominal Pipe Size) - shown for Pipe only */}
                                {PIPE_SHAPES.has(profileShape) && (
                                    <div className="col-md-4">
                                        <label className="form-label">Nominal Pipe Size (NPS)</label>
                                        <select
                                            className="form-select"
                                            value={nps}
                                            onChange={(e) => setNps(e.target.value)}
                                            style={!nps ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                        >
                                            <option value="">-- Select NPS --</option>
                                            <option value="1/8">1/8"</option>
                                            <option value="1/4">1/4"</option>
                                            <option value="3/8">3/8"</option>
                                            <option value="1/2">1/2"</option>
                                            <option value="3/4">3/4"</option>
                                            <option value="1">1"</option>
                                            <option value="1-1/4">1-1/4"</option>
                                            <option value="1-1/2">1-1/2"</option>
                                            <option value="2">2"</option>
                                            <option value="2-1/2">2-1/2"</option>
                                            <option value="3">3"</option>
                                            <option value="3-1/2">3-1/2"</option>
                                            <option value="4">4"</option>
                                            <option value="5">5"</option>
                                            <option value="6">6"</option>
                                            <option value="8">8"</option>
                                            <option value="10">10"</option>
                                            <option value="12">12"</option>
                                        </select>
                                    </div>
                                )}
                                {/* Schedule - shown for Pipe only */}
                                {PIPE_SHAPES.has(profileShape) && (
                                    <div className="col-md-4">
                                        <label className="form-label">Schedule</label>
                                        <select
                                            className="form-select"
                                            value={schedule}
                                            onChange={(e) => setSchedule(e.target.value)}
                                            style={!schedule ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                        >
                                            <option value="">-- Select Schedule --</option>
                                            <option value="10">Schedule 10</option>
                                            <option value="20">Schedule 20</option>
                                            <option value="40">Schedule 40</option>
                                            <option value="80">Schedule 80</option>
                                            <option value="160">Schedule 160</option>
                                        </select>
                                    </div>
                                )}
                                {/* Pounds per Foot - shown for Channel, I-Beam, Tee */}
                                {STRUCTURAL_SHAPES.has(profileShape) && (
                                    <div className="col-md-4">
                                        <label className="form-label">Pounds per Foot</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            className="form-control"
                                            value={poundsPerFoot}
                                            onChange={(e) => setPoundsPerFoot(e.target.value)}
                                            style={!poundsPerFoot ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Common fields continued */}
                    <div className="row mb-3">
                      <div className="col-md-4"><label className="form-label">Reserved (Read Only)</label><input type="number" className="form-control" value={reservedQuantity} disabled /></div>
                      <div className="col-md-4"><label className="form-label">Min Available (Safety Stock)</label><input type="number" className="form-control" value={minimumAvailable} onChange={(e) => setMinimumAvailable(e.target.value)} /></div>
                      <div className="col-md-4">
                        <label className="form-label">Owner</label>
                        <select
                            className="form-select"
                            value={ownerCustomerId}
                            onChange={(e) => setOwnerCustomerId(e.target.value)}
                            style={!dbMaterialId && !ownerCustomerId ? REQUIRED_FIELD_EMPTY_STYLE : {}}
                        >
                            <option value="">-- Select an Owner --</option>
                            {customers.map((c) => (<option key={c.customer_id} value={c.customer_id}>{c.name}{c.short_code ? ` (${c.short_code})` : ''}</option>))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      <textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                    <LotStockBadge materialId={dbMaterialId} refreshKey={originalStockQuantity} />
                    <div className="mb-3" style={{ fontSize: "1.1rem" }}>
                      <div><strong>QoH:</strong> {formatNumericValue(stockQuantity)}</div>
                      <div><strong>Reserved:</strong> {formatNumericValue(reservedQuantity)}</div>
                      <div><strong>Available:</strong> {formatNumericValue(available)}</div>
                      <div><strong>Inbound:</strong> {formatNumericValue(inboundQuantity)}</div>
                      <div><strong>Total Available:</strong> {formatNumericValue(totalAvailable)}</div>
                      <div><strong>Min Available (Safety Stock):</strong> {formatNumericValue(minimumAvailable)}</div>
                    </div>
                    <div className="card mt-3">
                      <div className="card-header fw-bold">
                        Inbound Orders
                      </div>
                      <div className="card-body" style={{ maxHeight: '200px', overflowY: 'auto', padding: 0 }}>
                        {inboundOrdersLoading ? (
                          <div className="p-3 text-center"><em>Loading...</em></div>
                        ) : inboundOrders.length > 0 ? (
                          <ul className="list-group list-group-flush">
                            {inboundOrders.map(order => (
                              <li key={order.purchase_order_id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                  <div className="fw-bold">Qty: {order.quantity_due}</div>
                                  <div className="text-muted small">
                                    From: {order.supplier || 'N/A'} (PO #{order.po_number})
                                  </div>
                                </div>
                                <span className="badge bg-secondary rounded-pill">
                                  ETA: {order.estimated_receive_date ? new Date(order.estimated_receive_date + 'T12:00:00Z').toLocaleDateString() : 'N/A'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="p-3 text-center">
                            <em>No inbound orders scheduled.</em>
                          </div>
                        )}
                      </div>
                    </div>
                    {dbMaterialId && (
                      <div className="mt-4">
                        <h5>Associated Suppliers</h5>
                        {materialVendors.length === 0 ? (<p>No suppliers for this material yet.</p>) : (
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Vendor Name</th>
                                {/* Dynamically change header based on category */}
                                <th>{formMaterialCategory === 'Shape Stock' ? "Cost/Unit (Manual)" : "Cost/Unit (Calculated)"}</th>
                                <th>$/CWT</th>
                                <th>Lead Time (days)</th>
                                <th>Last Price Date</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {materialVendors.map((mv) => {
                                const isEditing = editingVendorId === mv.material_vendor_id;
                                return (
                                  <tr key={mv.material_vendor_id}>
                                    <td>
                                      {mv.is_preferred ? (
                                        <strong>{mv.vendor_name} (Preferred)</strong>
                                      ) : (
                                        mv.vendor_name
                                      )}
                                    </td>
                                    <td>
                                      {/* MODIFICATION: Allow editing cost if isEditing is true */}
                                      {isEditing ? (
                                        <input
                                          type="number"
                                          step="0.01"
                                          className="form-control form-control-sm"
                                          style={{ width: "120px" }}
                                          value={editCost}
                                          onChange={(e) => setEditCost(e.target.value)}
                                        />
                                      ) : (
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          value={mv.cost_per_unit}
                                          readOnly
                                          style={{ width: "120px" }}
                                        />
                                      )}
                                    </td>
                                    <td>
                                      {(() => {
                                        const matWeight = parseFloat(formMaterialCategory === 'Shape Stock' ? stockWeight : weight);
                                        if (matWeight > 0 && mv.cost_per_unit > 0) {
                                          return `$${((mv.cost_per_unit / matWeight) * 100).toFixed(2)}`;
                                        }
                                        return "—";
                                      })()}
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <input type="number" className="form-control form-control-sm" style={{ width: "80px" }} value={editLeadTime} onChange={(e) => setEditLeadTime(e.target.value)} />
                                      ) : (
                                        mv.lead_time_days || ""
                                      )}
                                    </td>
                                    <td>
                                      {(() => {
                                        const pd = formatPriceDate(mv.updated_at);
                                        return (
                                          <span
                                            style={{
                                              color: pd.isVeryStale ? '#dc3545' : pd.isStale ? '#fd7e14' : 'inherit',
                                              fontWeight: pd.isStale ? 500 : 'normal',
                                              whiteSpace: 'nowrap'
                                            }}
                                            title={pd.daysSince !== null ? `${pd.daysSince} days ago` : ''}
                                          >
                                            {pd.text}
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <><button className="btn btn-sm btn-success me-2" onClick={() => handleUpdateMaterialVendor(mv.material_vendor_id)}>Save</button><button className="btn btn-sm btn-secondary" onClick={handleCancelEdit}>Cancel</button></>
                                      ) : (
                                        <>
                                          {!mv.is_preferred && (
                                            <button
                                              className="btn btn-sm btn-outline-success me-2"
                                              onClick={() => handleSetPreferred(mv.material_vendor_id)}
                                            >
                                              Set as Preferred
                                            </button>
                                          )}
                                          <button
                                            className="btn btn-sm btn-outline-info me-2"
                                            onClick={() => handleRecalculateVendorCostFromCwt(mv)}
                                            title="Recalculate cost from this vendor's CWT rule (falls back to default rule if no vendor-specific rule exists)"
                                          >
                                            Calc from CWT
                                          </button>
                                          <button className="btn btn-sm btn-primary me-2" onClick={() => handleEditVendorRow(mv)}>Edit</button>
                                          <button className="btn btn-sm btn-danger" onClick={() => handleRemoveMaterialVendor(mv.material_vendor_id)}>Remove</button>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                        <div className="card mt-3">
                          <div className="card-header">Add New Supplier</div>
                          <div className="card-body d-flex" style={{ gap: "1rem" }}>
                            <div style={{ flex: 1 }}><label className="form-label">Vendor</label><select className="form-select" value={newVendorId} onChange={(e) => setNewVendorId(e.target.value)}><option value="">-- Select a Vendor --</option>{vendors.map((v) => (<option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>))}</select></div>
                            <div style={{ flex: 1 }}><label className="form-label">Cost / Unit (Manual)</label><input type="number" step="0.01" className="form-control" value={newVendorCost} onChange={(e) => setNewVendorCost(e.target.value)} /></div>
                            <div style={{ flex: 1 }}><label className="form-label">Lead Time (days)</label><input type="number" className="form-control" value={newVendorLeadTime} onChange={(e) => setNewVendorLeadTime(e.target.value)} /></div>
                            <div style={{ alignSelf: "end" }}><button className="btn btn-primary" onClick={handleAddMaterialVendor}>Add</button></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-4">
                        <button className="btn btn-primary me-2" onClick={handleSaveMaterial}>{dbMaterialId ? "Save Changes" : "Create Material"}</button>
                        {dbMaterialId && (<>
                            {/* NEW DUPLICATE BUTTON */}
                            <button className="btn btn-info me-2" onClick={handleDuplicateMaterial}>Duplicate Material</button>
                            <button className="btn btn-danger me-2" onClick={handleDeleteMaterial}>Delete</button>
                            <button className="btn btn-info me-2" onClick={handlePrintLabel}>Print Label</button>
                            <button className="btn btn-warning me-2" onClick={openRfqModal} title="Create an RFQ bundle for this material — pick vendors + qty">
                                Request Quote…
                            </button>
                            <button
                                className="btn btn-outline-success"
                                onClick={() => {
                                    setError(null);
                                    setTransferSourceMtnId("");
                                    // Refresh MTNs for the picker so the
                                    // operator sees current available qty.
                                    if (dbMaterialId) loadMaterialMtns(dbMaterialId);
                                    setIsTransferModalOpen(true);
                                }}
                            >
                                Transfer Stock...
                            </button>
                            <button
                                className="btn btn-outline-warning ms-2"
                                onClick={() => setShowEarmarkModal(true)}
                                title="Hold sheets of this material for a sales order: from a pallet on the floor or a purchase order line on its way"
                            >
                                <i className="bi bi-bookmark-plus me-1"></i>Earmark Sheets…
                            </button>
                        </>)}
                    </div>
                    {dbMaterialId && (
                      <div className="card mb-4 mt-4">
                        <div className="card-header d-flex align-items-center">
                          <h5 className="mb-0 flex-grow-1">Material Demand Forecast</h5>
                          <span style={{ fontSize: 14, marginLeft: 16, color: "var(--jq-text-muted, #666)" }}>
                            By week for: <strong>{materialCode}</strong> ({description})
                          </span>
                        </div>
                        <div className="card-body">
                          <div className="row mb-3 align-items-end">
                            <div className="col-md-3">
                              <label className="form-label">Start Date</label>
                              <input type="date" className="form-control" value={forecastStart} onChange={(e) => setForecastStart(e.target.value)} disabled={forecastLoading} />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">End Date</label>
                              <input type="date" className="form-control" value={forecastEnd} onChange={(e) => setForecastEnd(e.target.value)} disabled={forecastLoading} />
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                              <button className="btn btn-primary" style={{ minWidth: 110 }} onClick={() => fetchMaterialForecast(dbMaterialId, forecastStart, forecastEnd)} disabled={forecastLoading}>
                                {forecastLoading ? "Loading..." : "Refresh"}
                              </button>
                            </div>
                            <div className="col-md-4 text-end">
                              {forecastError && (<div className="alert alert-danger py-2 px-3 mb-0">{forecastError}</div>)}
                            </div>
                          </div>
                          <div style={{ overflowX: "auto" }}>
                            <table className="table table-sm table-bordered align-middle">
                              <thead>
                                <tr>
                                  <th>Week Start</th>
                                  <th>Sheets Needed</th>
                                  <th>On Hand</th>
                                  <th>Inbound</th>
                                  <th>Needed to Order</th>
                                </tr>
                              </thead>
                              <tbody>
                                {forecastRows.length === 0 && (
                                  <tr><td colSpan={5} className="text-center text-muted">{forecastLoading ? "Loading..." : "No forecasted needs for this period."}</td></tr>
                                )}
                                {forecastRows.map((row) => (
                                  <tr key={row.week_start}>
                                    <td>{row.week_start}</td>
                                    <td>{row.sheets_required}</td>
                                    <td>{row.on_hand}</td>
                                    <td>{row.inbound}</td>
                                    <td style={{ color: row.needed_to_order > 0 ? "red" : undefined, fontWeight: row.needed_to_order > 0 ? "bold" : undefined }}>
                                      {row.needed_to_order}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {dbMaterialId && (
                      <div className="mt-4">
                        <hr />
                        <h5>Inventory Transactions</h5>
                        <div className="row mb-3">
                          <div className="col-md-3"><label className="form-label">Start Date</label><input type="date" className="form-control" value={startDate.toISOString().slice(0, 10)} onChange={(e) => {const newVal = new Date(e.target.value); setStartDate(newVal); loadMaterialTransactions(dbMaterialId, newVal, endDate);}} /></div>
                          <div className="col-md-3"><label className="form-label">End Date</label><input type="date" className="form-control" value={endDate.toISOString().slice(0, 10)} onChange={(e) => {const newVal = new Date(e.target.value); setEndDate(newVal); loadMaterialTransactions(dbMaterialId, startDate, newVal);}} /></div>
                        </div>
                        {transactionsLoading && (<div style={{ fontStyle: "italic" }}>Loading transactions...</div>)}
                        <div className="d-flex align-items-center mb-3" style={{ gap: "0.5rem" }}>
                          <div style={{ display: "flex", flexDirection: "column" }}><label className="form-label">Delta</label><input type="number" className="form-control" style={{ width: "100px" }} value={txDelta} onChange={(e) => setTxDelta(e.target.value)} /></div>
                          <div style={{ display: "flex", flexDirection: "column" }}><label className="form-label">Reason</label><select className="form-select" style={{ width: "200px" }} value={txReason} onChange={(e) => setTxReason(e.target.value)}><option value="">-- Select Reason --</option>{TRANSACTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                          <div style={{ display: "flex", flexDirection: "column" }}><label className="form-label">Job ID (optional)</label><input type="number" className="form-control" style={{ width: "100px" }} value={txJobId} onChange={(e) => setTxJobId(e.target.value)} /></div>
                          <div style={{ alignSelf: "end" }}><button className="btn btn-success ms-2" onClick={handleAddTransaction}>Add Transaction</button></div>
                          <div style={{ alignSelf: "end" }}><button className="btn btn-warning ms-2" onClick={handleDefectMaterial}>Defect Material</button></div>
                        </div>
                        <table className="table table-sm">
                          <thead><tr><th>Date (CT)</th><th>Before</th><th>Delta</th><th>After</th><th>Reason</th><th>Updated By</th><th>Job / PO</th></tr></thead>
                          <tbody>
                            {transactions.map((t) => {
                              let localCT = "";
                              try {localCT = new Date(t.created_at).toLocaleString("en-US", {timeZone: "America/Chicago"});} catch {localCT = t.created_at;}
                              const jobOrPo = [];
                              if (t.job_master_num) {jobOrPo.push(`Job# ${t.job_master_num}`);}
                              if (t.po_number) {jobOrPo.push(`PO# ${t.po_number}`);}
                              return (
                                <tr key={t.transaction_id}>
                                  <td>{localCT}</td><td>{formatNumericValue(t.quantity_before)}</td><td>{formatNumericValue(t.delta)}</td><td>{formatNumericValue(t.quantity_after)}</td><td>{t.reason}</td><td>{t.user_name ? t.user_name : "JobQ"}</td><td>{jobOrPo.join(", ")}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* MATERIAL TRACKING NUMBERS — per-material view */}
                        {dbMaterialId && (
                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Material Tracking Numbers</h5>
                                <div className="btn-group btn-group-sm">
                                    <button
                                        className={`btn ${materialMtnFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setMaterialMtnFilter('all')}
                                    >All ({materialMtns.length})</button>
                                    <button
                                        className={`btn ${materialMtnFilter === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setMaterialMtnFilter('active')}
                                    >Active</button>
                                    <button
                                        className={`btn ${materialMtnFilter === 'flagged' ? 'btn-danger' : 'btn-outline-danger'}`}
                                        onClick={() => setMaterialMtnFilter('flagged')}
                                    >Flagged</button>
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => loadMaterialMtns(dbMaterialId)}
                                        disabled={materialMtnLoading}
                                    >↻</button>
                                </div>
                            </div>
                            {materialMtnLoading ? (
                                <div className="text-muted small py-2">Loading tracking numbers…</div>
                            ) : filteredMaterialMtns.length === 0 ? (
                                <div className="text-muted small py-2">
                                    {materialMtns.length === 0
                                        ? "No tracking numbers recorded for this material yet."
                                        : "No tracking numbers match the current filter."}
                                </div>
                            ) : (
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-hover align-middle mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>TRK#</th>
                                                <th className="text-end">Received</th>
                                                <th className="text-end">Used</th>
                                                <th className="text-end">Remaining</th>
                                                <th className="text-end">Unit Cost</th>
                                                <th>Source</th>
                                                <th>Heat #</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMaterialMtns.map(row => {
                                                const isReconciled = !!row.reconciled_at;
                                                const { label: statusLabel, color: statusColor, title: statusTitle } = mtnLotBadge(row);
                                                return (
                                                    <tr
                                                        key={row.mtn_id}
                                                        onClick={() => { setSelectedMtnId(row.mtn_id); setShowMtnModal(true); }}
                                                        style={{ cursor: 'pointer', opacity: isReconciled ? 0.6 : 1 }}
                                                    >
                                                        <td><strong>{row.tracking_number_display}</strong></td>
                                                        <td className="text-end">{row.quantity}</td>
                                                        <td className="text-end">{row.used_qty || 0}</td>
                                                        <td className={`text-end ${row.remaining_qty < 0 ? 'text-danger fw-bold' : ''}`}>{row.remaining_qty}</td>
                                                        <td className="text-end small"><MtnUnitCostCell row={row} /></td>
                                                        <td>
                                                            {row.source_type === 'remnant' && row.parent_mtn_id && (
                                                                <div className="small" title="A remnant's own lot: pieces cut from this parent lot, with its heat">
                                                                    <span className="badge bg-warning text-dark me-1">Remnant</span>
                                                                    of TRK {String(row.parent_mtn_id).padStart(6, '0')}
                                                                </div>
                                                            )}
                                                            {row.source_type === 'customer_intake' || (row.source_type === 'remnant' && !row.po_number && row.customer_name)
                                                                ? (row.customer_name || 'Customer')
                                                                : (row.po_number ? `PO ${row.po_number}` : '—')
                                                            }
                                                            {row.supplier && <div className="small text-muted">{row.supplier}</div>}
                                                            {row.earmark_text && <div className="mt-1"><EarmarkChip text={row.earmark_text} /></div>}
                                                        </td>
                                                        <td>{row.heat_number || '—'}</td>
                                                        <td className="small">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                                                        <td><span className={`badge bg-${statusColor}`} title={statusTitle || undefined}>{statusLabel}</span></td>
                                                        <td className="text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary"
                                                                title={`Print 4×6 label for TRK ${row.tracking_number_display}`}
                                                                onClick={(e) => { e.stopPropagation(); handlePrintMtnLabel(row.material_id || dbMaterialId, row.mtn_id); }}
                                                            >
                                                                <i className="bi bi-printer me-1"></i>Print
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        )}

                        {dbMaterialId && (
                        <div className="mt-5">
                            <hr />
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="text-secondary">Analytics Dashboard</h4>
                                <div className="d-flex gap-2">
                                    <div>
                                        <label className="form-label text-muted small mb-0">From</label>
                                        <input type="date" className="form-control form-control-sm" value={analyticsStart} onChange={e => setAnalyticsStart(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="form-label text-muted small mb-0">To</label>
                                        <input type="date" className="form-control form-control-sm" value={analyticsEnd} onChange={e => setAnalyticsEnd(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="form-label text-muted small mb-0">Group By</label>
                                        <select className="form-select form-select-sm" value={analyticsGroupBy} onChange={e => setAnalyticsGroupBy(e.target.value)}>
                                            <option value="day">Day</option>
                                            <option value="week">Week</option>
                                            <option value="month">Month</option>
                                            <option value="year">Year</option>
                                        </select>
                                    </div>
                                    <div className="d-flex align-items-end">
                                        <button className="btn btn-sm btn-outline-primary" onClick={fetchAnalyticsData}>Refresh</button>
                                    </div>
                                </div>
                            </div>

                            {analyticsLoading ? (
                                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                            ) : (
                                <div className="row">
                                    {/* CHART 1: Price History */}
                                    <div className="col-lg-6 col-12 mb-4">
                                        <div className="card h-100 shadow-sm border-0 bg-light">
                                            <div className="card-header bg-transparent border-0">
                                                <h6 className="mb-0 fw-bold">Price History</h6>
                                                <small className="text-muted">Unit cost from Purchase Orders</small>
                                            </div>
                                            <div className="card-body" style={{ height: "300px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={priceHistory} margin={{ top: 28, right: 30, left: 20, bottom: 5 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" />
                                                        <YAxis domain={['auto', 'auto']} />
                                                        <Tooltip
                                                            content={({ active, payload, label }) => {
                                                                if (!active || !payload || !payload.length) return null;
                                                                const idx = priceHistory.findIndex(p => p.date === label);
                                                                const price = payload[0].value;
                                                                const prev = idx > 0 ? priceHistory[idx - 1] : null;
                                                                const pct = prev && prev.price ? ((price - prev.price) / prev.price * 100) : null;
                                                                return (
                                                                    <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px', fontSize: 13 }}>
                                                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                                                                        <div>Unit Cost: <strong>${Number(price).toFixed(2)}</strong></div>
                                                                        {pct !== null && Math.abs(pct) >= 0.01 && (
                                                                            <div style={{ color: pct > 0 ? '#dc3545' : '#198754', fontWeight: 600 }}>
                                                                                {pct > 0 ? '+' : ''}{pct.toFixed(1)}% vs prior
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }}
                                                        />
                                                        <Legend />
                                                        <Line type="monotone" dataKey="price" stroke="#8884d8" name="Unit Cost ($)" strokeWidth={2} activeDot={{ r: 8 }}
                                                            dot={(props) => {
                                                                const { cx, cy, index, payload } = props;
                                                                const prev = index > 0 ? priceHistory[index - 1] : null;
                                                                const pctChange = prev && prev.price ? ((payload.price - prev.price) / prev.price * 100) : null;
                                                                const showLabel = pctChange !== null && Math.abs(pctChange) >= 0.01;
                                                                const color = pctChange > 0 ? '#dc3545' : '#198754';
                                                                const sign = pctChange > 0 ? '+' : '';
                                                                return (
                                                                    <g key={`dot-${index}`}>
                                                                        <circle cx={cx} cy={cy} r={4} fill="#8884d8" stroke="#fff" strokeWidth={2} />
                                                                        {showLabel && (
                                                                            <text x={cx} y={cy - 12} textAnchor="middle" fill={color} fontSize={10} fontWeight="bold">
                                                                                {sign}{pctChange.toFixed(1)}%
                                                                            </text>
                                                                        )}
                                                                    </g>
                                                                );
                                                            }}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CHART 2: Consumption History */}
                                    <div className="col-lg-6 col-12 mb-4">
                                        <div className="card h-100 shadow-sm border-0 bg-light">
                                            <div className="card-header bg-transparent border-0">
                                                <h6 className="mb-0 fw-bold">Consumption History</h6>
                                                <small className="text-muted">Sheets used in completed Laser tasks</small>
                                            </div>
                                            <div className="card-body" style={{ height: "300px" }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={consumptionHistory} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                        <XAxis dataKey="period" />
                                                        <YAxis />
                                                        <Tooltip cursor={{fill: 'transparent'}} />
                                                        <Legend />
                                                        <Bar dataKey="quantity" fill="#82ca9d" name="Sheets Used" radius={[4, 4, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                      </div>
                    )}
                  </>
                )}

                {/* RUNOUT FORECAST (inline, below Stock Activity) */}
                {dbMaterialId && (
                  <div className="mt-4">
                    <RunoutForecastView
                      forecastData={runoutForecastData}
                      forecastLoading={runoutForecastLoading}
                      forecastError={runoutForecastError}
                      forecastHorizon={runoutForecastHorizon}
                      setForecastHorizon={setRunoutForecastHorizon}
                      forecastUnsched={runoutForecastUnsched}
                      setForecastUnsched={setRunoutForecastUnsched}
                      forecastSelectedDay={runoutForecastSelectedDay}
                      setForecastSelectedDay={setRunoutForecastSelectedDay}
                      onRefresh={() => loadRunoutForecast(dbMaterialId, runoutForecastHorizon, runoutForecastUnsched)}
                    />
                  </div>
                )}
            </div>
            {/* FREE ISSUE TAB — material of ours physically at a vendor. */}
            <div className={`tab-pane fade ${activeTab === 'freeissue' ? 'show active' : ''}`}>
              {dbMaterialId ? (
                <MaterialCustodyCard
                  materialId={dbMaterialId}
                  materialCode={materialCode}
                  onChanged={loadAllOptions}
                />
              ) : null}
              {/* Shop-wide roll-up. Without this the tab could only answer "where is THIS
                  steel", which needs you to already know the answer.
                  ⚠ onPick is what makes the roll-up ACTIONABLE. It now shows "Kemlee owes 1",
                  and without a way through to that material the operator has read a to-do they
                  cannot act on — they would have to go find the code in the sidebar by hand.
                  Selecting loads the position card above, where the return lives. */}
              <AllVendorCustodyCard
                onPick={(row) => {
                  handleSelectMaterial(row);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>

            {/* REMNANTS TAB CONTENT */}
            <div className={`tab-pane fade ${activeTab === 'remnants' ? 'show active' : ''}`}>

              {/* SECTION 1: Remnant Inventory */}
              <h5>Remnant Inventory</h5>
              <p className="text-muted">Leftover material pieces from laser jobs available for reuse.</p>

              <div className="p-2 mb-3 border rounded bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Filters</h6>
                  <div>
                    <button
                      className="btn btn-sm btn-link p-0 me-3"
                      onClick={() => {
                        setRemnantOwnerFilter("");
                        setRemnantTypeFilter("");
                        setRemnantThicknessFilter("");
                        setRemnantFinishFilter("");
                        setRemnantSearch("");
                      }}
                    >Clear</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={loadRemnants} disabled={remnantsLoading}>
                      {remnantsLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Owner</label>
                    <select className="form-select form-select-sm" value={remnantOwnerFilter} onChange={e => setRemnantOwnerFilter(e.target.value)}>
                      <option value="">(All Owners)</option>
                      {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}{c.short_code ? ` (${c.short_code})` : ''}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Material Type</label>
                    <select
                      className="form-select form-select-sm"
                      value={remnantTypeFilter}
                      onChange={e => {
                        setRemnantTypeFilter(e.target.value);
                        setRemnantThicknessFilter("");
                        setRemnantFinishFilter("");
                      }}
                    >
                      <option value="">(All Types)</option>
                      {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">Thickness</label>
                    <select
                      className="form-select form-select-sm"
                      value={remnantThicknessFilter}
                      onChange={e => setRemnantThicknessFilter(e.target.value)}
                      disabled={!remnantTypeFilter}
                    >
                      <option value="">(All Thicknesses)</option>
                      {remnantThicknessFilterOptions.map(opt => (
                        <option key={opt.thickness_id} value={opt.thickness_id}>{opt.display_thickness}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">Surface Finish</label>
                    <select
                      className="form-select form-select-sm"
                      value={remnantFinishFilter}
                      onChange={e => setRemnantFinishFilter(e.target.value)}
                      disabled={!remnantTypeFilter}
                    >
                      <option value="">(All Finishes)</option>
                      {surfaceFinishes.map(f => <option key={f.finish_name} value={f.finish_name}>{f.finish_name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">Search</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Code / notes..."
                      value={remnantSearch}
                      onChange={e => setRemnantSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-2 d-flex justify-content-between align-items-center">
                  <span className="text-muted small">
                    Showing {filteredRemnants.length} of {remnants.length} remnants
                  </span>
                  {(() => {
                    const selectedRows = remnants.filter(r => selectedRemnantIds.includes(r.material_id));
                    if (selectedRows.length === 0) return null;
                    const distinctOwners = Array.from(new Set(selectedRows.map(r => r.owner_customer_id)));
                    const vendorPreview = distinctOwners.length === 1
                      ? (selectedRows[0].owner_name || `Customer ${distinctOwners[0]}`)
                      : `${distinctOwners.length} different vendors`;
                    const totalWt = selectedRows.reduce((s, r) => s + ((r.weight || 0) * (r.stock_quantity || 0)), 0);
                    return (
                      <span>
                        <span className="text-muted small me-2">
                          {selectedRows.length} selected{distinctOwners.length === 1 ? `, ~${totalWt.toFixed(1)} lb total` : ''} → {vendorPreview}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => setSelectedRemnantIds([])}
                        >Clear</button>
                        <button
                          className="btn btn-sm btn-warning"
                          disabled={distinctOwners.length !== 1}
                          title={distinctOwners.length !== 1 ? "All selected remnants must share one owner before recording a return." : ""}
                          onClick={() => setShowReturnModal(true)}
                        >
                          <i className="fas fa-undo me-1"></i>Return to Vendor…
                        </button>
                      </span>
                    );
                  })()}
                </div>
              </div>

              {filteredRemnants.length === 0 && !remnantsLoading ? (
                <div className="alert alert-info">
                  {remnants.length === 0 ? 'No remnants in inventory. Create one below.' : 'No remnants match the current filters.'}
                </div>
              ) : (
                <div className="table-responsive" style={{maxHeight: '400px', overflowY: 'auto'}}>
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{width: '32px'}}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            title="Select all visible"
                            checked={filteredRemnants.length > 0 && filteredRemnants.every(r => selectedRemnantIds.includes(r.material_id))}
                            onChange={toggleAllVisibleRemnants}
                          />
                        </th>
                        <th>Code</th>
                        <th>Type</th>
                        <th>Thickness</th>
                        <th>Finish</th>
                        <th>Dimensions</th>
                        <th>Weight</th>
                        <th>Cost</th>
                        <th>Owner</th>
                        <th>Qty</th>
                        <th>Source</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRemnants.map(r => (
                        <tr key={r.material_id} className={selectedRemnantIds.includes(r.material_id) ? 'table-warning' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedRemnantIds.includes(r.material_id)}
                              onChange={() => toggleRemnantSelected(r.material_id)}
                            />
                          </td>
                          <td><small className="fw-bold">{r.material_code}</small></td>
                          <td><small>{r.material_type}</small></td>
                          <td><small>{r.display_thickness}</small></td>
                          <td><small>{r.surface_finish}</small></td>
                          <td><small>{r.width ? `${r.width}\"` : '?'} x {r.length ? `${r.length}\"` : '?'}</small>{amadaSizeText(r.width, r.length) ? (<div><small className="text-muted" title="Amada lists X (with the grain) first">Amada {amadaSizeText(r.width, r.length)}</small></div>) : null}</td>
                          <td><small>{r.weight != null ? `${r.weight.toFixed(1)} lbs` : '-'}</small></td>
                          <td>
                            {r.material_cost != null ? (
                              <small
                                className="fw-bold text-success"
                                title={r.cwt_price != null ? `CWT: $${r.cwt_price.toFixed(2)}/cwt × ${r.weight?.toFixed(1)} lbs` : ''}
                              >
                                ${r.material_cost.toFixed(2)}
                              </small>
                            ) : (
                              <small className="text-muted" title="No CWT price rule found for this material type/finish/thickness">—</small>
                            )}
                          </td>
                          <td><small>{r.owner_name}</small></td>
                          <td><span className={`badge ${r.available_quantity > 0 ? 'bg-success' : 'bg-secondary'}`}>{r.stock_quantity}</span></td>
                          <td><small className="text-muted">{r.source_material_code || '-'}</small></td>
                          <td><small className="text-muted">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</small></td>
                          <td className="text-nowrap">
                            <button className="btn btn-sm btn-outline-primary me-1" title="Print Label" onClick={() => handlePrintRemnantLabel(r.material_id)}>
                              <i className="fas fa-print"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-secondary me-1" title="Edit Dimensions" onClick={() => openEditRemnant(r)}>
                              <i className="fas fa-pencil-alt"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" title="Delete Remnant" onClick={() => setDeletingRemnant(r)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Return-to-Vendor Modal */}
              {showReturnModal && (() => {
                const selectedRows = remnants.filter(r => selectedRemnantIds.includes(r.material_id));
                const distinctOwners = Array.from(new Set(selectedRows.map(r => r.owner_customer_id)));
                const vendorName = distinctOwners.length === 1
                  ? (selectedRows[0]?.owner_name || `Customer ${distinctOwners[0]}`)
                  : "(mixed vendors — cannot proceed)";
                const totalQty = selectedRows.reduce((s, r) => s + (r.stock_quantity || 0), 0);
                const totalWt = selectedRows.reduce((s, r) => s + ((r.weight || 0) * (r.stock_quantity || 0)), 0);
                return (
                  <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055}}>
                    <div className="modal-dialog modal-lg">
                      <div className="modal-content">
                        <div className="modal-header bg-warning-subtle">
                          <h5 className="modal-title">
                            <i className="fas fa-undo me-2"></i>Record Material Return to Vendor
                          </h5>
                          <button type="button" className="btn-close" onClick={() => setShowReturnModal(false)} disabled={returnSubmitting}></button>
                        </div>
                        <div className="modal-body">
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="form-label">Vendor</label>
                              <input type="text" className="form-control" value={vendorName} disabled />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Return Date</label>
                              <input type="date" className="form-control" value={returnDate} onChange={e => setReturnDate(e.target.value)} />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Authorize PIN</label>
                              <input type="password" className="form-control" value={returnPin} onChange={e => setReturnPin(e.target.value)} placeholder="••••" />
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Notes (optional, prints on receipt)</label>
                            <input type="text" className="form-control" value={returnNotes} onChange={e => setReturnNotes(e.target.value)} placeholder="e.g., Boyd Metal credit memo, Q2 reconciliation" />
                          </div>
                          <p className="text-muted small mb-2">
                            Recording <strong>{selectedRows.length}</strong> remnant{selectedRows.length === 1 ? '' : 's'} ({totalQty} piece{totalQty === 1 ? '' : 's'}, ~{totalWt.toFixed(1)} lb) as returned to <strong>{vendorName}</strong>:
                          </p>
                          <div className="table-responsive border rounded" style={{maxHeight: '240px', overflowY: 'auto'}}>
                            <table className="table table-sm table-hover mb-0">
                              <thead className="table-light sticky-top">
                                <tr>
                                  <th>Code</th>
                                  <th>Dimensions</th>
                                  <th>Qty</th>
                                  <th>Weight</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedRows.map(r => (
                                  <tr key={r.material_id}>
                                    <td><small className="fw-bold">{r.material_code}</small></td>
                                    <td><small>{r.width ? `${r.width}"` : '?'} x {r.length ? `${r.length}"` : '?'}</small></td>
                                    <td><small>{r.stock_quantity}</small></td>
                                    <td><small>{r.weight != null ? `${(r.weight * (r.stock_quantity || 0)).toFixed(1)} lb` : '-'}</small></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="alert alert-warning small mt-3 mb-0">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            Confirming will set stock to <strong>0</strong> on each selected remnant and remove them from this list. A receipt PDF will open for the vendor to sign.
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-secondary" onClick={() => setShowReturnModal(false)} disabled={returnSubmitting}>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-warning"
                            onClick={handleSubmitReturnToVendor}
                            disabled={returnSubmitting || distinctOwners.length !== 1 || !returnPin || !returnDate}
                          >
                            {returnSubmitting ? 'Recording…' : `Confirm Return & Print Receipt`}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Edit Remnant Modal */}
              {editingRemnant && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055}}>
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          <i className="fas fa-pencil-alt me-2"></i>
                          Edit Remnant: <code className="ms-1">{editingRemnant.material_code}</code>
                        </h5>
                        <button type="button" className="btn-close" onClick={() => setEditingRemnant(null)} disabled={editRemnantSubmitting}></button>
                      </div>
                      <div className="modal-body">
                        <p className="text-muted small mb-3">
                          Updating dimensions auto-recalculates weight and regenerates the material code (REM-…). Cost is recalculated from the current CWT rule.
                        </p>
                        <div className="row g-3">
                          <div className="col-md-4">
                            <label className="form-label">{WIDTH_LABEL}</label>
                            <input type="number" step="0.01" min="0.01" className="form-control" value={editRemnantWidth} onChange={e => setEditRemnantWidth(e.target.value)} autoFocus />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">{LENGTH_LABEL}</label>
                            <input type="number" step="0.01" min="0.01" className="form-control" value={editRemnantLength} onChange={e => setEditRemnantLength(e.target.value)} />
                            {editRemnantWidth && editRemnantLength && (
                              <small className="form-text text-muted">Amada (X x Y): {amadaSizeText(editRemnantWidth, editRemnantLength)}</small>
                            )}
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Quantity</label>
                            <input type="number" min="0" className="form-control" value={editRemnantQty} onChange={e => setEditRemnantQty(e.target.value)} />
                          </div>
                    {editRemnantGrain && (
                      <div className="col-12">
                        <div className={`alert ${editRemnantGrain.code === 'looks_swapped' ? 'alert-warning' : 'alert-danger'} py-2 mb-0 small`}>
                          <i className="fas fa-arrows-alt-h me-1"></i>{editRemnantGrain.message}
                          {editRemnantGrain.code === 'looks_swapped' && (
                            <button type="button" className="btn btn-sm btn-warning ms-2"
                              onClick={() => { setEditRemnantWidth(String(editRemnantGrain.suggestedWidth)); setEditRemnantLength(String(editRemnantGrain.suggestedLength)); }}>
                              Swap to {editRemnantGrain.suggestedWidth} x {editRemnantGrain.suggestedLength}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                          <div className="col-12">
                            <label className="form-label">Notes</label>
                            <input type="text" className="form-control" value={editRemnantNotes} onChange={e => setEditRemnantNotes(e.target.value)} placeholder="e.g., Re-measured 2026-05-05" />
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setEditingRemnant(null)} disabled={editRemnantSubmitting}>
                          Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSaveEditRemnant} disabled={editRemnantSubmitting || !editRemnantWidth || !editRemnantLength}>
                          {editRemnantSubmitting ? 'Saving…' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Remnant Confirmation Modal */}
              {deletingRemnant && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055}}>
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header bg-danger-subtle">
                        <h5 className="modal-title">
                          <i className="fas fa-trash me-2"></i>Delete Remnant
                        </h5>
                        <button type="button" className="btn-close" onClick={() => setDeletingRemnant(null)} disabled={deleteRemnantSubmitting}></button>
                      </div>
                      <div className="modal-body">
                        <p className="mb-2">
                          Permanently delete <code className="fw-bold">{deletingRemnant.material_code}</code>?
                        </p>
                        <p className="text-muted small mb-0">
                          {deletingRemnant.width ? `${deletingRemnant.width}"` : '?'} × {deletingRemnant.length ? `${deletingRemnant.length}"` : '?'}
                          {' · '}{deletingRemnant.stock_quantity} on hand
                          {' · '}{deletingRemnant.material_type}{deletingRemnant.surface_finish ? ` ${deletingRemnant.surface_finish}` : ''}
                        </p>
                        <div className="alert alert-warning small mt-3 mb-0">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          This removes the listing entirely — it will no longer appear anywhere in the system. Cannot be undone.
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setDeletingRemnant(null)} disabled={deleteRemnantSubmitting}>
                          Cancel
                        </button>
                        <button type="button" className="btn btn-danger" onClick={handleConfirmDeleteRemnant} disabled={deleteRemnantSubmitting}>
                          {deleteRemnantSubmitting ? 'Deleting…' : 'Delete Permanently'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <hr className="my-4" />

              {/* SECTION 2: Create Remnant */}
              <h5>Record New Remnant</h5>
              <p className="text-muted">
                Select the source material and enter the remnant's dimensions. A label will be generated automatically.
                <br/><em className="small">Filter selections below persist after each create, so you can enter multiple remnants of the same type quickly.</em>
              </p>
              <div className="card">
                <div className="card-body">
                  {/* Filters for narrowing the Source Material dropdown (sticky across creations) */}
                  <div className="p-2 mb-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Source Material Filters</h6>
                      <button
                        className="btn btn-sm btn-link p-0"
                        onClick={() => {
                          setNewRemnantOwnerFilter("");
                          setNewRemnantTypeFilter("");
                          setNewRemnantThicknessFilter("");
                          setNewRemnantFinishFilter("");
                          setRemnantSourceMaterialId("");
                        }}
                      >Clear</button>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-3">
                        <label className="form-label form-label-sm">Owner</label>
                        <select className="form-select form-select-sm" value={newRemnantOwnerFilter} onChange={e => { setNewRemnantOwnerFilter(e.target.value); setRemnantSourceMaterialId(""); }}>
                          <option value="">(All Owners)</option>
                          {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}{c.short_code ? ` (${c.short_code})` : ''}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label form-label-sm">Material Type</label>
                        <select
                          className="form-select form-select-sm"
                          value={newRemnantTypeFilter}
                          onChange={e => {
                            setNewRemnantTypeFilter(e.target.value);
                            setNewRemnantThicknessFilter("");
                            setNewRemnantFinishFilter("");
                            setRemnantSourceMaterialId("");
                          }}
                        >
                          <option value="">(All Types)</option>
                          {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label form-label-sm">Thickness</label>
                        <select
                          className="form-select form-select-sm"
                          value={newRemnantThicknessFilter}
                          onChange={e => { setNewRemnantThicknessFilter(e.target.value); setRemnantSourceMaterialId(""); }}
                          disabled={!newRemnantTypeFilter}
                        >
                          <option value="">(All Thicknesses)</option>
                          {newRemnantThicknessFilterOptions.map(opt => (
                            <option key={opt.thickness_id} value={opt.thickness_id}>{opt.display_thickness}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label form-label-sm">Surface Finish</label>
                        <select
                          className="form-select form-select-sm"
                          value={newRemnantFinishFilter}
                          onChange={e => { setNewRemnantFinishFilter(e.target.value); setRemnantSourceMaterialId(""); }}
                          disabled={!newRemnantTypeFilter}
                        >
                          <option value="">(All Finishes)</option>
                          {surfaceFinishes.map(f => <option key={f.finish_name} value={f.finish_name}>{f.finish_name}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label fw-bold">Source Material</label>
                      <select className="form-select" value={remnantSourceMaterialId} onChange={e => setRemnantSourceMaterialId(e.target.value)}>
                        <option value="">
                          {newRemnantSourceCandidates.length === 0
                            ? '-- No matching source materials (adjust filters) --'
                            : `-- Select Source Material (${newRemnantSourceCandidates.length} match${newRemnantSourceCandidates.length === 1 ? '' : 'es'}) --`}
                        </option>
                        {newRemnantSourceCandidates.map(m => (
                          <option key={m.material_id} value={m.material_id}>
                            {m.material_code} - {m.description} [QoH: {m.stock_quantity}]
                          </option>
                        ))}
                      </select>
                    </div>

                    {remnantSourceMaterial && (
                      <div className="col-12">
                        <div className="p-2 bg-light rounded border">
                          <small className="text-muted">
                            <strong>Inherited:</strong> {remnantSourceMaterial.material_type} | {remnantSourceMaterial.thickness || 'N/A'} | {remnantSourceMaterial.surface_finish || 'N/A'} | Owner: {remnantSourceMaterial.owner || 'N/A'}
                          </small>
                        </div>
                      </div>
                    )}

                    <div className="col-md-4">
                      <label className="form-label">{WIDTH_LABEL}</label>
                      <input type="number" step="0.01" min="0.01" className="form-control" value={remnantWidth} onChange={e => setRemnantWidth(e.target.value)} placeholder="e.g., 24" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{LENGTH_LABEL}</label>
                      <input type="number" step="0.01" min="0.01" className="form-control" value={remnantLength} onChange={e => setRemnantLength(e.target.value)} placeholder="e.g., 36" />
                      {remnantWidth && remnantLength && (
                        <small className="form-text text-muted">Amada (X x Y): {amadaSizeText(remnantWidth, remnantLength)}</small>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Quantity</label>
                      <input type="number" min="1" className="form-control" value={remnantQty} onChange={e => setRemnantQty(e.target.value)} />
                    </div>
                    {newRemnantGrain && (
                      <div className="col-12">
                        <div className={`alert ${newRemnantGrain.code === 'looks_swapped' ? 'alert-warning' : 'alert-danger'} py-2 mb-0 small`}>
                          <i className="fas fa-arrows-alt-h me-1"></i>{newRemnantGrain.message}
                          {newRemnantGrain.code === 'looks_swapped' && (
                            <button type="button" className="btn btn-sm btn-warning ms-2"
                              onClick={() => { setRemnantWidth(String(newRemnantGrain.suggestedWidth)); setRemnantLength(String(newRemnantGrain.suggestedLength)); }}>
                              Swap to {newRemnantGrain.suggestedWidth} x {newRemnantGrain.suggestedLength}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">Tracking Number (optional)</label>
                      <input type="number" className="form-control" value={remnantMtnId} onChange={e => setRemnantMtnId(e.target.value)} placeholder="e.g., 000123" />
                      <small className="form-text text-muted">Enter the MTN from the source sheet for heat/vendor traceability.</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Notes (optional)</label>
                      <input type="text" className="form-control" value={remnantNotes} onChange={e => setRemnantNotes(e.target.value)} placeholder="e.g., From Job 6435" />
                    </div>
                  </div>
                  <button className="btn btn-primary mt-3" onClick={handleCreateRemnant} disabled={remnantCreating || !remnantSourceMaterialId || !remnantWidth || !remnantLength}>
                    {remnantCreating ? 'Creating...' : 'Create Remnant & Print Label'}
                  </button>
                </div>
              </div>

              <hr className="my-4" />

              {/* SECTION 3: Legacy Drop Label (quick label without inventory record) */}
              <details>
                <summary className="text-muted mb-2" style={{cursor: 'pointer'}}>Quick Label (no inventory record)</summary>
                <p className="text-muted small">Generate a one-off label without creating an inventory record. Use the form above to create tracked remnants.</p>
                <div className="card">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Material Type</label>
                        <select className="form-select" value={dropMaterialType} onChange={e => {setDropMaterialType(e.target.value); setDropThicknessId(''); setDropSurfaceFinish('');}}>
                          <option value="">-- Select Type --</option>
                          {materialTypes.map(type => (
                            <option key={type.material_type_id} value={type.type_name}>{type.type_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Owner</label>
                        <select className="form-select" value={dropOwnerId} onChange={e => setDropOwnerId(e.target.value)}>
                          <option value="">-- Select Owner --</option>
                          {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}{c.short_code ? ` (${c.short_code})` : ''}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Thickness</label>
                        <select className="form-select" value={dropThicknessId} onChange={e => setDropThicknessId(e.target.value)} disabled={!dropMaterialType}>
                          <option value="">-- Select Thickness --</option>
                          {thicknessOptions.filter(opt => opt.material_type === dropMaterialType).map(opt => (
                            <option key={opt.thickness_id} value={opt.thickness_id}>{opt.display_thickness}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Surface Finish</label>
                        <select className="form-select" value={dropSurfaceFinish} onChange={e => setDropSurfaceFinish(e.target.value)} disabled={!dropMaterialType}>
                          <option value="">-- Select Finish --</option>
                          {surfaceFinishes.map(f => <option key={f.surface_finish_id} value={f.finish_name}>{f.finish_name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">{LENGTH_LABEL}</label>
                        <input type="number" step="0.01" className="form-control" value={dropLength} onChange={e => setDropLength(e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">{WIDTH_LABEL}</label>
                        <input type="number" step="0.01" className="form-control" value={dropWidth} onChange={e => setDropWidth(e.target.value)} />
                      </div>
                    </div>
                    <button className="btn btn-outline-primary mt-3" onClick={handleGenerateDropLabel} disabled={dropLoading}>
                      {dropLoading ? 'Generating...' : 'Generate Label Only'}
                    </button>
                  </div>
                </div>
              </details>
            </div>
            {/* ======= NEW: CUSTOMER MATERIAL INTAKE TAB CONTENT ======== */}
            <div className={`tab-pane fade ${activeTab === 'intake' ? 'show active' : ''}`}>
                <h5>Customer Material</h5>
                <p>Receive customer-supplied sheets, or send unused stock back to the customer.</p>
                <hr />

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Select Customer</label>
                        <select
                            className="form-select"
                            value={intakeCustomerId}
                            onChange={handleSelectIntakeCustomer}
                            disabled={intakeLoading || cmrSubmitting}
                        >
                            <option value="">-- Select a Customer --</option>
                            {customers
                                .filter(c => materials.some(m => m.owner_customer_id === c.customer_id))
                                .map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)
                            }
                        </select>
                    </div>
                </div>

                {intakeCustomerId && (
                    <>
                        {/* Sub-tab strip */}
                        <ul className="nav nav-pills mb-3">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${intakeSubTab === 'intake' ? 'active' : ''}`}
                                    onClick={() => setIntakeSubTab('intake')}
                                >
                                    Intake <span className="small fw-normal">(receive)</span>
                                </button>
                            </li>
                            <li className="nav-item ms-2">
                                <button
                                    className={`nav-link ${intakeSubTab === 'return' ? 'active' : ''}`}
                                    onClick={() => setIntakeSubTab('return')}
                                >
                                    Return <span className="small fw-normal">(send back)</span>
                                </button>
                            </li>
                        </ul>

                        {intakeSubTab === 'intake' && (
                          <>
                        <div className="row g-3 mb-3 p-3 border bg-light rounded align-items-end">
                            <div className="col-lg-3 col-md-4">
                                <label className="form-label">Filter by Type</label>
                                <select className="form-select form-select-sm" value={intakeFilterType} onChange={e => { setIntakeFilterType(e.target.value); setIntakeFilterThickness(""); }}>
                                    <option value="">All Types</option>
                                    {[...new Set(intakeMaterials.map(m => m.material_type).filter(Boolean))].sort().map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-4">
                                <label className="form-label">Filter by Thickness</label>
                                <select className="form-select form-select-sm" value={intakeFilterThickness} onChange={e => setIntakeFilterThickness(e.target.value)}>
                                    <option value="">All Thicknesses</option>
                                    {thicknessOptions
                                        .filter(t => !intakeFilterType || t.material_type === intakeFilterType)
                                        .filter(t => intakeMaterials.some(m => m.thickness_id === t.thickness_id))
                                        .map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)
                                    }
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-4">
                                <label className="form-label">Filter by Finish</label>
                                <select className="form-select form-select-sm" value={intakeFilterFinish} onChange={e => setIntakeFilterFinish(e.target.value)}>
                                    <option value="">All Finishes</option>
                                    {[...new Set(intakeMaterials.map(m => m.surface_finish).filter(Boolean))].sort().map(f => (
                                        <option key={f} value={f}>{f}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-4">
                                <button className="btn btn-secondary btn-sm w-100" onClick={() => { setIntakeFilterType(""); setIntakeFilterThickness(""); setIntakeFilterFinish(""); }}>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                        <table className="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>Material Code</th>
                                    <th>Description</th>
                                    <th style={{width: "150px"}}>Quantity Received</th>
                                    <th style={{width: "180px"}}>Heat # <span className="text-muted fw-normal">(optional)</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {intakeMaterials
                                    .filter(m => !intakeFilterType || m.material_type === intakeFilterType)
                                    .filter(m => !intakeFilterThickness || m.thickness_id === parseInt(intakeFilterThickness, 10))
                                    .filter(m => !intakeFilterFinish || m.surface_finish === intakeFilterFinish)
                                    .map(m => (
                                    <tr key={m.material_id}>
                                        <td>{m.material_code}</td>
                                        <td>{m.description}</td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={intakeCounts[m.material_id] || ""}
                                                onChange={(e) => handleIntakeCountChange(m.material_id, e.target.value)}
                                                placeholder="Enter qty..."
                                                disabled={intakeLoading}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                maxLength={100}
                                                value={intakeHeatNumbers[m.material_id] || ""}
                                                onChange={(e) => handleIntakeHeatChange(m.material_id, e.target.value)}
                                                placeholder="e.g., 1605138"
                                                disabled={intakeLoading || !intakeCounts[m.material_id]}
                                                title={intakeCounts[m.material_id] ? "Heat # for this lot (optional)" : "Enter a quantity first"}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="row g-3 p-3 border bg-light rounded mb-2">
                            <div className="col-md-4">
                                <label className="form-label">Shipment Number <span className="text-muted">(optional)</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    maxLength={100}
                                    value={intakeShipmentNumber}
                                    onChange={(e) => setIntakeShipmentNumber(e.target.value)}
                                    placeholder="e.g., PO#, BOL, carrier tracking"
                                    disabled={intakeLoading}
                                />
                            </div>
                            <div className="col-md-8">
                                <label className="form-label">Scanned Manifest PDF <span className="text-muted">(optional)</span></label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept="application/pdf,.pdf"
                                    onChange={(e) => setIntakeManifestFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                                    disabled={intakeLoading}
                                />
                                {intakeManifestFile && (
                                    <div className="form-text">
                                        Selected: <strong>{intakeManifestFile.name}</strong> ({Math.round(intakeManifestFile.size / 1024)} KB)
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 ms-2"
                                            onClick={() => setIntakeManifestFile(null)}
                                            disabled={intakeLoading}
                                        >
                                            clear
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            className="btn btn-primary mt-2"
                            onClick={handleCommitIntake}
                            disabled={intakeLoading || Object.keys(intakeCounts).length === 0}
                        >
                            {intakeLoading ? "Processing..." : "Commit Intake & Download Labels"}
                        </button>
                          </>
                        )}

                        {intakeSubTab === 'return' && (() => {
                            const returnable = intakeMaterials.filter(
                                m => ((m.stock_quantity || 0) - (m.reserved_quantity || 0)) > 0
                            );
                            const filtered = returnable
                                .filter(m => !cmrFilterType || m.material_type === cmrFilterType)
                                .filter(m => !cmrFilterThickness || m.thickness_id === parseInt(cmrFilterThickness, 10))
                                .filter(m => !cmrFilterFinish || m.surface_finish === cmrFilterFinish);
                            const selectedIds = Object.keys(cmrSelected).filter(id => cmrSelected[id]).map(Number);
                            const allVisibleSelected = filtered.length > 0 && filtered.every(m => cmrSelected[m.material_id]);
                            return (
                              <>
                                <p className="text-muted small mb-2">
                                    Send unused customer-supplied sheets back to the customer. Defaults to returning every available sheet of each selected material; override the qty per row if your count is off.
                                </p>

                                {/* Filters */}
                                <div className="row g-3 mb-3 p-3 border bg-light rounded align-items-end">
                                    <div className="col-lg-3 col-md-4">
                                        <label className="form-label">Filter by Type</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={cmrFilterType}
                                            onChange={e => { setCmrFilterType(e.target.value); setCmrFilterThickness(""); }}
                                        >
                                            <option value="">All Types</option>
                                            {[...new Set(returnable.map(m => m.material_type).filter(Boolean))].sort().map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-lg-3 col-md-4">
                                        <label className="form-label">Filter by Thickness</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={cmrFilterThickness}
                                            onChange={e => setCmrFilterThickness(e.target.value)}
                                        >
                                            <option value="">All Thicknesses</option>
                                            {thicknessOptions
                                                .filter(t => !cmrFilterType || t.material_type === cmrFilterType)
                                                .filter(t => returnable.some(m => m.thickness_id === t.thickness_id))
                                                .map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)
                                            }
                                        </select>
                                    </div>
                                    <div className="col-lg-3 col-md-4">
                                        <label className="form-label">Filter by Finish</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={cmrFilterFinish}
                                            onChange={e => setCmrFilterFinish(e.target.value)}
                                        >
                                            <option value="">All Finishes</option>
                                            {[...new Set(returnable.map(m => m.surface_finish).filter(Boolean))].sort().map(f => (
                                                <option key={f} value={f}>{f}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-lg-3 col-md-4">
                                        <button
                                            className="btn btn-secondary btn-sm w-100"
                                            onClick={() => { setCmrFilterType(""); setCmrFilterThickness(""); setCmrFilterFinish(""); }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>

                                {returnable.length === 0 ? (
                                    <div className="alert alert-info">
                                        No returnable stock for this customer. Sheets are either fully consumed, reserved to a live job, or already at zero stock.
                                    </div>
                                ) : (
                                    <>
                                        <table className="table table-sm table-bordered align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{width: "40px"}}>
                                                        <input
                                                            type="checkbox"
                                                            checked={allVisibleSelected}
                                                            onChange={e => {
                                                                const next = {...cmrSelected};
                                                                filtered.forEach(m => { next[m.material_id] = e.target.checked; });
                                                                setCmrSelected(next);
                                                            }}
                                                            title="Select all visible rows"
                                                        />
                                                    </th>
                                                    <th>Material Code</th>
                                                    <th>Description</th>
                                                    <th className="text-end" style={{width: "60px"}}>Stock</th>
                                                    <th className="text-end" style={{width: "70px"}}>Reserved</th>
                                                    <th className="text-end" style={{width: "90px"}}>Returnable</th>
                                                    <th className="text-end" style={{width: "85px"}}>Wt / sheet</th>
                                                    <th style={{width: "130px"}}>Qty to return</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filtered.map(m => {
                                                    const stock = m.stock_quantity || 0;
                                                    const reserved = m.reserved_quantity || 0;
                                                    const ret = Math.max(0, stock - reserved);
                                                    const isSelected = !!cmrSelected[m.material_id];
                                                    const qtyValue = cmrQuantities[m.material_id] ?? "";
                                                    return (
                                                        <tr key={m.material_id} className={isSelected ? "table-warning" : ""}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={e => setCmrSelected(prev => ({...prev, [m.material_id]: e.target.checked}))}
                                                                />
                                                            </td>
                                                            <td>{m.material_code}</td>
                                                            <td>{m.description}</td>
                                                            <td className="text-end">{stock}</td>
                                                            <td className="text-end">{reserved}</td>
                                                            <td className="text-end fw-bold">{ret}</td>
                                                            <td className="text-end">{m.weight != null ? `${parseFloat(m.weight).toFixed(1)} lb` : "—"}</td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    min={1}
                                                                    max={ret}
                                                                    value={qtyValue}
                                                                    onChange={e => setCmrQuantities(prev => ({...prev, [m.material_id]: e.target.value}))}
                                                                    placeholder={`${ret} (all)`}
                                                                    disabled={!isSelected}
                                                                    title={`Defaults to all ${ret} available. Enter a lower number to do a partial return.`}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>

                                        <div className="row g-3 p-3 border bg-warning-subtle rounded align-items-end mt-2">
                                            <div className="col-md-3">
                                                <label className="form-label">Return Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={cmrReturnDate}
                                                    onChange={e => setCmrReturnDate(e.target.value)}
                                                    disabled={cmrSubmitting}
                                                />
                                            </div>
                                            <div className="col-md-5">
                                                <label className="form-label">Notes <span className="text-muted">(printed on manifest)</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    maxLength={500}
                                                    value={cmrNotes}
                                                    onChange={e => setCmrNotes(e.target.value)}
                                                    placeholder="e.g., picked up by their driver"
                                                    disabled={cmrSubmitting}
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">PIN <span className="text-danger">*</span></label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    value={cmrPin}
                                                    onChange={e => setCmrPin(e.target.value)}
                                                    placeholder="Authorize"
                                                    disabled={cmrSubmitting}
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <button
                                                    className="btn btn-warning w-100"
                                                    onClick={handleSubmitCustomerMaterialReturn}
                                                    disabled={cmrSubmitting || selectedIds.length === 0 || !cmrPin || !cmrReturnDate}
                                                    title={selectedIds.length === 0 ? "Select at least one row" : ""}
                                                >
                                                    {cmrSubmitting ? "Processing..." : `Confirm Return & Print (${selectedIds.length})`}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Return History — past customer-material returns for this customer */}
                                <div className="mt-4">
                                    <h5>Return History</h5>
                                    {cmrHistoryLoading ? (
                                        <p>Loading history…</p>
                                    ) : cmrHistory.length === 0 ? (
                                        <p className="text-muted">No previous returns recorded for this customer.</p>
                                    ) : (
                                        <table className="table table-sm table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Return #</th>
                                                    <th>Date</th>
                                                    <th className="text-end">Items</th>
                                                    <th className="text-end">Weight</th>
                                                    <th>Created by</th>
                                                    <th>Notes</th>
                                                    <th style={{width: "160px"}}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cmrHistory.map(r => {
                                                    const idStr = String(r.return_id).padStart(4, '0');
                                                    const dateStr = r.return_date || (r.created_at ? r.created_at.slice(0, 10) : '—');
                                                    const wt = r.total_weight_lb != null ? `${parseFloat(r.total_weight_lb).toFixed(1)} lb` : '—';
                                                    const noteText = r.notes || '';
                                                    return (
                                                        <tr key={r.return_id}>
                                                            <td><strong>VR-{idStr}</strong></td>
                                                            <td>{dateStr}</td>
                                                            <td className="text-end">{r.total_items != null ? r.total_items : '—'}</td>
                                                            <td className="text-end">{wt}</td>
                                                            <td>{r.created_by_name || '—'}</td>
                                                            <td className="small text-muted" style={{maxWidth: "240px"}} title={noteText}>
                                                                {noteText
                                                                    ? (noteText.length > 60 ? noteText.slice(0, 60) + '…' : noteText)
                                                                    : <span className="text-muted">—</span>}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    onClick={() => handleReprintReturnManifest(r.return_id)}
                                                                    title="Re-open the return receipt PDF + print dialog"
                                                                >
                                                                    Reprint Manifest
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                              </>
                            );
                        })()}
                    </>
                )}

                {/* Intake History — only visible on the Intake sub-tab */}
                {intakeCustomerId && intakeSubTab === 'intake' && (
                    <div className="mt-4">
                        <h5>Intake History</h5>
                        {intakeHistoryLoading ? (
                            <p>Loading history...</p>
                        ) : intakeHistory.length === 0 ? (
                            <p className="text-muted">No previous intakes for this customer.</p>
                        ) : (
                            <table className="table table-sm table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Intake ID</th>
                                        <th>Date</th>
                                        <th>Shipment #</th>
                                        <th>Processed By</th>
                                        <th>Materials</th>
                                        <th>Tracking #s</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {intakeHistory.map(intake => (
                                        <tr key={intake.intake_id}>
                                            <td><strong>{intake.intake_id}</strong></td>
                                            <td>{intake.created_at ? new Date(intake.created_at).toLocaleString() : 'N/A'}</td>
                                            <td>{intake.shipment_number ? <span className="badge bg-secondary">{intake.shipment_number}</span> : <span className="text-muted">—</span>}</td>
                                            <td>{intake.user_name || '—'}</td>
                                            <td>
                                                {intake.lines.map((ln, i) => (
                                                    <div key={i} style={{fontSize: "0.85rem"}}>
                                                        {ln.material_code} — Qty: {ln.quantity}
                                                    </div>
                                                ))}
                                            </td>
                                            <td>
                                                {(intake.tracking_numbers || []).map(t => (
                                                    <span key={t.mtn_id} className="badge bg-dark me-1">{t.display}</span>
                                                ))}
                                                {(!intake.tracking_numbers || intake.tracking_numbers.length === 0) && <span className="text-muted">—</span>}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary me-1"
                                                    onClick={() => handleDownloadIntakeReport(intake.intake_id)}
                                                    title="Download intake summary report"
                                                >
                                                    Report
                                                </button>
                                                {intake.has_manifest ? (
                                                    <button
                                                        className="btn btn-sm btn-outline-success me-1"
                                                        onClick={() => handleDownloadIntakeManifest(intake.intake_id, intake.manifest_original_filename)}
                                                        title={intake.manifest_original_filename ? `Download ${intake.manifest_original_filename}` : "Download manifest PDF"}
                                                    >
                                                        Manifest
                                                    </button>
                                                ) : (
                                                    <label className="btn btn-sm btn-outline-secondary me-1 mb-0" title="Attach scanned manifest PDF">
                                                        + Manifest
                                                        <input
                                                            type="file"
                                                            accept="application/pdf,.pdf"
                                                            style={{ display: "none" }}
                                                            onChange={(e) => {
                                                                const f = e.target.files && e.target.files[0];
                                                                if (f) handleUploadIntakeManifestForRow(intake.intake_id, f);
                                                                e.target.value = "";
                                                            }}
                                                        />
                                                    </label>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleDownloadIntakeLabels(intake.lines.map(l => l.material_id), intake.tracking_numbers)}
                                                    title="Download material labels"
                                                >
                                                    Labels
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
           
            {/* CWT PRICE MANAGER TAB */}
            <div className={`tab-pane fade ${activeTab === 'cwt' ? 'show active' : ''}`}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h5 className="mb-0">CWT Price Rules</h5>
                    <p className="mb-0">Manage the default Cost Per Hundredweight (CWT) for different material categories. Updating a price here will trigger a recalculation for all linked material sheets.</p>
                  </div>
                  <a href="/steel-market" className="btn btn-sm btn-outline-info text-nowrap ms-3">
                    View Market Trends
                  </a>
                </div>
                
                <div className="row g-3 mb-3 p-3 border bg-light rounded align-items-end">
                    <div className="col-lg-2 col-md-4">
                        <label className="form-label">Filter by Type</label>
                        <select className="form-select form-select-sm" value={cwtFilter.material_type} onChange={e => setCwtFilter({...cwtFilter, material_type: e.target.value, surface_finish: ''})}>
                            <option value="">All Types</option>
                            {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                        </select>
                    </div>
                    <div className="col-lg-2 col-md-4">
                        <label className="form-label">Filter by Finish</label>
                        <select className="form-select form-select-sm" value={cwtFilter.surface_finish} onChange={e => setCwtFilter({...cwtFilter, surface_finish: e.target.value})}>
                            <option value="">All Finishes</option>
                            {surfaceFinishes.map(f => <option key={f.surface_finish_id} value={f.finish_name}>{f.finish_name}</option>)}
                        </select>
                    </div>
                    <div className="col-lg-2 col-md-4">
                        <label className="form-label">Filter by Thickness</label>
                        <select className="form-select form-select-sm" value={cwtFilter.thickness_id} onChange={e => setCwtFilter({...cwtFilter, thickness_id: e.target.value})}>
                            <option value="">All Thicknesses</option>
                            {thicknessOptions.filter(t => !cwtFilter.material_type || t.material_type === cwtFilter.material_type).map(t => (
                                <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>
                            ))}
                        </select>
                    </div>
                    {/* New Vendor Filter */}
                    <div className="col-lg-3 col-md-6">
                        <label className="form-label">Filter by Vendor</label>
                        <select className="form-select form-select-sm" value={cwtFilter.vendor_id} onChange={e => setCwtFilter({...cwtFilter, vendor_id: e.target.value})}>
                            <option value="">All Vendors</option>
                            <option value="null">(Default / No Vendor)</option>
                            {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                        </select>
                    </div>
                    <div className="col-lg-2 col-md-6">
                        {/* Updated "Clear Filters" button */}
                        <button className="btn btn-secondary btn-sm w-100" onClick={() => setCwtFilter({ material_type: "", surface_finish: "", thickness_id: "", vendor_id: "" })}>
                            Clear Filters
                        </button>
                    </div>
                </div>

                {selectedCwtRuleIds.length > 0 && (
                    <div className="card bg-light border-primary mb-3">
                        <div className="card-body">
                            <h6 className="card-title">{selectedCwtRuleIds.length} rule(s) selected</h6>
                            <div className="d-flex align-items-end" style={{ gap: '1rem' }}>
                                <div>
                                    <label htmlFor="bulkCwtPrice" className="form-label">New CWT Price</label>
                                    <input
                                        type="number"
                                        id="bulkCwtPrice"
                                        step="0.01"
                                        className="form-control"
                                        placeholder="e.g., 75.50"
                                        value={bulkCwtPrice}
                                        onChange={e => setBulkCwtPrice(e.target.value)}
                                        disabled={isBulkUpdating}
                                    />
                                </div>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleBulkCwtUpdate}
                                    disabled={isBulkUpdating || !bulkCwtPrice}
                                >
                                    {isBulkUpdating ? 'Updating...' : `Apply to ${selectedCwtRuleIds.length} item(s)`}
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedCwtRuleIds([])}
                                    disabled={isBulkUpdating}
                                >
                                    Clear Selection
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <table className="table table-sm table-bordered">
                    <thead>
                        <tr>
                            <th style={{ width: '3rem' }}>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    title="Select all visible rules"
                                    checked={filteredCwtRules.length > 0 && selectedCwtRuleIds.length === filteredCwtRules.length}
                                    onChange={handleToggleAllCwtSelection}
                                />
                            </th>
                            <th>Material Type</th>
                            <th>Surface Finish</th>
                            <th>Thickness</th>
                            <th>Vendor</th>
                            <th>CWT Price</th>
                            <th>Last Price Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cwtLoading && <tr><td colSpan="8">Loading CWT rules...</td></tr>}
                        {filteredCwtRules.map(rule => (
                            <tr key={rule.cwt_price_id} className={selectedCwtRuleIds.includes(rule.cwt_price_id) ? 'table-info' : ''}>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={selectedCwtRuleIds.includes(rule.cwt_price_id)}
                                        onChange={() => handleToggleCwtSelection(rule.cwt_price_id)}
                                    />
                                </td>
                                <td>{rule.material_type}</td>
                                <td>{rule.surface_finish}</td>
                                <td>{rule.display_thickness} ({rule.thickness_in}")</td>
                                <td>{rule.vendor_name || <i>Default</i>}</td>
                                <td>
                                    {editingCwtRule?.id === rule.cwt_price_id ? (
                                        <div style={{ minWidth: '300px' }}>
                                            <div className="input-group input-group-sm mb-1">
                                                <span className="input-group-text" style={{width: '70px'}}>Sheet $</span>
                                                <input type="number" step="0.01" className="form-control" placeholder="Cost"
                                                      value={editingCwtRule.sheet_cost}
                                                      onChange={e => setEditingCwtRule({...editingCwtRule, sheet_cost: e.target.value})} />
                                            </div>
                                            <div className="input-group input-group-sm mb-1">
                                                <span className="input-group-text" style={{width: '70px'}}>Width</span>
                                                <input type="number" step="0.01" className="form-control" placeholder="W (in)"
                                                      value={editingCwtRule.sheet_width}
                                                      onChange={e => setEditingCwtRule({...editingCwtRule, sheet_width: e.target.value})} />
                                            </div>
                                            <div className="input-group input-group-sm mb-1">
                                                <span className="input-group-text" style={{width: '70px'}}>Length</span>
                                                <input type="number" step="0.01" className="form-control" placeholder="L (in)"
                                                      value={editingCwtRule.sheet_length}
                                                      onChange={e => setEditingCwtRule({...editingCwtRule, sheet_length: e.target.value})} />
                                            </div>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text fw-bold" style={{width: '70px'}}>CWT</span>
                                                <input type="number" step="0.01" className="form-control fw-bold"
                                                      value={editingCwtRule.price}
                                                      onChange={e => setEditingCwtRule({...editingCwtRule, price: e.target.value})}
                                                      readOnly={!!editingCwtRule.sheet_cost}
                                                      title={editingCwtRule.sheet_cost ? "CWT is auto-calculated" : "Enter CWT directly"}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        `$${parseFloat(rule.cwt_price).toFixed(2)}`
                                    )}
                                </td>
                                <td>
                                    {(() => {
                                        const pd = formatPriceDate(rule.updated_at);
                                        return (
                                            <span
                                                style={{
                                                    color: pd.isVeryStale ? '#dc3545' : pd.isStale ? '#fd7e14' : 'inherit',
                                                    fontWeight: pd.isStale ? 500 : 'normal',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title={pd.daysSince !== null ? `${pd.daysSince} days ago` : ''}
                                            >
                                                {pd.text}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td>
                                    {editingCwtRule?.id === rule.cwt_price_id ? (
                                        <>
                                            <button className="btn btn-sm btn-success me-2" onClick={handleUpdateCwtRule}>Save</button>
                                            <button className="btn btn-sm btn-secondary" onClick={() => setEditingCwtRule(null)}>Cancel</button>
                                        </>
                                    ) : (
                                        <>
                                            <button 
                                                className="btn btn-sm btn-primary me-2" 
                                                onClick={() => setEditingCwtRule({
                                                    id: rule.cwt_price_id, 
                                                    price: rule.cwt_price,
                                                    material_type: rule.material_type,
                                                    thickness_id: rule.thickness_id,
                                                    sheet_cost: "",
                                                    sheet_width: "",
                                                    sheet_length: ""
                                                })}>Edit</button>
                                            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCwtRule(rule.cwt_price_id)}>Delete</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <hr />
                <h5>Add New CWT Rule</h5>
                <div className="row g-3 align-items-end">
                    <div className="col-md-2">
                        <label className="form-label">Material Type</label>
                        <select className="form-select" value={newCwtRule.material_type} onChange={e => setNewCwtRule({...newCwtRule, material_type: e.target.value, surface_finish: '', thickness_id: ''})}>
                            <option value="">Select Type</option>
                            {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Surface Finish</label>
                        <select className="form-select" value={newCwtRule.surface_finish} onChange={e => setNewCwtRule({...newCwtRule, surface_finish: e.target.value})}>
                                <option value="">Select Finish</option>
                                {surfaceFinishes.map(f => <option key={f.surface_finish_id} value={f.finish_name}>{f.finish_name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Thickness</label>
                        <select className="form-select" value={newCwtRule.thickness_id} onChange={e => setNewCwtRule({...newCwtRule, thickness_id: e.target.value})}>
                            <option value="">Select Thickness</option>
                            {thicknessOptions.filter(opt => !newCwtRule.material_type || opt.material_type === newCwtRule.material_type).map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Sheet Cost ($)</label>
                        <input type="number" step="0.01" className="form-control"
                              value={newCwtRule.sheet_cost}
                              onChange={e => setNewCwtRule({...newCwtRule, sheet_cost: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Sheet Width (in)</label>
                        <input type="number" step="0.01" className="form-control"
                              value={newCwtRule.sheet_width}
                              onChange={e => setNewCwtRule({...newCwtRule, sheet_width: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Sheet Length (in)</label>
                        <input type="number" step="0.01" className="form-control"
                              value={newCwtRule.sheet_length}
                              onChange={e => setNewCwtRule({...newCwtRule, sheet_length: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">CWT Price (Calculated)</label>
                        <input type="number" step="0.01" className="form-control"
                              value={newCwtRule.cwt_price}
                              onChange={e => setNewCwtRule({...newCwtRule, cwt_price: e.target.value})}
                              // Make it read-only if sheet cost is entered
                              readOnly={!!newCwtRule.sheet_cost}
                              title={newCwtRule.sheet_cost ? "CWT price is auto-calculated from sheet cost" : "Enter CWT Price directly"}
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label">Vendor (Optional)</label>
                        <select className="form-select" value={newCwtRule.vendor_id} onChange={e => setNewCwtRule({...newCwtRule, vendor_id: e.target.value})}>
                            <option value="">Default</option>
                            {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <button className="btn btn-success" onClick={handleAddCwtRule}>Add Rule</button>
                    </div>
                </div>
            </div>

                {/* THICKNESS MANAGER TAB */}
                <div className={`tab-pane fade ${activeTab === 'thickness' ? 'show active' : ''}`}>
                    <h5>Material Thickness Manager</h5>
                    <p>Manage the standardized thickness values for each material type.</p>
                    
                    {thicknessesLoading ? <p>Loading thicknesses...</p> : (
                      <table className="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Material Type</th>
                            <th>Display Thickness (e.g., 11ga)</th>
                            <th>Thickness (in)</th>
                            <th style={{width: '150px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {thicknesses.map(t => (
                            <tr key={t.thickness_id}>
                              <td>
                                {editingThickness?.id === t.thickness_id ? (
                                  <select 
                                    className="form-select form-select-sm"
                                    value={editingThickness.material_type}
                                    onChange={e => setEditingThickness({...editingThickness, material_type: e.target.value})}
                                  >
                                    {materialTypes.map(type => <option key={type.material_type_id} value={type.type_name}>{type.type_name}</option>)}
                                  </select>
                                ) : (
                                  t.material_type
                                )}
                              </td>
                              <td>
                                {editingThickness?.id === t.thickness_id ? (
                                  <input 
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={editingThickness.display_thickness}
                                    onChange={e => setEditingThickness({...editingThickness, display_thickness: e.target.value})}
                                  />
                                ) : (
                                  t.display_thickness
                                )}
                              </td>
                              <td>
                                {editingThickness?.id === t.thickness_id ? (
                                  <input 
                                    type="number"
                                    step="0.0001"
                                    className="form-control form-control-sm"
                                    value={editingThickness.thickness_in}
                                    onChange={e => setEditingThickness({...editingThickness, thickness_in: e.target.value})}
                                  />
                                ) : (
                                  t.thickness_in
                                )}
                              </td>
                              <td>
                                {editingThickness?.id === t.thickness_id ? (
                                  <>
                                    <button className="btn btn-sm btn-success me-2" onClick={handleUpdateThickness}>Save</button>
                                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingThickness(null)}>Cancel</button>
                                  </>
                                ) : (
                                  <>
                                    <button className="btn btn-sm btn-primary me-2" onClick={() => setEditingThickness({id: t.thickness_id, display_thickness: t.display_thickness, thickness_in: t.thickness_in, material_type: t.material_type})}>Edit</button>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteThickness(t.thickness_id)}>Delete</button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <hr />
                    <h5>Add New Thickness</h5>
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label">Material Type</label>
                            <select 
                              className="form-select" 
                              value={newThickness.material_type} 
                              onChange={e => setNewThickness({...newThickness, material_type: e.target.value})}
                            >
                                <option value="">-- Select Type --</option>
                                {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Display Thickness</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="e.g., 11ga"
                              value={newThickness.display_thickness} 
                              onChange={e => setNewThickness({...newThickness, display_thickness: e.target.value})} 
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Thickness (in)</label>
                            <input 
                              type="number" 
                              step="0.0001"
                              placeholder="e.g., 0.1196"
                              className="form-control" 
                              value={newThickness.thickness_in} 
                              onChange={e => setNewThickness({...newThickness, thickness_in: e.target.value})} 
                            />
                        </div>
                        <div className="col-md-2">
                            <button className="btn btn-success" onClick={handleAddThickness}>Add Thickness</button>
                        </div>
                    </div>
                </div>
            {/* DENSITY MANAGER TAB */}
            <div className={`tab-pane fade ${activeTab === 'density' ? 'show active' : ''}`}>
                <h5>Material Density Manager</h5>
                <p>Manage the density values (lbs/in³) for each material type.</p>
                
                {densitiesLoading ? <p>Loading densities...</p> : (
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr>
                        <th>Material Type</th>
                        <th>Density (lb/in³)</th>
                        <th style={{width: '150px'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {densities.map(d => (
                        <tr key={d.density_id}>
                          <td>{editingDensity?.id === d.density_id ? editingDensity.type : d.material_type}</td>
                          <td>
                            {editingDensity?.id === d.density_id ? (
                              <input 
                                type="number"
                                step="0.001"
                                className="form-control form-control-sm"
                                value={editingDensity.value}
                                onChange={e => setEditingDensity({...editingDensity, value: e.target.value})}
                              />
                            ) : (
                              d.density_lbs_per_cubic_inch
                            )}
                          </td>
                          <td>
                            {editingDensity?.id === d.density_id ? (
                              <>
                                <button className="btn btn-sm btn-success me-2" onClick={handleUpdateDensity}>Save</button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingDensity(null)}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <button className="btn btn-sm btn-primary me-2" onClick={() => setEditingDensity({id: d.density_id, type: d.material_type, value: d.density_lbs_per_cubic_inch})}>Edit</button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDensity(d.density_id)}>Delete</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <hr />
                <h5>Add New Density</h5>
                <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                        <label className="form-label">Material Type</label>
                        <select 
                            className="form-select" 
                            value={newDensity.type} 
                            onChange={e => setNewDensity({...newDensity, type: e.target.value})}
                        >
                            <option value="">-- Select Type --</option>
                            {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Density (lb/in³)</label>
                        <input 
                            type="number" 
                            step="0.001"
                            placeholder="e.g., 0.284"
                            className="form-control" 
                            value={newDensity.value} 
                            onChange={e => setNewDensity({...newDensity, value: e.target.value})} 
                        />
                    </div>
                    <div className="col-md-2">
                        <button className="btn btn-success" onClick={handleAddDensity}>Add Density</button>
                    </div>
                </div>
            </div>
            <div className={`tab-pane fade ${activeTab === 'typeManager' ? 'show active' : ''}`}>
                <TypeFinishManager
                    types={materialTypes}
                    finishes={surfaceFinishes}
                    reloadOptions={loadAllOptions}
                />
            </div>

            {/* MATERIAL TRACKING TAB */}
            <div className={`tab-pane fade ${activeTab === 'tracking' ? 'show active' : ''}`}>
                <RemnantLineageModal
                    show={showRemnantLineage}
                    onHide={() => setShowRemnantLineage(false)}
                    onChanged={() => loadTrackingMtns()}
                />
                <div className="card mb-3">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <strong>Material Tracking Numbers</strong>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handlePrintCountSheet()}
                                disabled={countSheetLoading}
                                title="Print an inventory count sheet of on-hand (active/unused) MTN lots grouped by material, scoped to the current filters"
                            >
                                {countSheetLoading ? "Preparing…" : "🖨 Print Count Sheet"}
                            </button>
                            <button
                                className="btn btn-sm btn-outline-warning"
                                onClick={() => setShowRemnantLineage(true)}
                                title="Remnants whose pieces have no lot on record: read their tags and record the TRK"
                            >
                                Remnant origins
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => loadTrackingMtns()}
                                disabled={trackingLoading}
                            >
                                {trackingLoading ? "Loading…" : "Refresh"}
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {/* Filter bar */}
                        <div className="row g-2 mb-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small mb-1">Search</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="TRK#, material, heat #, PO, supplier…"
                                    value={trackingSearch}
                                    onChange={e => setTrackingSearch(e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={trackingStatus}
                                    onChange={e => setTrackingStatus(e.target.value)}
                                >
                                    {TRACKING_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Supplier</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={trackingSupplier}
                                    onChange={e => setTrackingSupplier(e.target.value)}
                                >
                                    <option value="">All suppliers</option>
                                    {trackingSupplierOptions.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary w-100"
                                    onClick={() => {
                                        setTrackingSearch('');
                                        setTrackingStatus('all');
                                        setTrackingSupplier('');
                                        setTrackingMaterialType('');
                                        setTrackingThicknessId('');
                                        setTrackingFinish('');
                                        setTrackingOwner('');
                                        setTrackingSort({ key: 'mtn_id', dir: 'desc' });
                                    }}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                        <div className="row g-2 mb-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Owner</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={trackingOwner}
                                    onChange={e => setTrackingOwner(e.target.value)}
                                >
                                    <option value="">All owners</option>
                                    {customers
                                        .filter(c => materials.some(m => m.owner_customer_id === c.customer_id))
                                        .map(c => (
                                            <option key={c.customer_id} value={c.customer_id}>
                                                {c.name}{c.short_code ? ` (${c.short_code})` : ''}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Material Type</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={trackingMaterialType}
                                    onChange={e => setTrackingMaterialType(e.target.value)}
                                >
                                    <option value="">All material types</option>
                                    {trackingMaterialTypeOptions.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Thickness</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={trackingThicknessId}
                                    onChange={e => setTrackingThicknessId(e.target.value)}
                                >
                                    <option value="">All thicknesses</option>
                                    {trackingThicknessOptions.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.label}{t.in != null ? ` (${t.in}")` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Surface Finish</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={trackingFinish}
                                    onChange={e => setTrackingFinish(e.target.value)}
                                >
                                    <option value="">All finishes</option>
                                    {trackingFinishOptions.map(f => (
                                        <option key={f} value={f}>{f}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Table */}
                        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <table className="table table-sm table-hover table-striped align-middle mb-0">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th
                                            role="button"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleTrackingSortClick('mtn_id')}
                                            title="Sort by tracking number"
                                        >
                                            TRK#{trackingSortIndicator('mtn_id')}
                                        </th>
                                        <th
                                            role="button"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleTrackingSortClick('material_type')}
                                            title="Sort by material type"
                                        >
                                            Material{trackingSortIndicator('material_type')}
                                        </th>
                                        <th
                                            role="button"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleTrackingSortClick('thickness')}
                                            title="Sort by thickness"
                                        >
                                            Thickness{trackingSortIndicator('thickness')}
                                        </th>
                                        <th
                                            role="button"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleTrackingSortClick('surface_finish')}
                                            title="Sort by surface finish"
                                        >
                                            Finish{trackingSortIndicator('surface_finish')}
                                        </th>
                                        <th>Source</th>
                                        <th className="text-end">Received</th>
                                        <th className="text-end">Used</th>
                                        <th className="text-end">Remaining</th>
                                        <th className="text-end">Unit Cost</th>
                                        <th>Status</th>
                                        <th>PO #</th>
                                        <th>Supplier</th>
                                        <th>Heat #</th>
                                        <th>Received Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trackingLoading ? (
                                        <tr><td colSpan={14} className="text-center text-muted py-3">Loading…</td></tr>
                                    ) : filteredTrackingMtns.length === 0 ? (
                                        <tr><td colSpan={14} className="text-center text-muted py-3">No tracking numbers match the current filters.</td></tr>
                                    ) : filteredTrackingMtns.map(row => {
                                        const isReconciled = !!row.reconciled_at;
                                        const { label: statusLabel, color: statusColor, title: statusTitle } = mtnLotBadge(row);
                                        return (
                                            <tr
                                                key={row.mtn_id}
                                                onClick={() => { setSelectedMtnId(row.mtn_id); setShowMtnModal(true); }}
                                                style={{
                                                    cursor: 'pointer',
                                                    opacity: isReconciled ? 0.6 : 1
                                                }}
                                            >
                                                <td><strong>{row.tracking_number_display}</strong></td>
                                                <td>
                                                    <div>{row.material_code || '—'}</div>
                                                    {row.material_description && (
                                                        <div className="small text-muted">{row.material_description}</div>
                                                    )}
                                                    {row.material_type && (
                                                        <div className="small text-muted">{row.material_type}</div>
                                                    )}
                                                </td>
                                                <td className="small">{row.display_thickness || '—'}</td>
                                                <td className="small">{row.surface_finish || '—'}</td>
                                                <td>
                                                    <span className={`badge bg-${row.source_type === 'customer_intake' ? 'success' : row.source_type === 'transfer' ? 'secondary' : row.source_type === 'remnant' ? 'warning text-dark' : 'primary'}`}
                                                          title={row.source_type === 'remnant' && row.parent_mtn_id ? `Remnant lot cut from TRK ${String(row.parent_mtn_id).padStart(6, '0')}` : undefined}>
                                                        {row.source_type === 'customer_intake' ? 'Customer' : row.source_type === 'transfer' ? 'Transfer' : row.source_type === 'remnant' ? 'Remnant' : 'PO'}
                                                    </span>
                                                </td>
                                                <td className="text-end">{row.quantity}</td>
                                                <td className="text-end">{row.used_qty || 0}</td>
                                                <td className={`text-end ${row.remaining_qty < 0 ? 'text-danger fw-bold' : ''}`}>
                                                    {row.remaining_qty}
                                                </td>
                                                <td className="text-end small"><MtnUnitCostCell row={row} /></td>
                                                <td><span className={`badge bg-${statusColor}`} title={statusTitle || undefined}>{statusLabel}</span></td>
                                                <td>
                                                    {row.po_number || (row.customer_name || '—')}
                                                    {row.earmark_text && <div className="mt-1"><EarmarkChip text={row.earmark_text} /></div>}
                                                </td>
                                                <td>{row.supplier || '—'}</td>
                                                <td>{row.heat_number || '—'}</td>
                                                <td className="small">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-muted small mt-2">
                            Showing {filteredTrackingMtns.length} of {trackingMtns.length} tracking numbers. Click a row to view details, consumption history, and reconcile.
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Earmark Sheets (material page button) */}
      <EarmarkSheetsModal
          show={showEarmarkModal}
          onHide={() => setShowEarmarkModal(false)}
          materialId={dbMaterialId}
          materialCode={materialCode}
          onChanged={() => {
              if (dbMaterialId) loadMaterialMtns(dbMaterialId);
              if (activeTab === 'tracking') loadTrackingMtns();
          }}
      />

      {/* MTN Detail Modal */}
      <MtnDetailModal
          mtnId={selectedMtnId}
          show={showMtnModal}
          onHide={closeMtnModal}
          onChanged={() => {
              // Refresh both lists after a reconcile/unreconcile
              if (activeTab === 'tracking') loadTrackingMtns();
              if (dbMaterialId) loadMaterialMtns(dbMaterialId);
          }}
      />

      {/* Request Quote (RFQ) Modal */}
      {showRfqModal && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content">
                      <div className="modal-header">
                          <h5 className="modal-title">Request Quote — {materialCode || 'New Material'}</h5>
                          <button
                              type="button"
                              className="btn-close"
                              onClick={() => setShowRfqModal(false)}
                              disabled={rfqSubmitting}
                          ></button>
                      </div>
                      <div className="modal-body">
                          <div className="alert alert-secondary mb-3">
                              <div><strong>Material:</strong> {materialCode || '—'}</div>
                              {description && <div className="small text-muted">{description}</div>}
                              <div className="small mt-1">
                                  QoH: <strong>{stockQuantity || 0}</strong>
                                  {' · '}Reserved: <strong>{reservedQuantity || 0}</strong>
                                  {' · '}Min: <strong>{minimumAvailable || 0}</strong>
                              </div>
                          </div>

                          <div className="row g-3 mb-3">
                              <div className="col-md-4">
                                  <label className="form-label">Quantity to quote <span className="text-danger">*</span></label>
                                  <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      className="form-control"
                                      value={rfqQty}
                                      onChange={e => setRfqQty(e.target.value)}
                                  />
                                  <div className="form-text small">{unitOfMeasurement || 'units'}</div>
                              </div>
                              <div className="col-md-4">
                                  <label className="form-label">Requested delivery date</label>
                                  <input
                                      type="date"
                                      className="form-control"
                                      value={rfqRequestedDeliveryDate}
                                      onChange={e => setRfqRequestedDeliveryDate(e.target.value)}
                                  />
                                  <div className="form-text small">Optional — when you need it on the floor</div>
                              </div>
                              <div className="col-md-4">
                                  <label className="form-label">Quote due by</label>
                                  <input
                                      type="date"
                                      className="form-control"
                                      value={rfqDueDate}
                                      onChange={e => setRfqDueDate(e.target.value)}
                                  />
                                  <div className="form-text small">Optional — vendor response deadline</div>
                              </div>
                          </div>

                          <div className="mb-3">
                              <label className="form-label">Notes (sent to vendors)</label>
                              <textarea
                                  className="form-control"
                                  rows={2}
                                  value={rfqNotes}
                                  placeholder={`RFQ for ${materialCode || 'this material'}`}
                                  onChange={e => setRfqNotes(e.target.value)}
                              />
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label mb-0">
                                  Vendors <span className="text-danger">*</span>{' '}
                                  <span className="text-muted small">({rfqSelectedVendorIds.length} selected)</span>
                              </label>
                              <div className="form-check form-switch">
                                  <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="rfqShowAllVendors"
                                      checked={rfqShowAllVendors}
                                      onChange={e => setRfqShowAllVendors(e.target.checked)}
                                  />
                                  <label htmlFor="rfqShowAllVendors" className="form-check-label small">
                                      Show all suppliers ({vendors.length})
                                  </label>
                              </div>
                          </div>

                          {rfqVendorList.length === 0 ? (
                              <div className="alert alert-warning small mb-0">
                                  No vendors are linked to this material yet. Flip the “Show all suppliers” toggle above, or add a vendor from the Associated Suppliers table first.
                              </div>
                          ) : (
                              <div className="border rounded" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                  <table className="table table-sm table-hover mb-0">
                                      <thead className="table-light sticky-top">
                                          <tr>
                                              <th style={{ width: '40px' }}></th>
                                              <th>Vendor</th>
                                              <th style={{ width: '120px' }}>Source</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {rfqVendorList.map(v => {
                                              const checked = rfqSelectedVendorIds.includes(v.vendor_id);
                                              return (
                                                  <tr
                                                      key={v.vendor_id}
                                                      style={{ cursor: 'pointer' }}
                                                      onClick={() => toggleRfqVendor(v.vendor_id)}
                                                  >
                                                      <td onClick={e => e.stopPropagation()}>
                                                          <input
                                                              type="checkbox"
                                                              className="form-check-input"
                                                              checked={checked}
                                                              onChange={() => toggleRfqVendor(v.vendor_id)}
                                                          />
                                                      </td>
                                                      <td>
                                                          {v.is_preferred ? <strong>{v.name} (Preferred)</strong> : v.name}
                                                      </td>
                                                      <td>
                                                          {v.is_known
                                                              ? <span className="badge bg-success">Linked</span>
                                                              : <span className="badge bg-secondary">Other</span>
                                                          }
                                                      </td>
                                                  </tr>
                                              );
                                          })}
                                      </tbody>
                                  </table>
                              </div>
                          )}

                          {/* Email step — preview + edit before send */}
                          <div className="mt-4 pt-3 border-top">
                              <div className="form-check mb-2">
                                  <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="rfqSendEmail"
                                      checked={rfqSendEmail}
                                      onChange={e => setRfqSendEmail(e.target.checked)}
                                  />
                                  <label htmlFor="rfqSendEmail" className="form-check-label fw-bold">
                                      Email this RFQ to selected vendors after creation
                                  </label>
                              </div>
                              {rfqSendEmail && (
                                  <>
                                      <label className="form-label small mb-1">
                                          Email body (sent to all {rfqSelectedVendorIds.length} selected vendor{rfqSelectedVendorIds.length === 1 ? '' : 's'})
                                      </label>
                                      <textarea
                                          className="form-control font-monospace"
                                          rows={10}
                                          value={rfqEmailBody}
                                          onChange={e => setRfqEmailBody(e.target.value)}
                                          style={{ fontSize: '0.875rem' }}
                                      />
                                      <div className="d-flex justify-content-between align-items-center mt-1">
                                          <div className="form-text small mb-0">
                                              The RFQ PDF is attached automatically. For per-vendor message customization, send from the Purchase Orders page instead.
                                          </div>
                                          <button
                                              type="button"
                                              className="btn btn-link btn-sm p-0 ms-2 text-decoration-none"
                                              onClick={() => setRfqEmailBody(buildDefaultRfqEmailBody({
                                                  materialCode,
                                                  description,
                                                  qty: rfqQty,
                                                  unit: unitOfMeasurement,
                                                  requestedDeliveryDate: rfqRequestedDeliveryDate,
                                                  dueDate: rfqDueDate
                                              }))}
                                              title="Restore the default template (uses current qty + dates)"
                                          >
                                              Reset to default
                                          </button>
                                      </div>
                                  </>
                              )}
                          </div>
                      </div>
                      <div className="modal-footer">
                          <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setShowRfqModal(false)}
                              disabled={rfqSubmitting}
                          >Cancel</button>
                          <button
                              type="button"
                              className="btn btn-warning"
                              onClick={handleSubmitRfq}
                              disabled={rfqSubmitting || rfqSelectedVendorIds.length === 0 || !rfqQty}
                          >
                              {rfqSubmitting
                                  ? (rfqSendEmail ? 'Creating + sending…' : 'Creating…')
                                  : (rfqSendEmail
                                      ? `Create RFQ + Email ${rfqSelectedVendorIds.length} vendor${rfqSelectedVendorIds.length === 1 ? '' : 's'}`
                                      : `Create RFQ for ${rfqSelectedVendorIds.length} vendor${rfqSelectedVendorIds.length === 1 ? '' : 's'}`)
                              }
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
      {isTransferModalOpen && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg modal-dialog-centered"> {/* Increased to modal-lg for better filter layout */}
                  <div className="modal-content">
                      <div className="modal-header">
                          <h5 className="modal-title">Transfer Material Stock</h5>
                          <button type="button" className="btn-close" onClick={() => { setIsTransferModalOpen(false); setTransferSourceMtnId(""); }} disabled={transferLoading}></button>
                      </div>
                      <div className="modal-body">
                          {error && <div className="alert alert-danger">{error}</div>}
                          
                          {/* Source Material Info */}
                          <div className="alert alert-secondary mb-3">
                              <strong>Transferring From:</strong> {materials.find(m => m.material_id === dbMaterialId)?.material_code || materialCode}<br/>
                              <span className="text-muted small">
                                {materialType} | {thicknessOptions.find(t => String(t.thickness_id) === String(thicknessId))?.display_thickness} | {surfaceFinish} <br/>
                                Current Available: {available}
                              </span>
                          </div>

                          {/* Source MTN picker — required when source has any
                              MTN with remaining qty. The new MTN inherits
                              heat/PO from the picked stack, and the source MTN
                              decrements so we don't keep ghost qty. */}
                          {(() => {
                              // Only OPEN stacks (active/unused) can be drawn from —
                              // see isTransferableMtn. Reconciled lots in particular can
                              // still carry a positive remaining_qty (that leftover IS
                              // the written-off variance), so a remaining>0 test lets
                              // retired steel back into the picker.
                              const eligibleMtns = (materialMtns || []).filter(isTransferableMtn);
                              const closedMtns = (materialMtns || []).filter(m => !isTransferableMtn(m));
                              if (eligibleMtns.length === 0 && closedMtns.length === 0) {
                                  return (
                                      <div className="alert alert-warning small mb-3">
                                          <strong>No MTN traceability:</strong> this material has no Material Tracking Numbers. The transfer will proceed without minting a child MTN — heat/PO traceability cannot be carried forward.
                                      </div>
                                  );
                              }
                              if (eligibleMtns.length === 0) {
                                  return (
                                      <div className="alert alert-danger small mb-3">
                                          <strong>No open MTN to transfer from:</strong> all {closedMtns.length} tracking number{closedMtns.length === 1 ? '' : 's'} on this material {closedMtns.length === 1 ? 'is' : 'are'} exhausted, transferred out, returned, or reconciled. A reconciled lot's leftover quantity is the variance the physical count wrote off — it is not steel on the floor. Receive or correct a lot before transferring.
                                      </div>
                                  );
                              }
                              return (
                                  <div className="mb-3">
                                      <label htmlFor="transfer-src-mtn" className="form-label">
                                          <strong>Source MTN <span className="text-danger">*</span></strong>
                                          <span className="text-muted small ms-2">(pick the physical stack you're drawing from)</span>
                                      </label>
                                      <select
                                          id="transfer-src-mtn"
                                          className="form-select"
                                          size={Math.min(Math.max(eligibleMtns.length + 1, 2), 6)}
                                          value={transferSourceMtnId}
                                          onChange={(e) => setTransferSourceMtnId(e.target.value)}
                                          disabled={transferLoading}
                                      >
                                          {/* Placeholder option matches the initial empty state.
                                              Without it the browser auto-selects the first real
                                              option visually, but React state stays "" and
                                              clicking the already-highlighted row fires no
                                              onChange — leaving Confirm Transfer disabled. */}
                                          <option value="">-- Select MTN to transfer from --</option>
                                          {eligibleMtns.map(m => {
                                              const trk = `TRK ${String(m.mtn_id).padStart(6, '0')}`;
                                              const heat = m.heat_number ? ` | Heat: ${m.heat_number}` : '';
                                              const supplier = m.supplier ? ` | ${m.supplier}` : '';
                                              const po = m.po_number ? ` | PO: ${m.po_number}` : '';
                                              const cust = (!m.supplier && m.customer_name) ? ` | Cust: ${m.customer_name}` : '';
                                              const parent = m.parent_mtn_id ? ` | Parent: ${String(m.parent_mtn_id).padStart(6, '0')}` : '';
                                              const avail = `Avail: ${m.remaining_qty}/${m.quantity}`;
                                              return (
                                                  <option key={m.mtn_id} value={m.mtn_id}>
                                                      {trk}{heat}{supplier}{po}{cust}{parent} | {avail}
                                                  </option>
                                              );
                                          })}
                                      </select>
                                      <div className="form-text">
                                          {eligibleMtns.length} open MTN{eligibleMtns.length === 1 ? '' : 's'} (active/unused) available to transfer from
                                          {closedMtns.length > 0 ? ` (${closedMtns.length} exhausted/transferred/returned/reconciled not shown)` : ''}.
                                      </div>
                                  </div>
                              );
                          })()}

                          <hr />

                          {/* --- NEW: DESTINATION FILTERS --- */}
                          <h6 className="mb-2">Find Destination Material</h6>
                          <div className="row g-2 mb-3 bg-light p-2 rounded border">
                              <div className="col-md-3">
                                  <label className="form-label small">Type</label>
                                  <select 
                                      className="form-select form-select-sm"
                                      value={transferFilters.type}
                                      onChange={e => setTransferFilters({...transferFilters, type: e.target.value})}
                                  >
                                      <option value="">(All)</option>
                                      {materialTypes.map(t => <option key={t.material_type_id} value={t.type_name}>{t.type_name}</option>)}
                                  </select>
                              </div>
                              <div className="col-md-3">
                                  <label className="form-label small">Thickness</label>
                                  <select 
                                      className="form-select form-select-sm"
                                      value={transferFilters.thickness}
                                      onChange={e => setTransferFilters({...transferFilters, thickness: e.target.value})}
                                  >
                                      <option value="">(All)</option>
                                      {thicknessOptions
                                        .filter(t => !transferFilters.type || t.material_type === transferFilters.type)
                                        .map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)
                                      }
                                  </select>
                              </div>
                              <div className="col-md-3">
                                  <label className="form-label small">Finish</label>
                                  <select 
                                      className="form-select form-select-sm"
                                      value={transferFilters.finish}
                                      onChange={e => setTransferFilters({...transferFilters, finish: e.target.value})}
                                  >
                                      <option value="">(All)</option>
                                      {surfaceFinishes.map(f => <option key={f.surface_finish_id} value={f.finish_name}>{f.finish_name}</option>)}
                                  </select>
                              </div>
                              <div className="col-md-3">
                                  <label className="form-label small">Owner</label>
                                  <select 
                                      className="form-select form-select-sm"
                                      value={transferFilters.owner}
                                      onChange={e => { setTransferFilters({...transferFilters, owner: e.target.value}); setTransferCreateFor(""); }}
                                  >
                                      <option value="">(All Owners)</option>
                                      {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}{c.short_code ? ` (${c.short_code})` : ''}</option>)}
                                  </select>
                              </div>
                              <div className="col-12">
                                  <input 
                                      type="text" 
                                      className="form-control form-control-sm" 
                                      placeholder="Search code or description..." 
                                      value={transferFilters.search}
                                      onChange={e => setTransferFilters({...transferFilters, search: e.target.value})}
                                  />
                              </div>
                          </div>

                          {/* Destination Selection */}
                          <div className="mb-3">
                              <label htmlFor="transfer-dest" className="form-label"><strong>Select Destination Material:</strong></label>
                              <select
                                  id="transfer-dest"
                                  className="form-select"
                                  size="5" // Show as a list box for easier scanning
                                  value={transferDestId}
                                  onChange={(e) => setTransferDestId(e.target.value)}
                                  disabled={transferLoading || transferCreateDest}
                              >
                                  {/* Placeholder so React state "" maps to a real option.
                                      Without it, browsers auto-highlight option 0 and the
                                      first user click registers no change event. */}
                                  <option value="">-- Select destination material --</option>
                                  {filteredTransferDestinations.length === 0 && (
                                      <option disabled>No matching materials found.</option>
                                  )}
                                  {filteredTransferDestinations.map(m => {
                                      const ownerObj = customers.find(c => c.customer_id === m.owner_customer_id);
                                      const ownerName = ownerObj ? ownerObj.name : "Unknown";
                                      return (
                                          <option key={m.material_id} value={m.material_id}>
                                              {m.material_code} | {ownerName} | {m.description} (QoH: {m.stock_quantity})
                                          </option>
                                      );
                                  })}
                              </select>
                              <div className="form-text">
                                  Showing {filteredTransferDestinations.length} matches based on filters.
                              </div>
                              {/* The owner picked above has none of this steel: make it as part of the transfer. */}
                              {ownerCopy && (ownerCopy.can_create ? (
                                  <div className="form-check mt-2">
                                      <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id="transfer-create-dest"
                                          checked={transferCreateDest}
                                          disabled={transferLoading}
                                          onChange={(e) => { setTransferCreateFor(e.target.checked ? `${dbMaterialId}:${transferFilters.owner}` : ""); if (e.target.checked) setTransferDestId(""); }}
                                      />
                                      <label className="form-check-label" htmlFor="transfer-create-dest">
                                          Create <strong>{ownerCopy.code}</strong> for {ownerCopy.owner_name} on transfer
                                      </label>
                                      <div className="form-text">
                                          {ownerCopy.owner_name} has no material of this steel. The new one copies this material's type, gauge, finish, size and cost, and holds only what you move. It is made only if the transfer goes through.
                                      </div>
                                  </div>
                              ) : (ownerCopy.twins || []).length === 1 ? (
                                  <div className="form-text mt-2">
                                      {ownerCopy.owner_name} already has this steel: <strong>{ownerCopy.twins[0].material_code}</strong>.{' '}
                                      {!materials.some(m => m.material_id === ownerCopy.twins[0].material_id) ? (
                                          // made since this page loaded its list: the list above can't show it
                                          <span>Reload the page to pick it.</span>
                                      ) : String(transferDestId) !== String(ownerCopy.twins[0].material_id) && (
                                          <button
                                              type="button"
                                              className="btn btn-link btn-sm p-0 align-baseline"
                                              disabled={transferLoading}
                                              onClick={() => {
                                                  setTransferFilters({ ...transferFilters, search: '' });   // so the list shows it
                                                  setTransferDestId(String(ownerCopy.twins[0].material_id));
                                              }}
                                          >
                                              Select it
                                          </button>
                                      )}
                                  </div>
                              ) : ownerCopy.reason ? (
                                  <div className="form-text mt-2">{ownerCopy.reason}</div>
                              ) : null)}
                          </div>

                          <div className="row g-3">
                              <div className="col-md-6">
                                  <label htmlFor="transfer-qty" className="form-label"><strong>Quantity to Transfer:</strong></label>
                                  <input
                                      type="number"
                                      id="transfer-qty"
                                      className="form-control"
                                      value={transferQty}
                                      onChange={(e) => setTransferQty(e.target.value)}
                                      disabled={transferLoading}
                                      placeholder="e.g., 5"
                                  />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="transfer-reason" className="form-label"><strong>Reason:</strong></label>
                                  <input
                                      type="text"
                                      id="transfer-reason"
                                      className="form-control"
                                      value={transferReason}
                                      onChange={(e) => setTransferReason(e.target.value)}
                                      disabled={transferLoading}
                                      placeholder="e.g., Job #1234 usage"
                                  />
                              </div>
                          </div>
                      </div>
                      <div className="modal-footer">
                          <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => { setIsTransferModalOpen(false); setTransferSourceMtnId(""); }}
                              disabled={transferLoading}
                          >
                              Cancel
                          </button>
                          <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleConfirmTransfer}
                              disabled={
                                  transferLoading
                                  || (!transferDestId && !transferCreateDest)
                                  || !transferQty
                                  // Any tracked material needs a source stack. When some are
                                  // open, one must be picked; when they are ALL closed out
                                  // (exhausted/transferred/reconciled) there is nothing valid
                                  // to pick, so stay disabled rather than send a doomed request.
                                  || ((materialMtns || []).length > 0 && !transferSourceMtnId)
                              }
                          >
                              {transferLoading ? 'Processing...' : 'Confirm Transfer'}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}

